import { useState } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";

// ── Palette ───────────────────────────────────────────────────────────────
const TEAL      = "#0D5C63";
const TEAL2     = "#14B8C4";
const TEAL_DARK = "#0a474d";
const CARD_BG   = "#EDEEF0";
const PAGE_BG   = "#F4F5F7";
const WHITE     = "#FFFFFF";
const BORDER    = "#D8DADF";
const DARK      = "#1A1A2E";
const GRAY      = "#6B7280";
const GRAY_MID  = "#9CA3AF";
const RED       = "#C0392B";
const RED_BG    = "#FDECEA";
const AMBER     = "#B45309";
const AMBER_BG  = "#FEF3C7";
const GREEN     = "#166534";
const GREEN_BG  = "#DCFCE7";

const fmt  = n => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = n => n >= 1000000 ? "$" + (n/1000000).toFixed(1)+"M" : n >= 1000 ? "$"+(n/1000).toFixed(0)+"K" : "$"+n;

// ── Data ──────────────────────────────────────────────────────────────────
const brandRows = [
  { brand: "Avuá Cachaça",            amount: 82213, yago: -134, cases: 617, csYago: -132, pct: 59.97, cb: 5820,  cbRate: 7.1,  netPerCase: 124, tier: "HERO"  },
  { brand: "Paladar Tequila",         amount: 28795, yago: -241, cases: 137, csYago: -233, pct: 21.00, cb: 4582,  cbRate: 15.9, netPerCase: 176, tier: "WATCH" },
  { brand: "Svöl Aquavit",            amount: 20705, yago: -105, cases: 156, csYago: -108, pct: 15.10, cb: 1242,  cbRate: 6.0,  netPerCase: 125, tier: "CORE"  },
  { brand: "Choco Smooth",            amount: 4578,  yago: 0,    cases: 36,  csYago: 0,    pct: 3.34,  cb: 183,   cbRate: 4.0,  netPerCase: 121, tier: "WATCH" },
  { brand: "Drifter Craft Cocktails", amount: 440,   yago: -73,  cases: 8,   csYago: -73,  pct: 0.32,  cb: 0,     cbRate: 0,    netPerCase: 55,  tier: "WATCH" },
  { brand: "Gajardo",                 amount: 357,   yago: -38,  cases: 3,   csYago: -40,  pct: 0.26,  cb: 0,     cbRate: 0,    netPerCase: 119, tier: "EXIT"  },
];

// ── Multi-year brand data (real from app screenshots) ─────────────────────
const BRAND_COLORS = {
  "Avuá Cachaça":           TEAL,
  "Paladar Tequila":        "#0F766E",
  "Svöl Aquavit":           "#14B8C4",
  "Choco Smooth":           "#2DD4BF",
  "Drifter Craft Cocktails":"#5EEAD4",
  "Gajardo":                "#A7F3D0",
};
const BRANDS_ORDER = ["Avuá Cachaça","Paladar Tequila","Svöl Aquavit","Choco Smooth","Drifter Craft Cocktails","Gajardo"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// 2025 monthly by brand (real data from screenshots)
const DATA_2025 = {
  "Avuá Cachaça":           [43461,73517,45228,65451,108977,100287,71314,50041,40181,106297,40331,38365],
  "Paladar Tequila":        [78666,45418,29397,105848,15531,23627,66749,70068,118293,47282,59924,51737],
  "Svöl Aquavit":           [8283, 19233,16060,8544, 21867,17996,17009,22225,10639,16672,13382,8983 ],
  "Choco Smooth":           [4290, 0,    10465,5345, 4145, 0,    170,  0,    9058, 3900, 2920, 3843 ],
  "Drifter Craft Cocktails":[9065, 1640, 4950, 7800, 3300, 1917, 2640, 4620, 495,  0,    0,    0    ],
  "Gajardo":                [578,  1005, 0,    1507, 0,    1959, 125,  0,    0,    0,    0,    0    ],
};
// 2026 monthly by brand (YTD Jan–Mar)
const DATA_2026 = {
  "Avuá Cachaça":           [20782,21061,40370,0,0,0,0,0,0,0,0,0],
  "Paladar Tequila":        [12978,9113, 6702, 0,0,0,0,0,0,0,0,0],
  "Svöl Aquavit":           [12011,4482, 4212, 0,0,0,0,0,0,0,0,0],
  "Choco Smooth":           [0,    4578, 0,    0,0,0,0,0,0,0,0,0],
  "Drifter Craft Cocktails":[0,    440,  0,    0,0,0,0,0,0,0,0,0],
  "Gajardo":                [357,  0,    0,    0,0,0,0,0,0,0,0,0],
};
// Prior year totals (for trend chart)
const YEAR_TOTALS = [
  { year: "2022", total: 111467,  avua: 108519, paladar: 0,       svol: 1440,   choco: 0,     drifter: 0,     gajardo: 1507  },
  { year: "2023", total: 1308665, avua: 767664, paladar: 292653,  svol: 115814, choco: 44752, drifter: 46175, gajardo: 28567 },
  { year: "2024", total: 1631279, avua: 661531, paladar: 717704,  svol: 123361, choco: 39325, drifter: 61254, gajardo: 21164 },
  { year: "2025", total: 1761123, avua: 783452, paladar: 712546,  svol: 180893, choco: 44136, drifter: 36427, gajardo: 3668  },
  { year: "2026*",total: 137088,  avua: 82213,  paladar: 28795,   svol: 20705,  choco: 4578,  drifter: 440,   gajardo: 357   },
];
// YTD 2025 same period (Jan–Mar) for correct YAGO
const YTD_2025_SAME = {
  "Avuá Cachaça":            43461+73517+45228,  // $162,206
  "Paladar Tequila":         78666+45418+29397,  // $153,481
  "Svöl Aquavit":            8283+19233+16060,   // $43,576
  "Choco Smooth":            4290+0+10465,       // $14,755
  "Drifter Craft Cocktails": 9065+1640+4950,     // $15,655
  "Gajardo":                 578+1005+0,         // $1,583
};
const TOTAL_YTD_2025_SAME = Object.values(YTD_2025_SAME).reduce((a,b)=>a+b,0); // ~$391,256

// Build stacked bar data for 2025
const stackedBar2025 = MONTHS.map((m, i) => {
  const row = { m };
  BRANDS_ORDER.forEach(b => { row[b] = DATA_2025[b][i]; });
  return row;
});
// Build stacked bar for 2026 (YTD only — show Jan-Mar)
const stackedBar2026 = MONTHS.map((m, i) => {
  const row = { m };
  BRANDS_ORDER.forEach(b => { row[b] = DATA_2026[b][i]; });
  return row;
});
// VS a year ago (monthly, correct calculation)
const vsYago = MONTHS.slice(0, 3).map((m, i) => {
  const row = { m };
  BRANDS_ORDER.forEach(b => {
    const curr = DATA_2026[b][i];
    const prior = DATA_2025[b][i];
    row[b] = prior > 0 ? Math.round(((curr - prior) / prior) * 100) : curr > 0 ? 100 : 0;
  });
  const tot2026 = BRANDS_ORDER.reduce((s,b)=>s+DATA_2026[b][i],0);
  const tot2025 = BRANDS_ORDER.reduce((s,b)=>s+DATA_2025[b][i],0);
  row["Total"] = tot2025 > 0 ? Math.round(((tot2026-tot2025)/tot2025)*100) : 0;
  return row;
});

const skuRows = [
  { brand: "Avuá Cachaça",    sku: "Avuá Cachaça Prata 750ml",                           amount: 46378, cases: 368, doh: 82,  tier: "HERO",  vel: 122.7 },
  { brand: "Avuá Cachaça",    sku: "Avuá Cachaça Amburana 750ml",                        amount: 27605, cases: 177, doh: 62,  tier: "HERO",  vel: 59.0  },
  { brand: "Svöl Aquavit",    sku: "Svöl Aquavit Swedish 750ml",                         amount: 11272, cases: 83,  doh: 91,  tier: "HERO",  vel: 27.7  },
  { brand: "Svöl Aquavit",    sku: "Svöl Aquavit Danish 750ml",                          amount: 9433,  cases: 73,  doh: 78,  tier: "CORE",  vel: 24.3  },
  { brand: "Paladar Tequila", sku: "Paladar Tequila Blanco 750ml",                       amount: 6798,  cases: 42,  doh: 44,  tier: "CORE",  vel: 14.0  },
  { brand: "Avuá Cachaça",    sku: "Avuá Falernum Liq 750mL",                            amount: 5370,  cases: 54,  doh: 110, tier: "HERO",  vel: 18.0  },
  { brand: "Paladar Tequila", sku: "Paladar SBS Reposado METAL BLADE 6/750",             amount: 4200,  cases: 21,  doh: 34,  tier: "WATCH", vel: 7.0   },
  { brand: "Choco Smooth",    sku: "Choco Smooth White Chocolate 375mL",                 amount: 3990,  cases: 30,  doh: 55,  tier: "WATCH", vel: 10.0  },
  { brand: "Paladar Tequila", sku: "Paladar Tequila Reposado 750ml",                     amount: 3248,  cases: 17,  doh: 28,  tier: "WATCH", vel: 5.7   },
  { brand: "Paladar Tequila", sku: "Paladar Agave Amburana",                             amount: 3016,  cases: 13,  doh: 18,  tier: "WATCH", vel: 4.3   },
  { brand: "Avuá Cachaça",    sku: "Avuá Cachaça Oak 750ml",                             amount: 2860,  cases: 18,  doh: 88,  tier: "CORE",  vel: 6.0   },
  { brand: "Paladar Tequila", sku: "Paladar Toasted French Revere ++",                   amount: 2030,  cases: 7,   doh: 14,  tier: "EXIT",  vel: 2.3   },
  { brand: "Paladar Tequila", sku: "Paladar Tequila Reposado 700mL",                     amount: 1910,  cases: 10,  doh: 22,  tier: "EXIT",  vel: 3.3   },
  { brand: "Paladar Tequila", sku: "Paladar Reposado Experimental Sauternes 6/750",      amount: 1740,  cases: 6,   doh: 11,  tier: "EXIT",  vel: 2.0   },
  { brand: "Paladar Tequila", sku: "Paladar Toasted American Medium Plus",               amount: 1740,  cases: 6,   doh: 9,   tier: "EXIT",  vel: 2.0   },
  { brand: "Paladar Tequila", sku: "Paladar Toasted French Medium",                      amount: 1740,  cases: 6,   doh: 7,   tier: "EXIT",  vel: 2.0   },
  { brand: "Paladar Tequila", sku: "Paladar Tequila Reposado Experimental Barolo 6/750", amount: 1450,  cases: 5,   doh: 5,   tier: "EXIT",  vel: 1.7   },
];

// ── SKU Monthly Revenue Data ─────────────────────────────────────────────
// m2026: Jan–Mar actuals (indices 0-2), 0 for future months
// m2025: Full 12 months prior year
// Trend computed from last 2 vs first 1 month of YTD
const SKU_MONTHLY = {
  "Avuá Cachaça Prata 750ml": {
    m2026: [13200, 11500, 21678, 0,0,0,0,0,0,0,0,0],
    m2025: [18200, 15400, 12800, 19600, 17200, 15800, 14200, 12600, 15900, 17400, 11200,  9800],
  },
  "Avuá Cachaça Amburana 750ml": {
    m2026: [8200,  7100, 12305, 0,0,0,0,0,0,0,0,0],
    m2025: [11400,  9800,  8200, 11800, 10400,  9100,  8600,  7400,  9800, 10600,  7200,  5800],
  },
  "Svöl Aquavit Swedish 750ml": {
    m2026: [3800,  3200,  4272, 0,0,0,0,0,0,0,0,0],
    m2025: [4900,  4200,  3800,  5600,  4900,  4200,  3800,  3200,  4400,  5100,  3600,  3100],
  },
  "Svöl Aquavit Danish 750ml": {
    m2026: [3100,  2600,  3733, 0,0,0,0,0,0,0,0,0],
    m2025: [4200,  3600,  3200,  4800,  4200,  3600,  3200,  2800,  3800,  4400,  3200,  2800],
  },
  "Paladar Tequila Blanco 750ml": {
    m2026: [2500,  2100,  2198, 0,0,0,0,0,0,0,0,0],
    m2025: [5800,  4900,  4200,  6400,  5600,  5100,  4800,  4200,  5400,  6200,  4400,  3600],
  },
  "Avuá Falernum Liq 750mL": {
    m2026: [1600,  1400,  2370, 0,0,0,0,0,0,0,0,0],
    m2025: [2200,  1900,  1600,  2600,  2200,  1900,  1700,  1400,  1900,  2100,  1500,  1200],
  },
  "Paladar SBS Reposado METAL BLADE 6/750": {
    m2026: [1800,  1400,   990, 0,0,0,0,0,0,0,0,0],
    m2025: [3100,  2600,  2200,  3600,  3200,  2800,  2600,  2200,  2900,  3400,  2400,  1900],
  },
  "Choco Smooth White Chocolate 375mL": {
    m2026: [0,     3990,     0, 0,0,0,0,0,0,0,0,0],
    m2025: [1800,  1600,  2400,  1900,  1600,  1400,  1200,  1000,  1400,  1600,  1200,   900],
  },
  "Paladar Tequila Reposado 750ml": {
    m2026: [1400,   980,   868, 0,0,0,0,0,0,0,0,0],
    m2025: [2800,  2400,  2000,  3200,  2800,  2400,  2200,  1800,  2400,  2800,  2000,  1600],
  },
  "Paladar Agave Amburana": {
    m2026: [1200,   880,   936, 0,0,0,0,0,0,0,0,0],
    m2025: [2400,  2000,  1700,  2800,  2400,  2000,  1900,  1600,  2100,  2400,  1700,  1400],
  },
  "Avuá Cachaça Oak 750ml": {
    m2026: [880,    760,  1220, 0,0,0,0,0,0,0,0,0],
    m2025: [1400,  1200,  1000,  1600,  1400,  1200,  1100,   900,  1200,  1400,  1000,   800],
  },
  "Paladar Toasted French Revere ++": {
    m2026: [1100,   600,   330, 0,0,0,0,0,0,0,0,0],
    m2025: [1800,  1600,  1400,  2200,  1900,  1700,  1600,  1400,  1800,  2100,  1500,  1200],
  },
  "Paladar Tequila Reposado 700mL": {
    m2026: [900,    580,   430, 0,0,0,0,0,0,0,0,0],
    m2025: [1600,  1400,  1200,  2000,  1700,  1500,  1400,  1200,  1600,  1900,  1400,  1100],
  },
  "Paladar Reposado Experimental Sauternes 6/750": {
    m2026: [800,    540,   400, 0,0,0,0,0,0,0,0,0],
    m2025: [1400,  1200,  1000,  1800,  1600,  1400,  1300,  1100,  1500,  1700,  1200,  1000],
  },
  "Paladar Toasted American Medium Plus": {
    m2026: [820,    520,   400, 0,0,0,0,0,0,0,0,0],
    m2025: [1400,  1200,  1000,  1800,  1600,  1400,  1300,  1100,  1500,  1700,  1200,  1000],
  },
  "Paladar Toasted French Medium": {
    m2026: [800,    520,   420, 0,0,0,0,0,0,0,0,0],
    m2025: [1300,  1100,   900,  1600,  1400,  1200,  1100,   900,  1300,  1500,  1100,   900],
  },
  "Paladar Tequila Reposado Experimental Barolo 6/750": {
    m2026: [700,    400,   350, 0,0,0,0,0,0,0,0,0],
    m2025: [1200,  1000,   900,  1500,  1300,  1100,  1000,   800,  1100,  1300,   900,   700],
  },
};

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Velocity trend: compare last 2 months vs first month
function skuTrend(sku) {
  const d = SKU_MONTHLY[sku];
  if (!d) return "STEADY";
  const ytd = d.m2026.filter(v => v > 0);
  if (ytd.length < 2) return "STEADY";
  const recent = ytd.slice(-1)[0];
  const earlier = ytd[0];
  const pct = ((recent - earlier) / earlier) * 100;
  if (pct > 12)  return "ACCELERATING";
  if (pct < -12) return "DECELERATING";
  return "STEADY";
}

const TREND_CFG = {
  ACCELERATING: { bg: GREEN_BG,  text: GREEN,  border: "#86EFAC", icon: "↑" },
  STEADY:       { bg: "#E8F4F5", text: TEAL,   border: "#B2D8DC", icon: "→" },
  DECELERATING: { bg: RED_BG,    text: RED,    border: "#FCA5A5", icon: "↓" },
};

// ── Full state dataset (real data from app screenshots) ───────────────────
const STATE_RAW = [
  { abbr:"NY", name:"New York",       dist:"Empire New York",           value:26709.28, cases:193, pv:48200,  pc:342 },
  { abbr:"CA", name:"California",     dist:"Pacific Edge CA",           value:26204.00, cases:171, pv:73896,  pc:515 },
  { abbr:"NV", name:"Nevada",         dist:"Breakthru Nevada",          value:16050.68, cases:128, pv:37800,  pc:285 },
  { abbr:"FL", name:"Florida",        dist:"Breakthru Florida",         value:12115.00, cases:82,  pv:16485,  pc:118 },
  { abbr:"AR", name:"Arkansas",       dist:"Denux Arkansas",            value:5870.00,  cases:40,  pv:2380,   pc:17  },
  { abbr:"MA", name:"Massachusetts",  dist:"MS Walker Massachusetts",   value:5560.00,  cases:40,  pv:9270,   pc:62  },
  { abbr:"LA", name:"Louisiana",      dist:"Wines Unlimited Louisiana", value:5368.00,  cases:52,  pv:8260,   pc:74  },
  { abbr:"CT", name:"Connecticut",    dist:"Brescome Barton Inc",       value:5329.00,  cases:41,  pv:8160,   pc:62  },
  { abbr:"DC", name:"Washington DC",  dist:"The Bourbon Concierge",     value:4199.58,  cases:21,  pv:13375,  pc:89  },
  { abbr:"SC", name:"South Carolina", dist:"Aleph Wines",               value:3865.50,  cases:20,  pv:5520,   pc:29  },
  { abbr:"MN", name:"Minnesota",      dist:"New France Minnesota",      value:3832.04,  cases:24,  pv:5100,   pc:31  },
  { abbr:"MD", name:"Maryland",       dist:"Breakthru Maryland",        value:3773.00,  cases:27,  pv:6290,   pc:44  },
  { abbr:"GA", name:"Georgia",        dist:"Prime Wine & Spirits GA",   value:3276.00,  cases:25,  pv:5950,   pc:45  },
  { abbr:"WA", name:"Washington",     dist:"Vehrs Distributing",        value:2971.56,  cases:21,  pv:2580,   pc:18  },
  { abbr:"PA", name:"Pennsylvania",   dist:"Breakthru Pennsylvania",    value:2900.00,  cases:10,  pv:7220,   pc:28  },
  { abbr:"AZ", name:"Arizona",        dist:"Breakthru Arizona",         value:2440.00,  cases:20,  pv:29168,  pc:152 },
  { abbr:"IL", name:"Illinois",       dist:"Breakthru Illinois",        value:2140.00,  cases:15,  pv:3450,   pc:24  },
  { abbr:"NJ", name:"New Jersey",     dist:"Allied Bev New Jersey",     value:1648.00,  cases:12,  pv:2060,   pc:14  },
  { abbr:"MO", name:"Missouri",       dist:"Vintegrity Missouri",       value:1604.00,  cases:11,  pv:3200,   pc:21  },
  { abbr:"CO", name:"Colorado",       dist:"Breakthru Colorado",        value:1601.00,  cases:13,  pv:0,      pc:0   },
  { abbr:"KS", name:"Kansas",         dist:"Vintegrity Kansas",         value:960.00,   cases:8,   pv:1068,   pc:9   },
  { abbr:"TN", name:"Tennessee",      dist:"Advintage Tennessee",       value:816.00,   cases:7,   pv:1170,   pc:10  },
  { abbr:"NC", name:"North Carolina", dist:"NC ABC",                    value:539.50,   cases:2,   pv:1540,   pc:6   },
];

const STATE_MAP = {};
STATE_RAW.forEach((s, i) => {
  const isNew  = s.pv === 0;
  const yoy    = isNew ? null : ((s.value - s.pv) / s.pv) * 100;
  const yoyCs  = isNew ? null : s.pc > 0 ? ((s.cases - s.pc) / s.pc) * 100 : null;
  const chgVal = s.value - s.pv;
  let status, action;
  if (isNew)           { status = "NEW";       action = "Onboard";         }
  else if (yoy >  10)  { status = "GROWTH";    action = "Expand";          }
  else if (yoy >= -10) { status = "STABLE";    action = "Maintain";        }
  else if (yoy >= -30) { status = "AT RISK";   action = "Schedule call";   }
  else                 { status = "DECLINING"; action = yoy < -60 ? "Evaluate change" : "Escalate"; }
  STATE_MAP[s.abbr] = { ...s, rank: i + 1, isNew, yoy, yoyCs, chgVal, status, action };
});

const STATE_STATUS = {
  NEW:       { tile: "#1D4ED8", tileText: WHITE, bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", icon: "★", label: "NEW MARKET" },
  GROWTH:    { tile: "#16A34A", tileText: WHITE, bg: GREEN_BG,  text: GREEN,     border: "#86EFAC", icon: "▲", label: "GROWTH"     },
  STABLE:    { tile: TEAL,      tileText: WHITE, bg: "#E8F4F5", text: TEAL,      border: "#B2D8DC", icon: "→", label: "STABLE"     },
  "AT RISK": { tile: "#D97706", tileText: WHITE, bg: AMBER_BG,  text: AMBER,     border: "#FCD34D", icon: "⚠", label: "AT RISK"    },
  DECLINING: { tile: "#DC2626", tileText: WHITE, bg: RED_BG,    text: RED,       border: "#FCA5A5", icon: "▼", label: "DECLINING"  },
  INACTIVE:  { tile: "#E5E7EB", tileText: GRAY_MID, bg: CARD_BG, text: GRAY,    border: BORDER,    icon: "",  label: ""           },
};

const STATE_ACTION_CFG = {
  "Onboard":         { bg: "#EFF6FF", text: "#1D4ED8" },
  "Expand":          { bg: GREEN_BG,  text: GREEN     },
  "Maintain":        { bg: "#E8F4F5", text: TEAL      },
  "Schedule call":   { bg: AMBER_BG,  text: AMBER     },
  "Escalate":        { bg: "#FEE2E2", text: "#B91C1C" },
  "Evaluate change": { bg: RED_BG,    text: RED       },
};

// Tile cartogram grid [abbr, col, row]
const MAP_TILES = [
  ["ME",11,0],
  ["VT",9,1],["NH",10,1],
  ["WA",0,2],["MT",1,2],["ND",2,2],["MN",3,2],["NY",8,2],["MA",9,2],["RI",10,2],
  ["OR",0,3],["ID",1,3],["WY",2,3],["SD",3,3],["WI",4,3],["MI",5,3],["PA",7,3],["CT",9,3],
  ["CA",0,4],["NV",1,4],["UT",2,4],["CO",3,4],["NE",4,4],["IA",5,4],["IL",6,4],["IN",7,4],["OH",8,4],["NJ",9,4],
  ["AZ",2,5],["NM",3,5],["KS",4,5],["MO",5,5],["KY",6,5],["WV",7,5],["VA",8,5],["MD",9,5],["DE",10,5],
  ["TX",3,6],["OK",4,6],["AR",5,6],["TN",6,6],["NC",7,6],["SC",8,6],["DC",9,6],
  ["LA",4,7],["MS",5,7],["AL",6,7],["GA",7,7],
  ["FL",6,8],
  ["AK",0,9],["HI",2,9],
];
const T_S = 36, T_G = 3, T_STEP = T_S + T_G;

// ── Open PO Pipeline (Monday.com source) ─────────────────────────────────
const openPOs = [
  { id: "PO-2841", dist: "Empire State Spirits", state: "NY", brand: "Avuá Cachaça",    sku: "Avuá Cachaça Prata 750ml",     cases: 48, value: 9840,  status: "ALLOCATED", estShip: "Mar 12", daysOut: 4,  risk: null },
  { id: "PO-2842", dist: "Empire State Spirits", state: "NY", brand: "Svöl Aquavit",    sku: "Svöl Aquavit Swedish 750ml",   cases: 24, value: 3264,  status: "ALLOCATED", estShip: "Mar 12", daysOut: 4,  risk: null },
  { id: "PO-2847", dist: "Golden State Bev.",    state: "CA", brand: "Avuá Cachaça",    sku: "Avuá Cachaça Amburana 750ml",  cases: 36, value: 5614,  status: "CONFIRMED", estShip: "Mar 15", daysOut: 7,  risk: null },
  { id: "PO-2851", dist: "Lone Star Dist.",      state: "TX", brand: "Paladar Tequila", sku: "Paladar Tequila Blanco 750ml", cases: 24, value: 3888,  status: "CONFIRMED", estShip: "Mar 18", daysOut: 10, risk: null },
  { id: "PO-2852", dist: "Lone Star Dist.",      state: "TX", brand: "Svöl Aquavit",    sku: "Svöl Aquavit Danish 750ml",    cases: 18, value: 2318,  status: "CONFIRMED", estShip: "Mar 18", daysOut: 10, risk: null },
  { id: "PO-2859", dist: "Sunshine Dist.",       state: "FL", brand: "Avuá Cachaça",    sku: "Avuá Cachaça Prata 750ml",     cases: 12, value: 2460,  status: "RECEIVED",  estShip: "Mar 25", daysOut: 17, risk: "AR overdue 38d — confirm before allocating" },
];

const PO_STATUS_CFG = {
  RECEIVED:  { bg: "#F3F4F6", text: GRAY,      border: BORDER     },
  CONFIRMED: { bg: AMBER_BG,  text: AMBER,     border: "#FCD34D"  },
  ALLOCATED: { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE"  },
  SHIPPED:   { bg: GREEN_BG,  text: GREEN,     border: "#86EFAC"  },
  INVOICED:  { bg: "#F5F3FF", text: "#6D28D9", border: "#DDD6FE"  },
};

// monthlyData: full 12-month dataset with brand breakdown + cases
// actual/ly are whole-portfolio totals; brand keys are per-brand revenue splits
const monthlyData = [
  { m:"Jan", mo:1, actual:46129,  target:95000, ly:148000, lyTarget:120000, cases:310, lyCases:991,  avua:27674, paladar:9687,  svol:6963,  other:1805  },
  { m:"Feb", mo:2, actual:39675,  target:95000, ly:101400, lyTarget:120000, cases:302, lyCases:676,  avua:23796, paladar:8332,  svol:5991,  other:1556  },
  { m:"Mar", mo:3, actual:51285,  target:95000, ly:52756,  lyTarget:120000, cases:345, lyCases:352,  avua:30773, paladar:10770, svol:7744,  other:1998  },
  { m:"Apr", mo:4, actual:0,      target:95000, ly:88230,  lyTarget:120000, cases:0,   lyCases:588,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"May", mo:5, actual:0,      target:95000, ly:78900,  lyTarget:120000, cases:0,   lyCases:526,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Jun", mo:6, actual:0,      target:95000, ly:71200,  lyTarget:120000, cases:0,   lyCases:475,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Jul", mo:7, actual:0,      target:95000, ly:65800,  lyTarget:120000, cases:0,   lyCases:439,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Aug", mo:8, actual:0,      target:95000, ly:59500,  lyTarget:120000, cases:0,   lyCases:397,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Sep", mo:9, actual:0,      target:95000, ly:68700,  lyTarget:120000, cases:0,   lyCases:458,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Oct", mo:10,actual:0,      target:95000, ly:72100,  lyTarget:120000, cases:0,   lyCases:481,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Nov", mo:11,actual:0,      target:95000, ly:48300,  lyTarget:120000, cases:0,   lyCases:322,  avua:0,     paladar:0,     svol:0,     other:0     },
  { m:"Dec", mo:12,actual:0,      target:95000, ly:28015,  lyTarget:120000, cases:0,   lyCases:187,  avua:0,     paladar:0,     svol:0,     other:0     },
];

// Per-brand full monthly history (used in brand-filter mode)
const BRAND_MONTHLY = {
  "All Brands":       monthlyData.map(d => ({ ...d })),
  "Avuá Cachaça":     monthlyData.map(d => ({ ...d, actual: d.avua,    target: Math.round(d.target*0.60), ly: Math.round(d.ly*0.60), cases: Math.round(d.cases*0.65) })),
  "Paladar Tequila":  monthlyData.map(d => ({ ...d, actual: d.paladar, target: Math.round(d.target*0.21), ly: Math.round(d.ly*0.21), cases: Math.round(d.cases*0.14) })),
  "Svöl Aquavit":     monthlyData.map(d => ({ ...d, actual: d.svol,    target: Math.round(d.target*0.15), ly: Math.round(d.ly*0.15), cases: Math.round(d.cases*0.16) })),
  "Other Brands":     monthlyData.map(d => ({ ...d, actual: d.other,   target: Math.round(d.target*0.04), ly: Math.round(d.ly*0.04), cases: Math.round(d.cases*0.05) })),
};

const TIER_CFG = {
  HERO:  { bg: GREEN_BG, text: GREEN, border: "#86EFAC" },
  CORE:  { bg: "#E8F4F5", text: TEAL, border: "#B2D8DC" },
  WATCH: { bg: AMBER_BG, text: AMBER, border: "#FCD34D" },
  EXIT:  { bg: RED_BG,   text: RED,   border: "#FCA5A5" },
};


const CATEGORY_LABELS = {
  cachaca:  "Cachaça",
  tequila:  "Tequila",
  gin:      "Gin",
  rtd:      "RTD / Cocktail",
  aquavit:  "Aquavit",
  liqueur:  "Liqueur / Specialty",
  bourbon:  "Bourbon",
  other:    "Other / Unclassified",
};

const STAGE_CFG = {
  ESTABLISHED: { bg: GREEN_BG,  text: GREEN,      border: "#86EFAC", icon: "🏆" },
  LAUNCH:      { bg: AMBER_BG,  text: AMBER,      border: "#FCD34D", icon: "🚀" },
  WATCH:       { bg: "#FEF3C7", text: "#92400E",  border: "#FCD34D", icon: "👁" },
  EXIT:        { bg: RED_BG,    text: RED,         border: "#FCA5A5", icon: "🚪" },
};

const STATUS_DOT = {
  Active:   "#22C55E",
  Review:   "#F59E0B",
  Inactive: "#9CA3AF",
};

const PRODUCT_CATALOG = [
  { id:"P001", code:"AVU-PRA-750", name:"Avuá Cachaça Prata 750ml",                       brand:"Avuá Cachaça",    line:"Avuá",    type:"Cachaça",  category:"cachaca", stage:"ESTABLISHED", ttbId:"TTB-2019-0041", doh:82,  vel:122.7, status:"Active",   organic:false },
  { id:"P002", code:"AVU-AMB-750", name:"Avuá Cachaça Amburana 750ml",                    brand:"Avuá Cachaça",    line:"Avuá",    type:"Cachaça",  category:"cachaca", stage:"ESTABLISHED", ttbId:"TTB-2019-0042", doh:62,  vel:59.0,  status:"Active",   organic:false },
  { id:"P003", code:"AVU-OAK-750", name:"Avuá Cachaça Oak 750ml",                         brand:"Avuá Cachaça",    line:"Avuá",    type:"Cachaça",  category:"cachaca", stage:"ESTABLISHED", ttbId:"TTB-2020-0018", doh:88,  vel:6.0,   status:"Active",   organic:false },
  { id:"P004", code:"AVU-FAL-750", name:"Avuá Falernum Liq 750mL",                        brand:"Avuá Cachaça",    line:"Avuá",    type:"Liqueur",  category:"liqueur", stage:"ESTABLISHED", ttbId:"TTB-2021-0033", doh:110, vel:18.0,  status:"Active",   organic:false },
  { id:"P005", code:"PAL-BLA-750", name:"Paladar Tequila Blanco 750ml",                   brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"ESTABLISHED", ttbId:"TTB-2020-0055", doh:44,  vel:14.0,  status:"Active",   organic:false },
  { id:"P006", code:"PAL-REP-750", name:"Paladar Tequila Reposado 750ml",                 brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"WATCH",       ttbId:"TTB-2020-0056", doh:28,  vel:5.7,   status:"Review",   organic:false },
  { id:"P007", code:"PAL-REP-700", name:"Paladar Tequila Reposado 700mL",                 brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"EXIT",        ttbId:"TTB-2020-0057", doh:22,  vel:3.3,   status:"Review",   organic:false },
  { id:"P008", code:"PAL-SBS-750", name:"Paladar SBS Reposado METAL BLADE 6/750",         brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"WATCH",       ttbId:"TTB-2021-0061", doh:34,  vel:7.0,   status:"Active",   organic:false },
  { id:"P009", code:"PAL-AGA-750", name:"Paladar Agave Amburana",                         brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"WATCH",       ttbId:"TTB-2022-0014", doh:18,  vel:4.3,   status:"Review",   organic:false },
  { id:"P010", code:"PAL-SAU-750", name:"Paladar Reposado Experimental Sauternes 6/750",  brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"EXIT",        ttbId:null,            doh:11,  vel:2.0,   status:"Review",   organic:false },
  { id:"P011", code:"PAL-BAR-750", name:"Paladar Tequila Reposado Experimental Barolo 6/750", brand:"Paladar Tequila", line:"Paladar", type:"Tequila", category:"tequila", stage:"EXIT",    ttbId:null,            doh:5,   vel:1.7,   status:"Review",   organic:false },
  { id:"P012", code:"PAL-FRA-750", name:"Paladar Toasted French Revere ++",               brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"EXIT",        ttbId:"TTB-2022-0019", doh:14,  vel:2.3,   status:"Review",   organic:false },
  { id:"P013", code:"PAL-AME-750", name:"Paladar Toasted American Medium Plus",           brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"EXIT",        ttbId:"TTB-2022-0020", doh:9,   vel:2.0,   status:"Review",   organic:false },
  { id:"P014", code:"PAL-FRM-750", name:"Paladar Toasted French Medium",                  brand:"Paladar Tequila", line:"Paladar", type:"Tequila",  category:"tequila", stage:"EXIT",        ttbId:"TTB-2022-0021", doh:7,   vel:2.0,   status:"Review",   organic:false },
  { id:"P015", code:"SVO-SWE-750", name:"Svöl Aquavit Swedish 750ml",                     brand:"Svöl Aquavit",    line:"Svöl",    type:"Aquavit",  category:"aquavit", stage:"ESTABLISHED", ttbId:"TTB-2020-0088", doh:91,  vel:27.7,  status:"Active",   organic:false },
  { id:"P016", code:"SVO-DAN-750", name:"Svöl Aquavit Danish 750ml",                      brand:"Svöl Aquavit",    line:"Svöl",    type:"Aquavit",  category:"aquavit", stage:"ESTABLISHED", ttbId:"TTB-2020-0089", doh:78,  vel:24.3,  status:"Active",   organic:false },
  { id:"P017", code:"CHO-WHI-375", name:"Choco Smooth White Chocolate 375mL",             brand:"Choco Smooth",    line:"Choco",   type:"Liqueur",  category:"liqueur", stage:"WATCH",       ttbId:"TTB-2021-0044", doh:55,  vel:10.0,  status:"Active",   organic:false },
  { id:"P018", code:"DRI-CRA-750", name:"Drifter Craft Cocktails 750ml",                  brand:"Drifter Craft Cocktails", line:"Drifter", type:"RTD", category:"rtd", stage:"WATCH",      ttbId:"TTB-2021-0077", doh:30,  vel:3.0,   status:"Review",   organic:false },
  { id:"P019", code:"GAJ-RED-750", name:"Gajardo Red 750ml",                              brand:"Gajardo",         line:"Gajardo", type:"Other",    category:"other",   stage:"EXIT",        ttbId:"TTB-2020-0031", doh:45,  vel:1.0,   status:"Inactive", organic:false },
];

// ── Reusable atoms ────────────────────────────────────────────────────────
const TierBadge = ({ tier }) => {
  const c = TIER_CFG[tier];
  return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{tier}</span>;
};

const YagoCell = ({ val }) => {
  if (val === 0) return <span style={{ color: GRAY_MID }}>—</span>;
  const n = -val; const up = n > 0;
  return <span style={{ color: up ? GREEN : RED, fontWeight: 600 }}>{up ? "▲" : "▼"} {Math.abs(n)}%</span>;
};

const DohPill = ({ val }) => {
  const color = val < 45 ? RED : val < 90 ? AMBER : GREEN;
  const bg    = val < 45 ? RED_BG : val < 90 ? AMBER_BG : GREEN_BG;
  return <span style={{ background: bg, color, fontWeight: 700, fontSize: 11, padding: "2px 7px", borderRadius: 4 }}>{val}d{val < 45 ? " ⚠" : ""}</span>;
};




const TH = ({ children, right, w }) => (
  <th style={{ background: TEAL, color: WHITE, padding: "8px 10px", textAlign: right ? "right" : "left", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", width: w }}>{children}</th>
);

const Select = ({ label, w }) => (
  <select style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: DARK, background: WHITE, cursor: "pointer", minWidth: w || 130 }}>
    <option>{label}</option>
  </select>
);

const Btn = ({ children, onClick, primary }) => (
  <button onClick={onClick} style={{ padding: "5px 10px", border: `1px solid ${primary ? TEAL : BORDER}`, borderRadius: 5, background: primary ? TEAL : CARD_BG, color: primary ? WHITE : GRAY, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
    {children}
  </button>
);

const Alert = ({ type, children }) => {
  const cfg = {
    warning:  { bg: AMBER_BG, border: "#FCD34D", left: AMBER, icon: "⚠" },
    critical: { bg: RED_BG,   border: "#FCA5A5", left: RED,   icon: "🚨" },
    info:     { bg: "#EFF6FF", border: "#BFDBFE", left: "#2563EB", icon: "ℹ" },
  }[type];
  return (
    <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderLeft: `4px solid ${cfg.left}`, borderRadius: 8, padding: "9px 14px", marginBottom: 12, display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12 }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{cfg.icon}</span>
      <div style={{ color: DARK, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
};

const Section = ({ title, controls, children, noPad }) => (
  <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
    {title && (
      <div style={{ padding: "13px 18px 11px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>{title}</div>
        {controls && <div style={{ display: "flex", gap: 6 }}>{controls}</div>}
      </div>
    )}
    <div style={{ padding: noPad ? 0 : "14px 18px" }}>{children}</div>
  </div>
);

const KPICard = ({ cashLabel, cashValue, casesLabel, casesValue, trend, trendLabel, pacing, pacingPct }) => {
  const pc = pacingPct < 40 ? RED : pacingPct < 70 ? AMBER : GREEN;
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "14px 16px", flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: DARK }}>{cashValue}</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>{cashLabel}</div>
          {trend !== undefined && (
            <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: trend >= 0 ? GREEN : RED }}>
              {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(2)}%
            </div>
          )}
          {pacing && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: GRAY, marginBottom: 3 }}>
                <span>{pacing}</span>
                <span style={{ fontWeight: 700, color: pc }}>{pacingPct?.toFixed(0)}%</span>
              </div>
              <div style={{ background: BORDER, borderRadius: 99, height: 4, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(pacingPct, 100)}%`, height: "100%", background: pc, borderRadius: 99 }} />
              </div>
            </div>
          )}
        </div>
        <div style={{ width: 32, height: 32, borderRadius: 6, background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>💲</div>
      </div>
      <div style={{ height: 1, background: BORDER, margin: "10px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: DARK }}>{casesValue}</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>{casesLabel}</div>
        </div>
        <div style={{ width: 32, height: 32, borderRadius: 6, background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>📦</div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// PAGE: SALES TOPLINE
// ═════════════════════════════════════════════════════════════════════════════
function SalesTopline({ navigate }) {
  const [poExpanded, setPoExpanded] = useState(false);

  // ── Core numbers ──────────────────────────────────────────────────────────
  const ytd = 137088, lyytd = 302156, annGoal = 1140000;
  const pace = (ytd / 3) * 12;
  const pacePct = (pace / annGoal) * 100;
  const totalCB = brandRows.reduce((s, b) => s + b.cb, 0);
  const netRev = ytd - totalCB;

  // ── Run-rate projection ───────────────────────────────────────────────────
  const TODAY_DAY   = 8;          // March 8
  const DAYS_IN_MO  = 31;         // March has 31 days
  const MTD_REV     = 51284.85;
  const MTD_CASES   = 345;
  const MONTHLY_TGT = 95000;
  const LY_MAR      = 52756;

  const dailyRate       = MTD_REV / TODAY_DAY;
  const projectedMonth  = dailyRate * DAYS_IN_MO;
  const projVsTarget    = ((projectedMonth / MONTHLY_TGT) - 1) * 100;
  const projVsLY        = ((projectedMonth / LY_MAR) - 1) * 100;
  const daysElapsedPct  = (TODAY_DAY / DAYS_IN_MO) * 100;
  const mtdVsTargetPct  = (MTD_REV / MONTHLY_TGT) * 100;

  // ── Open PO pipeline ─────────────────────────────────────────────────────
  const totalPOValue    = openPOs.reduce((s, p) => s + p.value, 0);
  const totalPOCases    = openPOs.reduce((s, p) => s + p.cases, 0);
  const riskPOs         = openPOs.filter(p => p.risk);
  const projectedYTD    = ytd + totalPOValue;
  const projectedYTDPct = (projectedYTD / annGoal) * 100;

  return (
    <div>
      {/* ── Alerts ─────────────────────────────────────────────────────────── */}
      <Alert type="warning">
        <strong>Annual Pacing:</strong> At current run rate, full-year projection is <strong>{fmt(pace)}</strong> vs <strong>{fmt(annGoal)}</strong> goal — {pacePct.toFixed(0)}% attainment, gap of <strong>{fmt(annGoal - pace)}</strong>.
        {" "}<span onClick={() => navigate("monthly")} style={{ color: TEAL, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>View Monthly Detail →</span>
      </Alert>
      <Alert type="critical">
        <strong>Concentration Risk:</strong> Avuá Cachaça = 59.97% of portfolio revenue. One supply or compliance event eliminates $82K+ in revenue.
        {" "}<span onClick={() => navigate("brand")} style={{ color: RED, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>View Brand Performance →</span>
      </Alert>

      {/* ── Yearly KPIs ────────────────────────────────────────────────────── */}
      <Section title="Sales Performance Yearly" controls={[<Select key="1" label="Select Brands" />, <Select key="2" label="2026" w={90} />]}>
        <div style={{ display: "flex", gap: 12 }}>
          <KPICard cashLabel="Current Year To Date — $" cashValue={fmt(ytd)} casesLabel="Current Year To Date — Cases" casesValue="957.00" pacing="Pacing to annual goal" pacingPct={pacePct} />
          <KPICard cashLabel="Last Year To Date — $" cashValue={fmt(lyytd)} casesLabel="Last Year To Date — Cases" casesValue="1,901.00" />
          <KPICard cashLabel="YoY % — $" cashValue="-54.63%" trend={-54.63} casesLabel="YoY % — Cases" casesValue="-49.66%" />
        </div>
      </Section>

      {/* ── Net Revenue strip ──────────────────────────────────────────────── */}
      <Section title="Net Revenue After Chargebacks">
        <div style={{ display: "flex", gap: 12 }}>
          {[
            { label: "Gross Revenue YTD",    value: fmt(ytd),        hi: false },
            { label: "Total Chargebacks YTD",value: fmt(totalCB),    sub: `${((totalCB/ytd)*100).toFixed(1)}% portfolio CB rate`, warn: true },
            { label: "Net Revenue to Brand", value: fmt(netRev),     hi: true },
            { label: "Net Revenue / Case",   value: fmt(netRev/957), hi: true },
          ].map((c, i) => (
            <div key={i} style={{ flex: 1, background: c.hi ? "#F0FDF4" : c.warn ? RED_BG : CARD_BG, border: `1px solid ${c.hi ? "#86EFAC" : c.warn ? "#FCA5A5" : BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.hi ? GREEN : c.warn ? RED : DARK }}>{c.value}</div>
              {c.sub && <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{c.sub}</div>}
            </div>
          ))}
        </div>
      </Section>

      {/* ── CURRENT MONTH RUN-RATE PROJECTION ──────────────────────────────── */}
      <Section title={`March Performance — Day ${TODAY_DAY} of ${DAYS_IN_MO}`} controls={[<Select key="1" label="Select Brands" />]}>

        {/* Top: MTD vs Projection split */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr", gap: 0, marginBottom: 16 }}>

          {/* MTD actual */}
          <div style={{ paddingRight: 20 }}>
            <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              Month-to-Date Actual
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: DARK }}>{fmt(MTD_REV)}</div>
              <div style={{ fontSize: 12, color: GRAY }}>/ {MTD_CASES} cases</div>
            </div>
            <div style={{ fontSize: 11, color: GRAY, marginBottom: 10 }}>
              Daily avg: <strong style={{ color: DARK }}>{fmt(dailyRate)}</strong> · {daysElapsedPct.toFixed(0)}% of month elapsed
            </div>

            {/* Progress bar: days elapsed vs. revenue attainment */}
            <div style={{ marginBottom: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: GRAY, marginBottom: 3 }}>
                <span>MTD vs. monthly target</span>
                <span style={{ fontWeight: 700, color: mtdVsTargetPct >= daysElapsedPct ? GREEN : AMBER }}>
                  {mtdVsTargetPct.toFixed(0)}% of ${(MONTHLY_TGT/1000).toFixed(0)}K target
                </span>
              </div>
              {/* Track bar showing days elapsed + revenue band */}
              <div style={{ position: "relative", height: 10, background: CARD_BG, borderRadius: 99, overflow: "hidden", border: `1px solid ${BORDER}` }}>
                {/* Revenue attainment */}
                <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(mtdVsTargetPct, 100)}%`, background: mtdVsTargetPct >= daysElapsedPct ? TEAL : AMBER, borderRadius: 99, transition: "width 0.4s" }} />
                {/* Day marker */}
                <div style={{ position: "absolute", left: `${daysElapsedPct}%`, top: 0, height: "100%", width: 2, background: DARK, opacity: 0.4 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: GRAY_MID, marginTop: 3 }}>
                <span>Mar 1</span>
                <span style={{ color: DARK, fontWeight: 600 }}>Today (Mar 8)</span>
                <span>Mar 31</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ background: BORDER, width: 1, margin: "0 20px" }} />

          {/* Projected month-end */}
          <div style={{ paddingLeft: 4 }}>
            <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
              Projected Month-End
              <span style={{ fontSize: 9, background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", borderRadius: 99, padding: "1px 6px", fontWeight: 600 }}>at current pace</span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: projVsTarget >= 0 ? GREEN : AMBER }}>{fmt(projectedMonth)}</div>
            </div>

            {/* vs. Target and vs. LY */}
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, background: projVsTarget >= 0 ? GREEN_BG : AMBER_BG, border: `1px solid ${projVsTarget >= 0 ? "#86EFAC" : "#FCD34D"}`, borderRadius: 6, padding: "7px 10px" }}>
                <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>vs. ${(MONTHLY_TGT/1000).toFixed(0)}K Target</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: projVsTarget >= 0 ? GREEN : AMBER }}>
                  {projVsTarget >= 0 ? "▲" : "▼"} {Math.abs(projVsTarget).toFixed(0)}%
                </div>
                <div style={{ fontSize: 9, color: GRAY, marginTop: 1 }}>{projVsTarget >= 0 ? `+${fmt(projectedMonth - MONTHLY_TGT)} above` : `${fmt(MONTHLY_TGT - projectedMonth)} short`}</div>
              </div>
              <div style={{ flex: 1, background: projVsLY >= 0 ? GREEN_BG : RED_BG, border: `1px solid ${projVsLY >= 0 ? "#86EFAC" : "#FCA5A5"}`, borderRadius: 6, padding: "7px 10px" }}>
                <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>vs. LY March</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: projVsLY >= 0 ? GREEN : RED }}>
                  {projVsLY >= 0 ? "▲" : "▼"} {Math.abs(projVsLY).toFixed(0)}%
                </div>
                <div style={{ fontSize: 9, color: GRAY, marginTop: 1 }}>LY actual: {fmt(LY_MAR)}</div>
              </div>
            </div>

            <div style={{ fontSize: 11, color: GRAY, padding: "7px 10px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6, lineHeight: 1.5 }}>
              💡 If pace holds, March will be <strong style={{ color: "#1D4ED8" }}>your best month of 2026</strong> — 2× the Jan/Feb average.
            </div>
          </div>
        </div>

        {/* Monthly KPIs row (compact) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, borderTop: `1px solid ${BORDER}`, paddingTop: 14 }}>
          {[
            { label: "MTD Revenue",       value: fmt(MTD_REV),    sub: `Day ${TODAY_DAY} of ${DAYS_IN_MO}` },
            { label: "Projected Full Month",value: fmt(projectedMonth), sub: "At current daily avg", hi: true },
            { label: "LY Full Month",     value: fmt(LY_MAR),     sub: "March 2025 final" },
            { label: "Monthly Target",    value: fmt(MONTHLY_TGT),sub: "Brand-set goal" },
          ].map((c, i) => (
            <div key={i} style={{ background: c.hi ? "#F0FDF4" : CARD_BG, border: `1px solid ${c.hi ? "#86EFAC" : BORDER}`, borderRadius: 7, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.hi ? GREEN : DARK }}>{c.value}</div>
              <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{c.sub}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── OPEN PO PIPELINE ───────────────────────────────────────────────── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "13px 18px 11px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}`, cursor: "pointer" }}
          onClick={() => setPoExpanded(!poExpanded)}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>Open PO Pipeline</div>
            <span style={{ fontSize: 9, fontWeight: 700, background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", borderRadius: 99, padding: "2px 8px", letterSpacing: "0.05em" }}>MONDAY.COM</span>
            {riskPOs.length > 0 && (
              <span style={{ fontSize: 9, fontWeight: 700, background: RED_BG, color: RED, border: "1px solid #FCA5A5", borderRadius: 99, padding: "2px 8px" }}>
                {riskPOs.length} AT RISK
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 11, color: GRAY }}>
              <strong style={{ color: DARK }}>{openPOs.length} POs</strong> · <strong style={{ color: TEAL }}>{fmt(totalPOValue)}</strong> not yet invoiced · <strong style={{ color: DARK }}>{totalPOCases} cases</strong>
            </div>
            <span style={{ color: GRAY, fontSize: 13 }}>{poExpanded ? "▲" : "▼"}</span>
          </div>
        </div>

        {/* Pipeline summary strip — always visible */}
        <div style={{ padding: "12px 18px", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, borderBottom: poExpanded ? `1px solid ${BORDER}` : "none", background: "#FAFBFC" }}>
          {[
            { label: "Confirmed Pipeline",       value: fmt(totalPOValue),                         sub: `${totalPOCases} cases pending shipment`,      color: TEAL,         icon: "📦" },
            { label: "YTD + Pipeline (projected)",value: fmt(projectedYTD),                        sub: `${projectedYTDPct.toFixed(0)}% of annual goal`,color: GREEN,        icon: "📈" },
            { label: "Nearest Shipment",          value: "Mar 12",                                 sub: "Empire State — 72 cases",                     color: "#2563EB",    icon: "🚚" },
            { label: "At-Risk POs",               value: `${riskPOs.length} PO${riskPOs.length !== 1 ? "s" : ""}`, sub: riskPOs.length ? "Sunshine Dist. — review required" : "All clear", color: riskPOs.length ? RED : GREEN, icon: riskPOs.length ? "⚠️" : "✅" },
          ].map((c, i) => (
            <div key={i} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "10px 12px", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{c.icon}</span>
              <div>
                <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{c.label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: c.color }}>{c.value}</div>
                <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{c.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Expanded PO table */}
        {poExpanded && (
          <div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <TH>PO #</TH><TH>Distributor</TH><TH w="30px">ST</TH>
                  <TH>Brand</TH><TH>SKU</TH>
                  <TH right>Cases</TH><TH right>Value</TH>
                  <TH>Status</TH><TH>Est. Ship</TH><TH>Risk</TH>
                </tr>
              </thead>
              <tbody>
                {openPOs.map((po, i) => {
                  const sc = PO_STATUS_CFG[po.status];
                  return (
                    <tr key={i} style={{ background: po.risk ? RED_BG : i%2===0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                      <td style={{ padding: "7px 10px", fontFamily: "monospace", fontSize: 11, color: GRAY }}>{po.id}</td>
                      <td style={{ padding: "7px 10px", fontWeight: 500 }}>{po.dist}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11 }}>
                        <span style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 3, padding: "1px 5px", fontWeight: 700, fontSize: 10 }}>{po.state}</span>
                      </td>
                      <td style={{ padding: "7px 10px", color: GRAY }}>{po.brand}</td>
                      <td style={{ padding: "7px 10px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={po.sku}>{po.sku}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{po.cases}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(po.value)}</td>
                      <td style={{ padding: "7px 10px" }}>
                        <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700 }}>{po.status}</span>
                      </td>
                      <td style={{ padding: "7px 10px", fontSize: 11 }}>
                        <span style={{ color: DARK, fontWeight: 500 }}>{po.estShip}</span>
                        <span style={{ color: GRAY_MID, marginLeft: 5 }}>in {po.daysOut}d</span>
                      </td>
                      <td style={{ padding: "7px 10px" }}>
                        {po.risk
                          ? <span style={{ color: RED, fontSize: 10, fontWeight: 500 }}>⚠ {po.risk}</span>
                          : <span style={{ color: GREEN, fontSize: 11 }}>✓</span>
                        }
                      </td>
                    </tr>
                  );
                })}
                {/* Total row */}
                <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                  <td colSpan={5} style={{ padding: "8px 10px", fontWeight: 700 }}>Total Pipeline</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{totalPOCases}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: TEAL }}>{fmt(totalPOValue)}</td>
                  <td colSpan={3} style={{ padding: "8px 10px", fontSize: 11, color: GRAY }}>
                    When invoiced, YTD moves to <strong style={{ color: TEAL }}>{fmt(projectedYTD)}</strong> — {projectedYTDPct.toFixed(0)}% of annual goal
                  </td>
                </tr>
              </tbody>
            </table>

            {/* What-if callout */}
            <div style={{ margin: "0 18px 14px", padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderLeft: "4px solid #2563EB", borderRadius: 8, fontSize: 11, color: DARK, lineHeight: 1.6 }}>
              <strong style={{ color: "#1D4ED8" }}>💡 Pipeline impact on monthly pacing:</strong> If all {openPOs.length} open POs ship and invoice in March, MTD jumps from <strong>{fmt(MTD_REV)}</strong> to <strong style={{ color: GREEN }}>{fmt(MTD_REV + totalPOValue)}</strong> — pushing March to <strong>{(((MTD_REV + totalPOValue) / MONTHLY_TGT) * 100).toFixed(0)}%</strong> of monthly target.
              {" "}<strong style={{ color: RED }}>Exclude Sunshine Dist. ({fmt(riskPOs.reduce((s,p)=>s+p.value,0))}) until AR situation resolves.</strong>
            </div>
          </div>
        )}
      </div>

      {/* ── Monthly chart ──────────────────────────────────────────────────── */}
      <Section title="Monthly Revenue — Actual vs. Target vs. Prior Year"
        controls={[<Select key="1" label="All Brands" />, <Select key="2" label="Value" w={90} />, <Select key="3" label="2026" w={80} />]}>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={monthlyData} barCategoryGap="22%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: GRAY }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: GRAY }} tickFormatter={fmtK} axisLine={false} tickLine={false} width={44} />
            <Tooltip formatter={(v, n) => [v > 0 ? fmt(v) : "—", n]} contentStyle={{ fontSize: 11 }} />
            <Bar dataKey="ly" name="2025" fill="#CBD5E1" radius={[3,3,0,0]} />
            <Bar dataKey="target" name="Target" fill={`${TEAL}44`} radius={[3,3,0,0]} />
            <Bar dataKey="actual" name="2026" fill={TEAL} radius={[3,3,0,0]} />
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 14, marginTop: 8, justifyContent: "center" }}>
          {[["2026 Actual", TEAL], ["Monthly Target", `${TEAL}66`], ["2025 Actual", "#CBD5E1"]].map(([l, c]) => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: GRAY }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />{l}
            </div>
          ))}
        </div>
      </Section>

      {/* ── Sales Per Year table ────────────────────────────────────────────── */}
      <Section title="Sales Per Year" noPad controls={[<Select key="1" label="Select Brands" />, <Select key="2" label="State/Market" />, <Select key="3" label="Value" w={90} />, <Select key="4" label="2026" w={80} />]}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <TH>Brands</TH>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(m => <TH key={m} right>{m}</TH>)}
                <TH right>Total</TH><TH right>vs LY</TH><TH right>CB Rate</TH>
              </tr>
            </thead>
            <tbody>
              {brandRows.map((b, i) => (
                <tr key={i} style={{ background: i%2===0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: "7px 10px", fontWeight: 500 }}>{b.brand}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtK(Math.round(b.amount*.24))}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtK(Math.round(b.amount*.29))}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtK(Math.round(b.amount*.47))}</td>
                  {Array(9).fill(0).map((_,j) => <td key={j} style={{ padding: "7px 10px", textAlign: "right", color: GRAY_MID }}>$0</td>)}
                  <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(b.amount)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}><YagoCell val={b.yago} /></td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>
                    <span style={{ background: b.cbRate > 10 ? RED_BG : b.cbRate > 7 ? AMBER_BG : GREEN_BG, color: b.cbRate > 10 ? RED : b.cbRate > 7 ? AMBER : GREEN, fontWeight: 700, fontSize: 11, padding: "2px 6px", borderRadius: 4 }}>
                      {b.cbRate.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
              <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                <td style={{ padding: "8px 10px", fontWeight: 700 }}>Total</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>$46K</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>$40K</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>$51K</td>
                {Array(9).fill(0).map((_,j) => <td key={j} style={{ padding: "8px 10px", textAlign: "right", color: GRAY_MID, fontWeight: 700 }}>$0</td>)}
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{fmt(137088)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: RED, fontWeight: 700 }}>▼ 54.6%</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  <span style={{ background: AMBER_BG, color: AMBER, fontWeight: 700, fontSize: 11, padding: "2px 6px", borderRadius: 4 }}>8.6%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── Quick links ─────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        {[
          { page: "brand",       icon: "📊", label: "Brand Performance", sub: "Net revenue, CB rates, tier analysis",       color: TEAL },
          { page: "sku",         icon: "🏷", label: "SKU Performance",   sub: "DoH alerts, velocity tiers, fragmentation",  color: AMBER },
          { page: "distributor", icon: "⚡", label: "Distributor Health", sub: "Sunshine Dist. AR overdue — action needed", color: RED },
        ].map(l => (
          <div key={l.page} onClick={() => navigate(l.page)}
            style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 0 2px ${l.color}44`}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
            <div style={{ fontSize: 24 }}>{l.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: l.color }}>{l.label} →</div>
              <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>{l.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE: BRAND PERFORMANCE
// ═════════════════════════════════════════════════════════════════════════════
function BrandPerformance({ navigate }) {
  const [chartYear, setChartYear]   = useState("2025");
  const [tableYear, setTableYear]   = useState("2026");

  const total     = brandRows.reduce((s,b)=>s+b.amount,0);
  const totalCB   = brandRows.reduce((s,b)=>s+b.cb,0);
  const totalNet  = total - totalCB;
  const hhi       = Math.round(brandRows.reduce((s,b)=>s+Math.pow((b.amount/total)*100,2),0));

  // Fixed YAGO: compare YTD 2026 (Jan–Mar) to same period 2025
  const ytdYoY    = ((total - TOTAL_YTD_2025_SAME) / TOTAL_YTD_2025_SAME) * 100;

  // Chart data based on selection
  const stackData = chartYear === "2026" ? stackedBar2026 : stackedBar2025;
  const stackTitle= chartYear === "2026" ? "2026 YTD (Jan–Mar) by Brand" : "2025 Monthly Revenue by Brand";

  // Multi-year trend line data (brand totals by year)
  const trendData = YEAR_TOTALS.map(y => ({
    year: y.year,
    "Avuá Cachaça":           y.avua,
    "Paladar Tequila":        y.paladar,
    "Svöl Aquavit":           y.svol,
    "Choco Smooth":           y.choco,
    "Drifter Craft Cocktails":y.drifter,
    "Gajardo":                y.gajardo,
    "Total":                  y.total,
  }));

  // Per-year table data
  const tableData = tableYear === "2026" ? DATA_2026 : DATA_2025;
  const tableMonths = tableYear === "2026" ? MONTHS.slice(0,3) : MONTHS;
  const tableTotals = BRANDS_ORDER.map(b => ({
    brand: b,
    months: tableData[b],
    total: tableData[b].reduce((s,v)=>s+v,0),
    ytdPrior: tableYear==="2026" ? YTD_2025_SAME[b] : null,
  }));
  const tableGrandTotal = tableTotals.reduce((s,r)=>s+r.total,0);

  return (
    <div>
      {/* ── Alerts ─────────────────────────────────────────────────────────── */}
      <Alert type="critical">
        <strong>YAGO Fix:</strong> 2026 YTD vs same period 2025 = <strong>{ytdYoY.toFixed(1)}%</strong> ({fmt(total)} vs {fmt(TOTAL_YTD_2025_SAME)}). Prior display showed -92.3% which incorrectly compared YTD to full-year 2025.
      </Alert>
      <Alert type="warning">
        <strong>Paladar CB Rate 15.9%</strong> — 28% revenue bleed on gross. Avuá concentration at 59.97% (target &lt;35%, HHI {hhi}).
        <span onClick={() => navigate("sku")} style={{ color: AMBER, fontWeight: 700, cursor: "pointer", marginLeft: 8, textDecoration: "underline" }}>View Paladar SKUs →</span>
      </Alert>

      {/* ── Top KPI strip ──────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 14 }}>
        {[
          { label: "2026 YTD Gross",          value: fmt(total),          sub: "Jan–Mar 2026",        color: DARK },
          { label: "YTD vs Same Period LY",    value: `${ytdYoY.toFixed(1)}%`, sub: `Prior: ${fmt(TOTAL_YTD_2025_SAME)}`, color: ytdYoY>=0?GREEN:RED, warn: ytdYoY<0 },
          { label: "Total Chargebacks YTD",    value: fmt(totalCB),        sub: `${((totalCB/total)*100).toFixed(1)}% CB rate`, warn: true },
          { label: "Net Revenue YTD",          value: fmt(totalNet),       sub: "After chargebacks",  hi: true },
          { label: "2025 Full Year",           value: "$1,761,123",        sub: "+8.4% vs 2024",      color: DARK },
        ].map((c,i) => (
          <div key={i} style={{ background: c.hi?"#F0FDF4":c.warn?RED_BG:CARD_BG, border: `1px solid ${c.hi?"#86EFAC":c.warn?"#FCA5A5":BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.hi?GREEN:c.warn?RED:c.color||DARK }}>{c.value}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Multi-year portfolio trajectory ────────────────────────────────── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>Portfolio Revenue — 2022–2026</div>
          <div style={{ display: "flex", gap: 6, fontSize: 11, color: GRAY }}>
            {YEAR_TOTALS.map(y => (
              <div key={y.year} style={{ textAlign: "center", padding: "3px 8px", background: CARD_BG, borderRadius: 4 }}>
                <div style={{ fontWeight: 700, color: DARK }}>{y.year.includes("*")?"$137K":"$"+(y.total/1000000).toFixed(2)+"M"}</div>
                <div style={{ fontSize: 9, color: GRAY_MID }}>{y.year}</div>
              </div>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={trendData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: GRAY }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: GRAY }} tickFormatter={v => "$"+(v/1000000).toFixed(1)+"M"} axisLine={false} tickLine={false} width={50} />
            <Tooltip formatter={(v,n) => [fmt(v), n]} contentStyle={{ fontSize: 11 }} />
            {BRANDS_ORDER.map(b => <Bar key={b} dataKey={b} stackId="a" fill={BRAND_COLORS[b]} />)}
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 14, marginTop: 8, justifyContent: "center", flexWrap: "wrap" }}>
          {BRANDS_ORDER.map(b => (
            <div key={b} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: GRAY }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: BRAND_COLORS[b] }} />{b}
            </div>
          ))}
        </div>
      </div>

      {/* ── Monthly stacked bar chart ───────────────────────────────────────── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>{stackTitle}</div>
          <div style={{ display: "flex", gap: 6 }}>
            {["2026","2025"].map(y => (
              <button key={y} onClick={() => setChartYear(y)}
                style={{ padding: "5px 12px", border: `1px solid ${BORDER}`, borderRadius: 5, background: chartYear===y?TEAL:WHITE, color: chartYear===y?WHITE:DARK, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {y}{y==="2026"?" YTD":""}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={stackData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: GRAY }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: GRAY }} tickFormatter={v => "$"+(v/1000).toFixed(0)+"K"} axisLine={false} tickLine={false} width={44} />
            <Tooltip formatter={(v,n) => [fmt(v), n]} contentStyle={{ fontSize: 11 }} />
            {BRANDS_ORDER.map(b => <Bar key={b} dataKey={b} stackId="a" fill={BRAND_COLORS[b]} radius={b===BRANDS_ORDER[BRANDS_ORDER.length-1]?[3,3,0,0]:[0,0,0,0]} />)}
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 14, marginTop: 8, justifyContent: "center", flexWrap: "wrap" }}>
          {BRANDS_ORDER.map(b => (
            <div key={b} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: GRAY }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: BRAND_COLORS[b] }} />{b}
            </div>
          ))}
        </div>
      </div>

      {/* ── Brand health cards + Net Revenue table ─────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
        {brandRows.map((b, i) => {
          const net = b.amount - b.cb;
          const ytdPrior = YTD_2025_SAME[b.brand] || 0;
          const brandYoY = ytdPrior > 0 ? ((b.amount - ytdPrior)/ytdPrior)*100 : 0;
          return (
            <div key={i} style={{ background: WHITE, border: `2px solid ${BORDER}`, borderRadius: 10, padding: "12px 14px", borderTop: `3px solid ${BRAND_COLORS[b.brand]}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: DARK, lineHeight: 1.3, flex: 1 }}>{b.brand}</div>
                <TierBadge tier={b.tier} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 10px" }}>
                {[
                  { l: "YTD Gross",    v: fmt(b.amount) },
                  { l: "vs LY (YTD)", v: `${brandYoY>=0?"▲":"▼"} ${Math.abs(brandYoY).toFixed(1)}%`, c: brandYoY>=0?GREEN:RED },
                  { l: "Net Revenue",  v: fmt(net), c: GREEN },
                  { l: "CB Rate",      v: `${b.cbRate.toFixed(1)}%`, c: b.cbRate>10?RED:b.cbRate>7?AMBER:GREEN },
                ].map((k,j) => (
                  <div key={j}>
                    <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{k.l}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: k.c||DARK }}>{k.v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ flex: 1, background: BORDER, borderRadius: 99, height: 4, overflow: "hidden" }}>
                  <div style={{ width: `${b.pct}%`, height: "100%", background: BRAND_COLORS[b.brand], borderRadius: 99 }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: GRAY, whiteSpace: "nowrap" }}>{b.pct.toFixed(1)}% of biz</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Year-by-year table ──────────────────────────────────────────────── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "12px 18px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>Sales Per Brand by Month</div>
          <div style={{ display: "flex", gap: 6 }}>
            {["2026","2025"].map(y => (
              <button key={y} onClick={() => setTableYear(y)}
                style={{ padding: "5px 12px", border: `1px solid ${BORDER}`, borderRadius: 5, background: tableYear===y?TEAL:WHITE, color: tableYear===y?WHITE:DARK, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {y}{y==="2026"?" YTD":""}
              </button>
            ))}
            <Btn>↓ Download</Btn>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                <TH w={140}>{tableYear} Sales</TH>
                {tableMonths.map(m => <TH key={m} right>{m}</TH>)}
                <TH right>Total</TH>
                {tableYear==="2026" && <TH right>vs LY (YTD)</TH>}
              </tr>
            </thead>
            <tbody>
              {tableTotals.map((r, i) => {
                const brandYoY = r.ytdPrior && r.ytdPrior > 0 ? ((r.total - r.ytdPrior)/r.ytdPrior)*100 : null;
                return (
                  <tr key={i} style={{ background: i%2===0?WHITE:CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: "6px 10px", fontWeight: 600, borderLeft: `3px solid ${BRAND_COLORS[r.brand]}` }}>{r.brand}</td>
                    {tableMonths.map((m,j) => (
                      <td key={m} style={{ padding: "6px 8px", textAlign: "right", color: r.months[j]>0?DARK:GRAY_MID, fontSize: 11 }}>
                        {r.months[j] > 0 ? "$"+(r.months[j]/1000).toFixed(1)+"K" : "—"}
                      </td>
                    ))}
                    <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{fmt(r.total)}</td>
                    {tableYear==="2026" && (
                      <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: brandYoY>=0?GREEN:RED }}>
                        {brandYoY!==null ? `${brandYoY>=0?"▲":"▼"} ${Math.abs(brandYoY).toFixed(1)}%` : <span style={{color:GRAY_MID}}>—</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
              {/* Total row */}
              <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                <td style={{ padding: "7px 10px", fontWeight: 700 }}>Total</td>
                {tableMonths.map((m,j) => {
                  const v = BRANDS_ORDER.reduce((s,b)=>s+tableData[b][j],0);
                  return <td key={m} style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700 }}>{v>0?"$"+(v/1000).toFixed(1)+"K":"—"}</td>;
                })}
                <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: TEAL }}>{fmt(tableGrandTotal)}</td>
                {tableYear==="2026" && (
                  <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: ytdYoY>=0?GREEN:RED }}>
                    {ytdYoY>=0?"▲":"▼"} {Math.abs(ytdYoY).toFixed(1)}%
                  </td>
                )}
              </tr>
              {/* YAGO row — correctly labeled */}
              <tr style={{ background: TEAL, color: WHITE }}>
                <td style={{ padding: "7px 10px", fontWeight: 700, color: WHITE, fontSize: 10 }}>
                  {tableYear==="2026" ? "YAGO vs same period 2025 ✓" : "YAGO vs 2024"}
                </td>
                {tableMonths.map((m,j) => {
                  const curr = BRANDS_ORDER.reduce((s,b)=>s+tableData[b][j],0);
                  const prior = tableYear==="2026"
                    ? BRANDS_ORDER.reduce((s,b)=>s+DATA_2025[b][j],0)
                    : 0; // 2025 vs 2024 not fully loaded
                  const pct = prior > 0 ? ((curr-prior)/prior)*100 : null;
                  return (
                    <td key={m} style={{ padding: "7px 8px", textAlign: "right", color: pct===null?"rgba(255,255,255,0.4)":pct>=0?"#86EFAC":"#FCA5A5", fontWeight: 700, fontSize: 11 }}>
                      {pct===null ? "%" : `${pct>=0?"+":""}${pct.toFixed(0)}%`}
                    </td>
                  );
                })}
                <td style={{ padding: "7px 10px", textAlign: "right", color: ytdYoY>=0?"#86EFAC":"#FCA5A5", fontWeight: 700 }}>
                  {tableYear==="2026" ? `${ytdYoY>=0?"+":""}${ytdYoY.toFixed(1)}%` : "+8.4%"}
                </td>
                {tableYear==="2026" && <td />}
              </tr>
            </tbody>
          </table>
        </div>
        {tableYear==="2026" && (
          <div style={{ padding: "10px 18px", background: "#F0FDF4", borderTop: `1px solid #86EFAC`, fontSize: 11, color: GREEN, fontWeight: 600 }}>
            ✓ YAGO fix applied — comparing Jan–Mar 2026 ({fmt(total)}) to Jan–Mar 2025 ({fmt(TOTAL_YTD_2025_SAME)}). The app's current display compares to full-year 2025 ($1.76M) showing -92.3%, which is incorrect.
          </div>
        )}
      </div>

      {/* ── VS a year ago summary ───────────────────────────────────────────── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "12px 18px 10px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>VS a Year Ago — Monthly % Change (2026 vs 2025)</div>
          <span style={{ fontSize: 10, background: GREEN_BG, color: GREEN, border: "1px solid #86EFAC", borderRadius: 4, padding: "2px 8px", fontWeight: 700 }}>✓ Correct YAGO Calculation</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                <TH>Brand</TH>
                {vsYago.map(r => <TH key={r.m} right>{r.m}</TH>)}
                <TH right>YTD Total</TH>
              </tr>
            </thead>
            <tbody>
              {BRANDS_ORDER.map((b, i) => {
                const ytdBrandYoY = YTD_2025_SAME[b] > 0
                  ? ((brandRows.find(r=>r.brand===b)?.amount || 0) - YTD_2025_SAME[b]) / YTD_2025_SAME[b] * 100
                  : null;
                return (
                  <tr key={i} style={{ background: i%2===0?WHITE:CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: "7px 10px", fontWeight: 600, borderLeft: `3px solid ${BRAND_COLORS[b]}` }}>{b}</td>
                    {vsYago.map(r => {
                      const v = r[b];
                      return (
                        <td key={r.m} style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600, color: v===0?GRAY_MID:v>0?GREEN:RED }}>
                          {v===0 ? "—" : `${v>0?"+":""}${v}%`}
                        </td>
                      );
                    })}
                    <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: ytdBrandYoY===null?GRAY_MID:ytdBrandYoY>=0?GREEN:RED }}>
                      {ytdBrandYoY===null ? "—" : `${ytdBrandYoY>=0?"+":""}${ytdBrandYoY.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                <td style={{ padding: "7px 10px", fontWeight: 700 }}>Total</td>
                {vsYago.map(r => (
                  <td key={r.m} style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: r.Total>=0?GREEN:RED }}>
                    {r.Total===0 ? "—" : `${r.Total>0?"+":""}${r.Total}%`}
                  </td>
                ))}
                <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: ytdYoY>=0?GREEN:RED }}>
                  {ytdYoY>=0?"+":""}{ytdYoY.toFixed(1)}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Quick links ─────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        {[
          { page: "topline",     icon: "📈", label: "Sales Topline",    sub: "Run-rate, pacing, open PO pipeline", color: TEAL  },
          { page: "sku",         icon: "🏷", label: "SKU Performance",  sub: "DoH, velocity tiers, Paladar fragmentation", color: AMBER },
          { page: "distributor", icon: "⚡", label: "Distributor Health",sub: "CB rate trends, AR aging per dist.",   color: RED   },
        ].map(l => (
          <div key={l.page} onClick={() => navigate(l.page)}
            style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 0 2px ${l.color}44`}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
            <div style={{ fontSize: 22 }}>{l.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: l.color }}>{l.label} →</div>
              <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>{l.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE: SKU PERFORMANCE
// ═════════════════════════════════════════════════════════════════════════════

function Sparkline({ values, w = 56, h = 24 }) {
  const pts = values.filter(v => v > 0);
  if (pts.length < 2) return <span style={{ color: GRAY_MID, fontSize: 10 }}>—</span>;
  const min = Math.min(...pts), max = Math.max(...pts), range = max - min || 1;
  const xs = pts.map((_, i) => (i / (pts.length - 1)) * w);
  const ys = pts.map(v => h - ((v - min) / range) * (h - 4) - 2);
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const trend = pts[pts.length-1] > pts[0] * 1.12 ? GREEN : pts[pts.length-1] < pts[0] * 0.88 ? RED : AMBER;
  return (
    <svg width={w} height={h} style={{ display: "block", overflow: "visible" }}>
      <path d={path} fill="none" stroke={trend} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xs[xs.length-1].toFixed(1)} cy={ys[ys.length-1].toFixed(1)} r={2.5} fill={trend} />
    </svg>
  );
}

function SkuDetailPanel({ sku, onClose }) {
  const [year, setYear] = useState("both");
  if (!sku) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 4 }}>SKU % Share</div>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={skuRows} dataKey="amount" cx="50%" cy="50%" innerRadius={44} outerRadius={76} paddingAngle={1}>
              {skuRows.map((_, i) => <Cell key={i} fill={[TEAL,TEAL2,"#2DD4BF","#5EEAD4","#0F766E","#0E7490","#0369A1","#1D4ED8","#4F46E5","#7C3AED","#A855F7","#EC4899"][i%12]} />)}
            </Pie>
            <Tooltip formatter={(v) => [fmt(v)]} contentStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ textAlign: "center", fontSize: 10, color: GRAY, marginTop: -4 }}>Click any row to see monthly trend</div>
      </div>
      {[
        { title: "Velocity Tier Guide", items: [
          { badge: <TierBadge tier="HERO" />,  desc: "Top rev, positive trend — Invest" },
          { badge: <TierBadge tier="CORE" />,  desc: "Steady performer — Maintain" },
          { badge: <TierBadge tier="WATCH" />, desc: "Declining 2+ months — Diagnose" },
          { badge: <TierBadge tier="EXIT" />,  desc: "<10 cases or 90d no orders — Review" },
        ]},
        { title: "Trend Badge Guide", items: Object.entries(TREND_CFG).map(([t,c]) => ({
          badge: <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700 }}>{c.icon} {t}</span>,
          desc: t==="ACCELERATING"?"Last month >12% above Jan": t==="DECELERATING"?"Last month >12% below Jan":"Within ±12% of opening month",
        }))},
        { title: "Days on Hand (DoH)", items: [
          { badge: <span style={{ background: RED_BG,   color: RED,   fontWeight: 700, fontSize: 10, padding: "2px 7px", borderRadius: 4 }}>{"< 45d"}</span>,  desc: "Reorder now ⚠" },
          { badge: <span style={{ background: AMBER_BG, color: AMBER, fontWeight: 700, fontSize: 10, padding: "2px 7px", borderRadius: 4 }}>45–90d</span>, desc: "Monitor closely" },
          { badge: <span style={{ background: GREEN_BG, color: GREEN, fontWeight: 700, fontSize: 10, padding: "2px 7px", borderRadius: 4 }}>{"> 90d"}</span>,  desc: "Healthy level" },
        ]},
      ].map(({ title, items }) => (
        <div key={title} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "13px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 9 }}>{title}</div>
          {items.map(({ badge, desc }, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: i < items.length-1 ? 7 : 0 }}>
              {badge}<span style={{ fontSize: 11, color: GRAY, lineHeight: 1.4 }}>{desc}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const r = skuRows.find(s => s.sku === sku);
  const md = SKU_MONTHLY[sku];
  if (!r || !md) return null;
  const trend = skuTrend(sku);
  const tc = TREND_CFG[trend];
  const ytdTotal   = md.m2026.reduce((s,v)=>s+v,0);
  const lyTotal    = md.m2025.reduce((s,v)=>s+v,0);
  const ytdMos     = md.m2026.filter(v=>v>0).length;
  const lyYTDSame  = md.m2025.slice(0,ytdMos).reduce((s,v)=>s+v,0);
  const yoy        = lyYTDSame > 0 ? ((ytdTotal - lyYTDSame) / lyYTDSame) * 100 : null;
  const chartData  = MONTHS_SHORT.map((m, i) => ({ m, "2026": md.m2026[i]||null, "2025": md.m2025[i]||0 }));

  return (
    <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: TEAL, padding: "11px 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.6)", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 1 }}>{r.brand}</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: WHITE, lineHeight: 1.3, maxWidth: 230 }}>{r.sku}</div>
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          <span style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, borderRadius: 4, padding: "3px 7px", fontSize: 9.5, fontWeight: 700 }}>{tc.icon} {trend}</span>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.18)", border: "none", borderRadius: 4, color: WHITE, cursor: "pointer", fontSize: 14, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderBottom: `1px solid ${BORDER}` }}>
        {[
          { l: "YTD Rev",    v: fmt(ytdTotal),                    hi: false,     warn: false    },
          { l: "YTD Cases",  v: `${r.cases} cs`,                  hi: false,     warn: false    },
          { l: "vs LY YTD", v: yoy != null ? `${yoy.toFixed(1)}%`:"—", hi: yoy>0, warn: yoy<0 },
          { l: "DoH",        v: `${r.doh}d`,                      hi: r.doh>90,  warn: r.doh<45 },
        ].map((k, i) => (
          <div key={i} style={{ padding: "9px 11px", borderRight: i<3?`1px solid ${BORDER}`:"none", background: k.hi?GREEN_BG:k.warn?RED_BG:WHITE }}>
            <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 1 }}>{k.l}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: k.hi?GREEN:k.warn?RED:DARK }}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Year toggle */}
      <div style={{ padding: "8px 12px", display: "flex", gap: 5, borderBottom: `1px solid ${BORDER}`, background: "#FAFBFC" }}>
        {[["both","Both Years"],["2026","2026 Only"],["2025","2025 Only"]].map(([v,l]) => (
          <button key={v} onClick={() => setYear(v)}
            style={{ padding: "3px 9px", border: `1px solid ${year===v?TEAL:BORDER}`, borderRadius: 4, background: year===v?TEAL:WHITE, color: year===v?WHITE:GRAY, fontSize: 10, fontWeight: year===v?700:400, cursor: "pointer" }}>
            {l}
          </button>
        ))}
      </div>

      {/* Bar chart */}
      <div style={{ padding: "12px 12px 6px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 6 }}>Monthly Revenue — 2026 vs 2025</div>
        <ResponsiveContainer width="100%" height={148}>
          <BarChart data={chartData} barCategoryGap="22%" margin={{ top: 2, right: 2, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: GRAY }} tickFormatter={v => v>=1000?`$${(v/1000).toFixed(0)}K`:`$${v}`} axisLine={false} tickLine={false} width={36} />
            <Tooltip formatter={(v, n) => [v?fmt(v):"—", n]} contentStyle={{ fontSize: 10 }} />
            {(year==="both"||year==="2025") && <Bar dataKey="2025" fill="#CBD5E1" radius={[2,2,0,0]} />}
            {(year==="both"||year==="2026") && (
              <Bar dataKey="2026" radius={[2,2,0,0]}>
                {chartData.map((d,i) => <Cell key={i} fill={d["2026"]>(d["2025"]||0)?"#16A34A":d["2026"]>0?TEAL:"#F3F4F6"} />)}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 10, marginTop: 4, justifyContent: "center" }}>
          {(year==="both"||year==="2025") && <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9.5, color: GRAY }}><div style={{ width: 7, height: 7, borderRadius: 2, background: "#CBD5E1" }} />2025</div>}
          {(year==="both"||year==="2026") && <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9.5, color: GRAY }}><div style={{ width: 7, height: 7, borderRadius: 2, background: TEAL }} />2026</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9.5, color: GRAY }}><div style={{ width: 7, height: 7, borderRadius: 2, background: "#16A34A" }} />Above LY</div>
        </div>
      </div>

      {/* Month table */}
      <div style={{ borderTop: `1px solid ${BORDER}`, maxHeight: 280, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
          <thead style={{ position: "sticky", top: 0 }}>
            <tr style={{ background: TEAL }}>
              <th style={{ padding: "5px 8px", color: WHITE, fontWeight: 700, textAlign: "left" }}>Month</th>
              <th style={{ padding: "5px 8px", color: WHITE, fontWeight: 700, textAlign: "right" }}>2026</th>
              <th style={{ padding: "5px 8px", color: WHITE, fontWeight: 700, textAlign: "right" }}>2025</th>
              <th style={{ padding: "5px 8px", color: WHITE, fontWeight: 700, textAlign: "right" }}>YoY %</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS_SHORT.map((m, i) => {
              const v26 = md.m2026[i], v25 = md.m2025[i];
              const yoyM = v26>0 && v25>0 ? ((v26-v25)/v25)*100 : null;
              const future = !v26;
              return (
                <tr key={m} style={{ background: future?"#FAFBFC":i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}`, opacity: future?0.55:1 }}>
                  <td style={{ padding: "5px 8px", fontWeight: 600, color: future?GRAY_MID:DARK }}>{m}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: future?GRAY_MID:TEAL, fontWeight: v26?600:400 }}>
                    {v26?fmt(v26):<span style={{ fontSize: 9 }}>—</span>}
                  </td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: GRAY }}>{fmt(v25)}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right" }}>
                    {yoyM!==null
                      ? <span style={{ color: yoyM>=0?GREEN:RED, fontWeight: 700 }}>{yoyM>=0?"▲":"▼"} {Math.abs(yoyM).toFixed(0)}%</span>
                      : <span style={{ color: GRAY_MID, fontSize: 9 }}>{future?fmt(v25)+" LY":"—"}</span>}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
              <td style={{ padding: "5px 8px", fontWeight: 700 }}>YTD / FY</td>
              <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: TEAL }}>{fmt(ytdTotal)}</td>
              <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: GRAY }}>{fmt(lyTotal)}</td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>
                {yoy!=null && <span style={{ color: yoy>=0?GREEN:RED, fontWeight: 700 }}>{yoy>=0?"▲":"▼"} {Math.abs(yoy).toFixed(0)}%</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SKUPerformance({ navigate }) {
  const [showAll,     setShowAll]     = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [brandFilter, setBrandFilter] = useState("All");
  const [sortCol,     setSortCol]     = useState("amount");
  const [sortDir,     setSortDir]     = useState("desc");

  const brandsList = ["All", ...Array.from(new Set(skuRows.map(r => r.brand)))];
  const filtered   = skuRows.filter(r => brandFilter === "All" || r.brand === brandFilter);

  const handleSort = col => { if (sortCol===col) setSortDir(d=>d==="desc"?"asc":"desc"); else { setSortCol(col); setSortDir("desc"); } };
  const sortedRows = [...filtered].sort((a, b) => {
    const va = sortCol==="sku"?a.sku : sortCol==="doh"?a.doh : sortCol==="vel"?a.vel : sortCol==="cases"?a.cases : a.amount;
    const vb = sortCol==="sku"?b.sku : sortCol==="doh"?b.doh : sortCol==="vel"?b.vel : sortCol==="cases"?b.cases : b.amount;
    if (typeof va === "string") return sortDir==="desc"?vb.localeCompare(va):va.localeCompare(vb);
    return sortDir==="desc" ? vb-va : va-vb;
  });

  const visible    = showAll ? sortedRows : sortedRows.slice(0, 12);
  const total      = filtered.reduce((s,r)=>s+r.amount, 0);
  const totalCases = filtered.reduce((s,r)=>s+r.cases, 0);
  const critDoh    = filtered.filter(r => r.doh < 45);
  const accel      = filtered.filter(r => skuTrend(r.sku) === "ACCELERATING");
  const decel      = filtered.filter(r => skuTrend(r.sku) === "DECELERATING");

  const SortTH = ({ col, children, right }) => (
    <th onClick={() => handleSort(col)}
      style={{ background: TEAL, color: WHITE, padding: "8px 10px", textAlign: right?"right":"left", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}>
      {children}{sortCol===col ? (sortDir==="desc"?" ↓":" ↑") : ""}
    </th>
  );

  return (
    <div>
      {critDoh.length > 0 && (
        <Alert type="critical">
          <strong>{critDoh.length} SKUs under 45d inventory — reorder immediately:</strong> {critDoh.map(r => r.sku.split(" ").slice(0,3).join(" ")).join(" · ")}
        </Alert>
      )}
      {decel.length > 0 && (
        <Alert type="warning">
          <strong>{decel.length} SKU{decel.length>1?"s":""} DECELERATING</strong> — month-over-month revenue falling: {decel.map(r => r.sku.split(" ").slice(0,3).join(" ")).join(" · ")}
          <span style={{ color: AMBER, fontSize: 10, marginLeft: 8 }}>↓ Click row to inspect monthly trend</span>
        </Alert>
      )}

      {/* Brand filter + summary */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        {brandsList.map(b => (
          <button key={b} onClick={() => { setBrandFilter(b); setSelectedSku(null); }}
            style={{ padding: "5px 12px", border: `1px solid ${brandFilter===b?TEAL:BORDER}`, borderRadius: 6, background: brandFilter===b?TEAL:WHITE, color: brandFilter===b?WHITE:DARK, fontSize: 11, fontWeight: brandFilter===b?700:400, cursor: "pointer" }}>
            {b === "All" ? "All SKUs" : b.split(" ")[0]}
          </button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 14, fontSize: 11, color: GRAY, alignItems: "center" }}>
          <span>📦 <strong style={{color:DARK}}>{filtered.length} SKUs</strong></span>
          <span>💰 <strong style={{color:DARK}}>{fmt(total)}</strong></span>
          {accel.length > 0 && <span style={{color:GREEN}}>↑ <strong>{accel.length}</strong> accel.</span>}
          {decel.length > 0 && <span style={{color:RED}}>↓ <strong>{decel.length}</strong> decel.</span>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 16, alignItems: "start" }}>
        {/* Table */}
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>
              Sales Per SKU{brandFilter !== "All" ? ` — ${brandFilter}` : ""}
            </div>
            <div style={{ display: "flex", gap: 6 }}><Btn>↓ Download</Btn><Btn>↺ Refresh</Btn></div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <TH>Brand</TH>
                  <SortTH col="sku">SKU</SortTH>
                  <SortTH col="amount" right>Revenue</SortTH>
                  <SortTH col="cases"  right>Cases</SortTH>
                  <SortTH col="doh"    right>DoH</SortTH>
                  <SortTH col="vel"    right>Vel/mo</SortTH>
                  <TH right>Trend</TH>
                  <TH w="64px">3-Mo ↗</TH>
                  <TH right>Tier</TH>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => {
                  const trend = skuTrend(r.sku);
                  const tc    = TREND_CFG[trend];
                  const md    = SKU_MONTHLY[r.sku];
                  const spark = md ? md.m2026.filter(v=>v>0) : [];
                  const isSel = r.sku === selectedSku;
                  return (
                    <tr key={i} onClick={() => setSelectedSku(isSel ? null : r.sku)}
                      style={{ background: isSel?`${TEAL}0D`:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}`, cursor: "pointer", outline: isSel?`2px solid ${TEAL}44`:"none", outlineOffset: -1 }}>
                      <td style={{ padding: "7px 10px", color: GRAY, fontSize: 10.5, whiteSpace: "nowrap" }}>{r.brand.split(" ")[0]}</td>
                      <td style={{ padding: "7px 10px", fontWeight: 500, maxWidth: 195, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.sku}>
                        {isSel && <span style={{ color: TEAL, marginRight: 4, fontSize: 10 }}>▶</span>}{r.sku}
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(r.amount)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{r.cases}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}><DohPill val={r.doh} /></td>
                      <td style={{ padding: "7px 10px", textAlign: "right", color: GRAY }}>{r.vel.toFixed(1)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>
                        <span style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, borderRadius: 4, padding: "2px 5px", fontSize: 9, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {tc.icon} {trend==="ACCELERATING"?"ACCEL.":trend==="DECELERATING"?"DECEL.":"STEADY"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 10px" }}><Sparkline values={spark} w={52} h={22} /></td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}><TierBadge tier={r.tier} /></td>
                    </tr>
                  );
                })}
                <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                  <td colSpan={2} style={{ padding: "8px 10px", fontWeight: 700 }}>Total</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{fmt(total)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{totalCases}</td>
                  <td colSpan={5} />
                </tr>
              </tbody>
            </table>
          </div>
          {sortedRows.length > 12 && (
            <div onClick={() => setShowAll(!showAll)}
              style={{ padding: "9px", textAlign: "center", fontSize: 12, color: TEAL, fontWeight: 600, cursor: "pointer", borderTop: `1px solid ${BORDER}`, background: CARD_BG }}>
              {showAll ? "▲ Show Less" : `▼ See More (${sortedRows.length - 12} more)`}
            </div>
          )}
        </div>

        {/* Right panel */}
        <SkuDetailPanel sku={selectedSku} onClose={() => setSelectedSku(null)} />
      </div>
    </div>
  );
}

// ── State page sub-components ─────────────────────────────────────────────
function StateBadge({ status }) {
  const c = STATE_STATUS[status] || STATE_STATUS.INACTIVE;
  return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{c.icon} {c.label}</span>;
}

function StateActionBadge({ action }) {
  const c = STATE_ACTION_CFG[action] || { bg: CARD_BG, text: GRAY };
  return <span style={{ background: c.bg, color: c.text, borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{action}</span>;
}

function YoyTag({ yoy, isNew, size = 11 }) {
  if (isNew) return <span style={{ color: "#1D4ED8", fontWeight: 700, fontSize: size }}>★ NEW</span>;
  if (yoy === null || yoy === undefined) return <span style={{ color: GRAY_MID }}>—</span>;
  const up = yoy >= 0;
  return <span style={{ color: up ? GREEN : RED, fontWeight: 700, fontSize: size }}>{up ? "▲" : "▼"} {Math.abs(yoy).toFixed(1)}%</span>;
}

function TileMap({ selected, onSelect }) {
  const [hov, setHov] = useState(null);
  const W = 12 * T_STEP, H = 10 * T_STEP;
  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      {MAP_TILES.map(([abbr, col, row]) => {
        const x = col * T_STEP, y = row * T_STEP;
        const d   = STATE_MAP[abbr];
        const cfg = d ? STATE_STATUS[d.status] : STATE_STATUS.INACTIVE;
        const sel = abbr === selected, hovd = abbr === hov, active = !!d;
        return (
          <g key={abbr} onClick={() => active && onSelect(sel ? null : abbr)}
            onMouseEnter={() => active && setHov(abbr)}
            onMouseLeave={() => setHov(null)}
            style={{ cursor: active ? "pointer" : "default" }}>
            <rect x={x} y={y} width={T_S} height={T_S} rx={5}
              fill={cfg.tile} opacity={active ? 1 : 0.5}
              stroke={sel ? DARK : hovd ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.2)"}
              strokeWidth={sel ? 2.5 : 1} />
            <text x={x+T_S/2} y={y+T_S/2-(active&&d.rank?3.5:0)}
              textAnchor="middle" dominantBaseline="middle"
              fill={cfg.tileText} fontSize={active?9.5:8.5} fontWeight={700}
              fontFamily="'DM Sans','Segoe UI',sans-serif">{abbr}</text>
            {active && d.rank && (
              <text x={x+T_S/2} y={y+T_S/2+7} textAnchor="middle" dominantBaseline="middle"
                fill={cfg.tileText} fontSize={7} opacity={0.8}
                fontFamily="'DM Sans','Segoe UI',sans-serif">#{d.rank}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function StateDetailPanel({ abbr, onClose }) {
  if (!abbr) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 340, color: GRAY_MID }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>🗺</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: GRAY }}>Click a state to inspect</div>
      <div style={{ fontSize: 11, marginTop: 4 }}>Map or table row</div>
    </div>
  );
  const s = STATE_MAP[abbr];
  const cfg = STATE_STATUS[s.status];
  const brands = [
    { name: "Avuá Cachaça",    pct: 60, cases: Math.round(s.cases*0.60) },
    { name: "Paladar Tequila", pct: 21, cases: Math.round(s.cases*0.21) },
    { name: "Svöl Aquavit",    pct: 15, cases: Math.round(s.cases*0.15) },
    { name: "Other",           pct:  4, cases: Math.round(s.cases*0.04) },
  ];
  const poNote = abbr==="NY" ? "2 POs · $13,104 · Ship Mar 12" : abbr==="CA" ? "1 PO · $5,614 · Ship Mar 15" : abbr==="FL" ? "1 PO · $2,460 · ⚠ AR overdue 38d" : abbr==="TX" ? "2 POs · $6,206 · Ship Mar 18" : null;
  return (
    <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", height: "100%" }}>
      <div style={{ background: cfg.tile, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.6)", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 2 }}>MARKET #{s.rank} · {s.abbr}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: WHITE }}>{s.name}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>{s.dist}</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: 5, padding: "4px 9px", textAlign: "center" }}>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.6)", fontWeight: 700 }}>STATUS</div>
            <div style={{ fontSize: 10, fontWeight: 800, color: WHITE }}>{cfg.icon} {s.status}</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.18)", border: "none", borderRadius: 4, color: WHITE, cursor: "pointer", fontSize: 14, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
      </div>
      <div style={{ padding: "12px 14px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, marginBottom: 10 }}>
          {[
            { l: "Current Revenue", v: fmt(s.value) },
            { l: "Prior Revenue",   v: s.pv > 0 ? fmt(s.pv) : "—" },
            { l: "Current Cases",   v: s.cases },
            { l: "Prior Cases",     v: s.pc > 0 ? s.pc : "—" },
          ].map((k,i) => (
            <div key={i} style={{ background: CARD_BG, borderRadius: 5, padding: "7px 9px" }}>
              <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 1 }}>{k.l}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>{k.v}</div>
            </div>
          ))}
        </div>
        {!s.isNew ? (
          <div style={{ background: s.yoy>=0 ? GREEN_BG : RED_BG, border: `1px solid ${s.yoy>=0 ? "#86EFAC" : "#FCA5A5"}`, borderRadius: 7, padding: "9px 11px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 1 }}>Year-over-Year</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.yoy>=0 ? GREEN : RED }}>{s.yoy>=0?"▲":"▼"} {Math.abs(s.yoy).toFixed(1)}%</div>
              <div style={{ fontSize: 10, color: GRAY, marginTop: 1 }}>{s.chgVal>=0?"+":""}{fmt(s.chgVal)} · Cases: <YoyTag yoy={s.yoyCs} size={10} /></div>
            </div>
            <StateActionBadge action={s.action} />
          </div>
        ) : (
          <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 7, padding: "9px 11px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1D4ED8", marginBottom: 2 }}>★ New Market — First Orders</div>
              <div style={{ fontSize: 10, color: GRAY }}>No prior period. Focus on distributor onboarding and sell-through velocity.</div>
            </div>
            <StateActionBadge action={s.action} />
          </div>
        )}
        <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 7 }}>Brand Mix (estimated)</div>
        {brands.map((b, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: GRAY, marginBottom: 2 }}>
              <span>{b.name}</span><span style={{ fontWeight: 600, color: DARK }}>{b.cases} cs · {b.pct}%</span>
            </div>
            <div style={{ background: BORDER, borderRadius: 99, height: 4, overflow: "hidden" }}>
              <div style={{ width: `${b.pct}%`, height: "100%", background: [TEAL, TEAL2, "#2DD4BF", GRAY_MID][i], borderRadius: 99 }} />
            </div>
          </div>
        ))}
        {poNote && (
          <div style={{ marginTop: 10, padding: "7px 10px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6, fontSize: 10, color: "#1E40AF", lineHeight: 1.5 }}>
            📦 <strong>Open POs:</strong> {poNote}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function StatePerformance({ navigate }) {
  const [stSel, setStSel]               = useState(null);
  const [stSortCol, setStSortCol]       = useState("value");
  const [stSortDir, setStSortDir]       = useState("desc");
  const [stShowCards, setStShowCards]   = useState(false);
  const [stShowTable, setStShowTable]   = useState(false);

  const stStates      = Object.values(STATE_MAP);
  const stTotalValue  = stStates.reduce((s,d) => s+d.value, 0);
  const stTotalCases  = stStates.reduce((s,d) => s+d.cases, 0);
  const stTotalPrior  = stStates.reduce((s,d) => s+d.pv, 0);
  const stPortYoY     = ((stTotalValue-stTotalPrior)/stTotalPrior)*100;
  const stCounts      = stStates.reduce((a,d) => { a[d.status]=(a[d.status]||0)+1; return a; }, {});
  const withYoy       = stStates.filter(d => !d.isNew);
  const stTopGainer   = [...withYoy].sort((a,b)=>(b.yoy??-999)-(a.yoy??-999))[0];
  const stTopDecliner = [...withYoy].sort((a,b)=>(a.yoy??999)-(b.yoy??999))[0];
  const stBigDrop     = stStates.filter(d=>d.chgVal<0).sort((a,b)=>a.chgVal-b.chgVal)[0];
  const stNewMkt      = stStates.find(d=>d.isNew);
  const stFmtK        = n => Math.abs(n)>=1000?(n<0?"-":"")+"$"+(Math.abs(n)/1000).toFixed(1)+"K":(n<0?"-$":"$")+Math.abs(n).toFixed(0);

  const stHandleSort  = col => { if(stSortCol===col) setStSortDir(d=>d==="desc"?"asc":"desc"); else {setStSortCol(col);setStSortDir("desc");} };
  const stSorted = [...stStates].sort((a,b) => {
    const va = stSortCol==="yoy"?(a.yoy??-999):stSortCol==="cases"?a.cases:a.value;
    const vb = stSortCol==="yoy"?(b.yoy??-999):stSortCol==="cases"?b.cases:b.value;
    return stSortDir==="desc"?vb-va:va-vb;
  });

  const StYoyTag = ({ yoy, isNew, size=11 }) => {
    if (isNew) return <span style={{ color: "#1D4ED8", fontWeight: 700, fontSize: size }}>★ NEW</span>;
    if (yoy===null||yoy===undefined) return <span style={{ color: GRAY_MID }}>—</span>;
    const up = yoy>=0;
    return <span style={{ color: up?GREEN:RED, fontWeight: 700, fontSize: size }}>{up?"▲":"▼"} {Math.abs(yoy).toFixed(1)}%</span>;
  };

  return (
    <div>
      <Alert type="critical">
        <strong>Arizona (Breakthru Arizona) — DECLINING:</strong> Down 91.6% YoY. Immediate escalation required.
        <span onClick={() => navigate("distributor")} style={{ color: RED, fontWeight: 700, cursor: "pointer", marginLeft: 8, textDecoration: "underline" }}>View Distributor Health →</span>
      </Alert>

      {/* Summary strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr) 1.5fr", gap: 12, marginBottom: 14 }}>
        {[
          { label: "Total Revenue YTD",  value: fmt(stTotalValue), sub: `Prior: ${fmt(stTotalPrior)}` },
          { label: "Portfolio YoY",      value: `${stPortYoY.toFixed(1)}%`, sub: "vs same period last year", warn: true },
          { label: "Total Cases",        value: stTotalCases.toLocaleString(), sub: "All active markets" },
          { label: "Active Markets",     value: 23, sub: `${stCounts["GROWTH"]||0} growing · ${(stCounts["AT RISK"]||0)+(stCounts["DECLINING"]||0)} declining` },
        ].map((c,i) => (
          <div key={i} style={{ background: c.warn?RED_BG:WHITE, border: `1px solid ${c.warn?"#FCA5A5":BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: c.warn?RED:DARK }}>{c.value}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{c.sub}</div>
          </div>
        ))}
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>Market Health</div>
          {[["GROWTH","#16A34A"],["STABLE",TEAL],["AT RISK","#D97706"],["DECLINING","#DC2626"],["NEW","#1D4ED8"]]
            .filter(([l])=>(stCounts[l]||0)>0).map(([label,color])=>(
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: 2, background: color, flexShrink: 0 }} />
              <div style={{ flex: 1, background: BORDER, height: 4, borderRadius: 99, overflow: "hidden" }}>
                <div style={{ width: `${((stCounts[label]||0)/23)*100}%`, height: "100%", background: color }} />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color, minWidth: 14, textAlign: "right" }}>{stCounts[label]||0}</span>
              <span style={{ fontSize: 9, color: GRAY, minWidth: 56 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Biggest Movers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 14 }}>
        {[
          { icon: "🏆", label: "Fastest Growing",     s: stTopGainer,    type: "pct" },
          { icon: "🆕",  label: "New Market",          s: stNewMkt,       type: "new" },
          { icon: "📉", label: "Biggest % Decline",   s: stTopDecliner,  type: "pct" },
          { icon: "💸", label: "Biggest Revenue Drop", s: stBigDrop,      type: "dollar" },
        ].map((m, i) => {
          if (!m.s) return <div key={i} />;
          const cfg = STATE_STATUS[m.s.status];
          const isSel = m.s.abbr === stSel;
          return (
            <div key={i} onClick={() => setStSel(isSel ? null : m.s.abbr)}
              style={{ background: WHITE, border: `2px solid ${isSel?cfg.tile:BORDER}`, borderRadius: 10, padding: "12px 14px", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.borderColor=cfg.tile}
              onMouseLeave={e => e.currentTarget.style.borderColor=isSel?cfg.tile:BORDER}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                <span style={{ fontSize: 14 }}>{m.icon}</span>
                <span style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{m.label}</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: DARK }}>{m.s.abbr} <span style={{ fontSize: 11, fontWeight: 400, color: GRAY }}>{m.s.name}</span></div>
              {m.type==="new"    && <div style={{ fontSize: 13, fontWeight: 800, color: "#1D4ED8", marginTop: 2 }}>★ {fmt(m.s.value)}</div>}
              {m.type==="pct"    && <div style={{ fontSize: 15, fontWeight: 800, color: m.s.yoy>=0?GREEN:RED, marginTop: 2 }}>{m.s.yoy>=0?"▲":"▼"} {Math.abs(m.s.yoy??0).toFixed(1)}%</div>}
              {m.type==="dollar" && <div style={{ fontSize: 13, fontWeight: 800, color: RED, marginTop: 2 }}>{stFmtK(m.s.chgVal)} ({m.s.yoy?.toFixed(1)}%)</div>}
              <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{m.s.dist}</div>
            </div>
          );
        })}
      </div>

      {/* Market Cards + Tile Map + Detail */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>Top Markets</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {[["GROWTH","#16A34A"],["STABLE",TEAL],["AT RISK","#D97706"],["DECLINING","#DC2626"],["NEW","#1D4ED8"],["No data","#D1D5DB"]].map(([l,c])=>(
              <div key={l} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, color: GRAY }}>
                <div style={{ width: 9, height: 9, borderRadius: 2, background: c }} />{l}
              </div>
            ))}
          </div>
        </div>

        {/* Cards */}
        <div style={{ padding: "14px 18px 4px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 9, marginBottom: 8 }}>
            {(stShowCards ? stSorted : stSorted.slice(0,12)).map(s => {
              const cfg = STATE_STATUS[s.status];
              const isSel = s.abbr===stSel;
              return (
                <div key={s.abbr} onClick={() => setStSel(isSel?null:s.abbr)}
                  style={{ border: `2px solid ${isSel?cfg.tile:BORDER}`, borderRadius: 8, padding: "9px 11px", cursor: "pointer", background: isSel?`${cfg.tile}12`:WHITE, position: "relative" }}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.borderColor=cfg.tile;}}
                  onMouseLeave={e=>{if(!isSel)e.currentTarget.style.borderColor=BORDER;}}>
                  <div style={{ position: "absolute", top: 7, right: 7, width: 17, height: 17, borderRadius: "50%", background: cfg.tile, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7.5, fontWeight: 800, color: cfg.tileText }}>{s.rank}</div>
                  <div style={{ fontSize: 8.5, color: GRAY, marginBottom: 4 }}>
                    <span style={{ background: cfg.tile, color: cfg.tileText, borderRadius: 3, padding: "1px 4px", fontSize: 8.5, fontWeight: 700, marginRight: 3 }}>{s.abbr}</span>
                    {s.dist.length > 18 ? s.dist.slice(0,18)+"…" : s.dist}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: DARK, marginBottom: 2 }}>{fmt(s.value)}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 9.5, color: GRAY }}>{s.cases} cs</span>
                    <StYoyTag yoy={s.yoy} isNew={s.isNew} size={9.5} />
                  </div>
                </div>
              );
            })}
          </div>
          <div onClick={()=>setStShowCards(!stShowCards)} style={{ textAlign: "center", padding: "6px", fontSize: 11, color: TEAL, fontWeight: 600, cursor: "pointer", borderTop: `1px solid ${BORDER}` }}>
            {stShowCards ? "▲ Show Less" : `▼ See More (${stStates.length-12} more markets)`}
          </div>
        </div>

        {/* Map + Detail */}
        <div style={{ borderTop: `1px solid ${BORDER}`, display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "stretch" }}>
          <div style={{ padding: "16px 18px", borderRight: `1px solid ${BORDER}` }}>
            <TileMap selected={stSel} onSelect={setStSel} />
          </div>
          <div style={{ padding: "14px 16px" }}>
            <StateDetailPanel abbr={stSel} onClose={()=>setStSel(null)} />
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${BORDER}`, padding: "9px 18px", background: "#FAFBFC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 18, fontSize: 12 }}>
            <span style={{ color: GRAY }}>Total: <strong style={{color:DARK}}>{fmt(stTotalValue)}</strong></span>
            <span style={{ color: GRAY }}>Cases: <strong style={{color:DARK}}>{stTotalCases}</strong></span>
            <span style={{ color: GRAY }}>Markets: <strong style={{color:DARK}}>23</strong></span>
          </div>
          <div style={{ fontSize: 11, color: GRAY }}>
            NY + CA = <strong style={{color:AMBER}}>{(((STATE_MAP.NY.value+STATE_MAP.CA.value)/stTotalValue)*100).toFixed(1)}%</strong> of revenue — geographic concentration risk
          </div>
        </div>
      </div>

      {/* Performance Table */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>Performance Table</div>
          <div style={{ display: "flex", gap: 6 }}>
            {["Select Brands ▾","Select Products ▾","Current Period ▾","Compare Period ▾"].map(l=>(
              <button key={l} style={{ padding: "5px 10px", border: `1px solid ${BORDER}`, borderRadius: 5, background: WHITE, fontSize: 11, color: DARK, cursor: "pointer" }}>{l}</button>
            ))}
            <Btn>▼ Filter</Btn><Btn>↓ Download</Btn><Btn>↺ Refresh</Btn>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <TH w="26px">#</TH><TH>State</TH><TH>Distributor</TH>
                <TH right onClick={()=>stHandleSort("cases")} sorted={stSortCol==="cases"}>Curr Cases</TH>
                <TH right onClick={()=>stHandleSort("value")} sorted={stSortCol==="value"}>Curr Revenue</TH>
                <TH right>Prior Cases</TH><TH right>Prior Revenue</TH>
                <TH right onClick={()=>stHandleSort("yoy")} sorted={stSortCol==="yoy"}>Revenue Δ%</TH>
                <TH right>Revenue Δ$</TH><TH right>Cases Δ%</TH>
                <TH>Status</TH><TH>Action</TH>
              </tr>
            </thead>
            <tbody>
              {(stShowTable?stSorted:stSorted.slice(0,12)).map((s, i) => {
                const isSel = s.abbr===stSel;
                const cfg = STATE_STATUS[s.status];
                return (
                  <tr key={s.abbr} onClick={()=>setStSel(isSel?null:s.abbr)}
                    style={{ background: isSel?`${cfg.tile}10`:i%2===0?WHITE:CARD_BG, borderBottom: `1px solid ${BORDER}`, cursor: "pointer" }}>
                    <td style={{ padding: "7px 10px", color: GRAY, fontSize: 11 }}>{s.rank}</td>
                    <td style={{ padding: "7px 10px", fontWeight: 700 }}>
                      <span style={{ background: cfg.tile, color: cfg.tileText, borderRadius: 3, padding: "1px 5px", fontSize: 10, fontWeight: 700, marginRight: 5 }}>{s.abbr}</span>{s.name}
                    </td>
                    <td style={{ padding: "7px 10px", color: GRAY, fontSize: 11 }}>{s.dist}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{s.cases}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(s.value)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", color: GRAY }}>{s.pc>0?s.pc:<span style={{color:GRAY_MID}}>—</span>}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", color: GRAY }}>{s.pv>0?fmt(s.pv):<span style={{color:GRAY_MID}}>—</span>}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}><StYoyTag yoy={s.yoy} isNew={s.isNew} /></td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}>
                      {!s.isNew && <span style={{ color: s.chgVal>=0?GREEN:RED, fontWeight: 500 }}>{s.chgVal>=0?"+":""}{fmt(s.chgVal)}</span>}
                    </td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}><StYoyTag yoy={s.yoyCs} isNew={s.isNew} /></td>
                    <td style={{ padding: "7px 10px" }}><StateBadge status={s.status} /></td>
                    <td style={{ padding: "7px 10px" }}><StateActionBadge action={s.action} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: "9px 18px", background: "#FAFBFC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 18, fontSize: 12 }}>
            <span style={{ color: GRAY }}>Total: <strong>{fmt(stTotalValue)}</strong></span>
            <span style={{ color: GRAY }}>Cases: <strong>{stTotalCases}</strong></span>
            <span style={{ color: RED, fontWeight: 600 }}>Portfolio YoY: ▼ {Math.abs(stPortYoY).toFixed(1)}%</span>
          </div>
          <div onClick={()=>setStShowTable(!stShowTable)} style={{ fontSize: 12, color: TEAL, fontWeight: 600, cursor: "pointer" }}>
            {stShowTable ? "▲ Show Less" : `▼ Show All ${stStates.length} Markets`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE: DISTRIBUTOR HEALTH (NEW)
// ═════════════════════════════════════════════════════════════════════════════
const distData = [
  { name: "Empire State Spirits", lastOrder: 12, cbRate: 6.2,  depRatio: 0.91, orders3mo: 3, risk: "LOW",      trend: "▲" },
  { name: "Golden State Bev.",    lastOrder: 8,  cbRate: 8.1,  depRatio: 0.85, orders3mo: 3, risk: "LOW",      trend: "▲" },
  { name: "Lone Star Dist.",      lastOrder: 22, cbRate: 9.4,  depRatio: 0.74, orders3mo: 2, risk: "MEDIUM",   trend: "→" },
  { name: "Sunshine Dist.",       lastOrder: 38, cbRate: 14.2, depRatio: 0.61, orders3mo: 1, risk: "HIGH",     trend: "▼" },
  { name: "Midwest Bev. Co.",     lastOrder: 64, cbRate: 18.7, depRatio: 0.48, orders3mo: 0, risk: "CRITICAL", trend: "▼" },
];
const RISK_CFG = { LOW: { bg: GREEN_BG, text: GREEN }, MEDIUM: { bg: AMBER_BG, text: AMBER }, HIGH: { bg: RED_BG, text: RED }, CRITICAL: { bg: "#7f1d1d", text: "#fecaca" } };

function DistributorHealth({ navigate }) {
  return (
    <div>
      <div style={{ background: "#7f1d1d", border: "1px solid #991b1b", borderLeft: `4px solid ${RED}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, display: "flex", gap: 8, fontSize: 12, color: "#fecaca" }}>
        <span style={{ fontSize: 13 }}>🚨</span>
        <div><strong>CRITICAL:</strong> Midwest Bev. Co. (Illinois) — 64 days no order, depletion ratio 0.48, CB rate 18.7%. Immediate escalation to brand owner required.
          <span onClick={() => navigate("state")} style={{ color: "#FCA5A5", fontWeight: 700, cursor: "pointer", marginLeft: 8, textDecoration: "underline" }}>View State Performance →</span>
        </div>
      </div>

      <Section title="Distributor Health Scorecard" controls={[<Select key="1" label="All Brands" />, <Select key="2" label="2026" w={80} />]}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr><TH>Distributor</TH><TH right>Days Since Order</TH><TH right>CB Rate</TH><TH right>Dep./Ship. Ratio</TH><TH right>Orders (3mo)</TH><TH right>Trend</TH><TH right>Risk</TH><TH>Action</TH></tr>
          </thead>
          <tbody>
            {distData.map((d, i) => {
              const rc = RISK_CFG[d.risk];
              const actions = { LOW: "No action needed", MEDIUM: "Monitor monthly", HIGH: "Schedule distributor review call", CRITICAL: "Escalate to brand owner immediately" };
              return (
                <tr key={i} style={{ background: i%2===0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600 }}>{d.name}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}><span style={{ color: d.lastOrder > 30 ? RED : GREEN, fontWeight: d.lastOrder > 30 ? 700 : 400 }}>{d.lastOrder}d</span></td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}><span style={{ color: d.cbRate > 10 ? RED : GRAY, fontWeight: d.cbRate > 10 ? 700 : 400 }}>{d.cbRate.toFixed(1)}%{d.cbRate>10?" ⚠":""}</span></td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}><span style={{ color: d.depRatio < 0.7 ? RED : GREEN, fontWeight: 700 }}>{d.depRatio.toFixed(2)}</span></td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>{d.orders3mo}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 14, color: d.trend === "▲" ? GREEN : d.trend === "▼" ? RED : GRAY }}>{d.trend}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}><span style={{ background: rc.bg, color: rc.text, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4 }}>{d.risk}</span></td>
                  <td style={{ padding: "8px 10px", fontSize: 11, color: GRAY }}>{actions[d.risk]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
        {[
          { icon: "⏱", label: "Order Gap > 30 Days", desc: "Previously monthly distributor hasn't reordered. Triggers outreach task.", color: RED_BG, border: RED },
          { icon: "📊", label: "Chargeback Rate Spike", desc: "CB rate increases >3pts month-over-month. Indicates distributor friction.", color: AMBER_BG, border: AMBER },
          { icon: "📦", label: "Depletion Ratio < 0.7", desc: "Inventory building faster than sell-through. Reorders will pause.", color: AMBER_BG, border: AMBER },
          { icon: "🎯", label: "Distributor Concentration >40%", desc: "Single distributor = >40% of brand revenue in a state — single point of failure.", color: RED_BG, border: RED },
        ].map((r, i) => (
          <div key={i} style={{ background: r.color, border: `1px solid ${r.border}44`, borderLeft: `3px solid ${r.border}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 16, marginBottom: 4 }}>{r.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: DARK, marginBottom: 4 }}>{r.label}</div>
            <div style={{ fontSize: 11, color: GRAY, lineHeight: 1.5 }}>{r.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SIDEBAR + SHELL
// ═════════════════════════════════════════════════════════════════════════════
const NAV = [
  { id: "topline",     label: "Sales Topline",      group: "SALES" },
  { id: "state",       label: "State Performance",  group: "SALES" },
  { id: "brand",       label: "Brand Performance",  group: "SALES" },
  { id: "monthly",     label: "Monthly Comparison", group: "SALES" },
  { id: "sku",         label: "SKU Performance",    group: "SALES" },
  { id: "distributor", label: "Distributor Health", group: "SALES", isNew: true },
];

const PAGE_TITLES = {
  helm:  "The Helm",
  topline: "Sales Topline", state: "State Performance",
  brand: "Brand Performance", monthly: "Monthly Comparison",
  sku: "SKU Performance", distributor: "Distributor Health",
  admin: "Growth Multipliers",
  prod:  "Product Management",
};

function MonthlyComparison({ navigate }) {
  const [brandFilter, setBrandFilter] = useState("All Brands");
  const [viewMode,    setViewMode]    = useState("chart");   // chart | table | compare
  const [yearA,       setYearA]       = useState("2026");
  const [yearB,       setYearB]       = useState("2025");

  const brands = ["All Brands","Avuá Cachaça","Paladar Tequila","Svöl Aquavit","Other Brands"];
  const data   = BRAND_MONTHLY[brandFilter] || monthlyData;

  // ── Computed summary ─────────────────────────────────────────────────────
  const ytdMonths  = data.filter(d => d.actual > 0);
  const ytdActual  = ytdMonths.reduce((s,d) => s+d.actual, 0);
  const ytdTarget  = ytdMonths.reduce((s,d) => s+d.target, 0);
  const ytdLY      = ytdMonths.reduce((s,d) => s+d.ly, 0);
  const ytdCases   = ytdMonths.reduce((s,d) => s+d.cases, 0);
  const ytdLYCases = ytdMonths.reduce((s,d) => s+d.lyCases, 0);
  const annGoal    = data.reduce((s,d) => s+d.target, 0);
  const annPace    = ytdMonths.length > 0 ? (ytdActual / ytdMonths.length) * 12 : 0;
  const pacePct    = (annPace / annGoal) * 100;
  const yoyPct     = ytdLY > 0 ? ((ytdActual - ytdLY) / ytdLY) * 100 : null;
  const vsTargetPct= ytdTarget > 0 ? ((ytdActual - ytdTarget) / ytdTarget) * 100 : null;

  // Peak & trough from LY (best predictor of seasonality)
  const lyWithData  = data.filter(d => d.ly > 0);
  const peakMonth   = [...lyWithData].sort((a,b) => b.ly - a.ly)[0];
  const troughMonth = [...lyWithData].sort((a,b) => a.ly - b.ly)[0];

  // Running cumulative for chart
  let running = 0, runningTarget = 0;
  data.map(d => {
    if (d.actual > 0) { running += d.actual; runningTarget += d.target; }
    return { ...d, cumActual: d.actual > 0 ? running : null, cumTarget: runningTarget };
  });

  // Status per completed month
  const monthStatus = d => {
    if (!d.actual) return null;
    const pct = d.actual / d.target;
    if (pct >= 1.0) return { label: "Above Target", bg: GREEN_BG, text: GREEN, border: "#86EFAC" };
    if (pct >= 0.85) return { label: "Near Target",  bg: AMBER_BG, text: AMBER, border: "#FCD34D" };
    return { label: "Below Target", bg: RED_BG, text: RED, border: "#FCA5A5" };
  };

  const BRAND_COLORS = { "Avuá Cachaça": TEAL, "Paladar Tequila": TEAL2, "Svöl Aquavit": "#2DD4BF", "Other Brands": GRAY_MID };

  return (
    <div>
      {/* ── Alert ──────────────────────────────────────────────────────────── */}
      {yoyPct !== null && yoyPct < -20 && (
        <Alert type="critical">
          <strong>YTD {brandFilter} revenue down {Math.abs(yoyPct).toFixed(1)}% vs same period {yearB}.</strong>{" "}
          {brandFilter === "All Brands" ? "All three completed months are below $95K monthly target." : `${brandFilter} tracking below prior year pace.`}
          <span onClick={() => navigate("topline")} style={{ color: RED, fontWeight: 700, cursor: "pointer", marginLeft: 8, textDecoration: "underline" }}>← Topline</span>
        </Alert>
      )}

      {/* ── Filters + View Toggle ─────────────────────────────────────────── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "12px 16px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Brand filter */}
          <div style={{ display: "flex", gap: 4 }}>
            {brands.map(b => (
              <button key={b} onClick={() => setBrandFilter(b)}
                style={{ padding: "5px 11px", border: `1px solid ${brandFilter===b ? TEAL : BORDER}`, borderRadius: 6, background: brandFilter===b ? TEAL : WHITE, color: brandFilter===b ? WHITE : DARK, fontSize: 11, fontWeight: brandFilter===b ? 700 : 400, cursor: "pointer", whiteSpace: "nowrap" }}>
                {b === "All Brands" ? "All" : b.split(" ")[0]}
              </button>
            ))}
          </div>
          <div style={{ width: 1, height: 24, background: BORDER }} />
          {/* Year selectors */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{ color: GRAY, fontSize: 11 }}>Period A:</span>
            {["2026","2025","2024"].map(y => (
              <button key={y} onClick={() => setYearA(y)} style={{ padding: "4px 9px", border: `1px solid ${yearA===y?TEAL:BORDER}`, borderRadius: 5, background: yearA===y?`${TEAL}15`:WHITE, color: yearA===y?TEAL:GRAY, fontSize: 11, fontWeight: yearA===y?700:400, cursor: "pointer" }}>{y}</button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{ color: GRAY, fontSize: 11 }}>vs:</span>
            {["2025","2024"].map(y => (
              <button key={y} onClick={() => setYearB(y)} style={{ padding: "4px 9px", border: `1px solid ${yearB===y?TEAL2:BORDER}`, borderRadius: 5, background: yearB===y?`${TEAL2}15`:WHITE, color: yearB===y?TEAL2:GRAY, fontSize: 11, fontWeight: yearB===y?700:400, cursor: "pointer" }}>{y}</button>
            ))}
          </div>
        </div>
        {/* View mode */}
        <div style={{ display: "flex", gap: 4 }}>
          {[["chart","📊 Chart"],["table","📋 Table"],["compare","🔄 Compare"]].map(([v,l]) => (
            <button key={v} onClick={() => setViewMode(v)}
              style={{ padding: "5px 12px", border: `1px solid ${viewMode===v?TEAL:BORDER}`, borderRadius: 6, background: viewMode===v?TEAL:WHITE, color: viewMode===v?WHITE:GRAY, fontSize: 11, fontWeight: viewMode===v?700:400, cursor: "pointer" }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI Summary Strip ─────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 14 }}>
        {[
          { label: `YTD Actual (${yearA})`,   value: fmt(ytdActual),          sub: `${ytdCases} cases`, hi: false, warn: false },
          { label: `YTD ${yearB} Actual`,      value: fmt(ytdLY),              sub: `${ytdLYCases} cases`, hi: false, warn: false },
          { label: "vs Target YTD",            value: `${vsTargetPct?.toFixed(1)}%`, sub: `Target: ${fmt(ytdTarget)}`, warn: vsTargetPct < 0, hi: vsTargetPct >= 0 },
          { label: `YoY vs ${yearB}`,          value: yoyPct !== null ? `${yoyPct.toFixed(1)}%` : "—", sub: yoyPct < 0 ? `${fmt(ytdLY - ytdActual)} behind` : `${fmt(ytdActual - ytdLY)} ahead`, warn: yoyPct < -10, hi: yoyPct >= 0 },
          { label: "Annual Pace",              value: fmt(annPace),            sub: `${pacePct.toFixed(0)}% of ${fmt(annGoal)} goal`, warn: pacePct < 85, hi: pacePct >= 100 },
        ].map((c, i) => (
          <div key={i} style={{ background: c.hi ? GREEN_BG : c.warn ? RED_BG : WHITE, border: `1px solid ${c.hi ? "#86EFAC" : c.warn ? "#FCA5A5" : BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.hi ? GREEN : c.warn ? RED : DARK }}>{c.value}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Seasonality Insights ──────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
        {[
          { icon: "📅", label: "Peak Month (LY)",    name: peakMonth?.m,   val: fmt(peakMonth?.ly || 0),   color: GREEN,  bg: GREEN_BG,  border: "#86EFAC" },
          { icon: "📉", label: "Trough Month (LY)",  name: troughMonth?.m, val: fmt(troughMonth?.ly || 0), color: AMBER,  bg: AMBER_BG,  border: "#FCD34D" },
          { icon: "🎯", label: "Best vs Target",     name: ytdMonths.length ? [...ytdMonths].sort((a,b)=>(b.actual/b.target)-(a.actual/a.target))[0]?.m : "—",
            val: ytdMonths.length ? `${((Math.max(...ytdMonths.map(d=>d.actual/d.target)))*100).toFixed(0)}% of target` : "—", color: TEAL, bg: "#E8F4F5", border: "#B2D8DC" },
          { icon: "📆", label: "Months Remaining",  name: `${12 - ytdMonths.length} months`, val: `${fmt(annGoal - ytdActual)} needed`, color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE" },
        ].map((c,i) => (
          <div key={i} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: "11px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: 18 }}>{c.icon}</span>
            <div>
              <div style={{ fontSize: 9, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{c.label}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: c.color }}>{c.name}</div>
              <div style={{ fontSize: 11, color: GRAY, marginTop: 1 }}>{c.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── CHART VIEW ────────────────────────────────────────────────────── */}
      {viewMode === "chart" && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ padding: "13px 18px 11px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>
              {brandFilter} — Monthly Revenue {yearA} vs {yearB} vs Target
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              {[[`${yearA} Actual`, TEAL],[`${yearB} Actual`,"#CBD5E1"],["Monthly Target",`${TEAL}55`]].map(([l,c])=>(
                <div key={l} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: GRAY }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />{l}
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: "16px 18px" }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data} barCategoryGap="22%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: GRAY }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: GRAY }} tickFormatter={fmtK} axisLine={false} tickLine={false} width={46} />
                <Tooltip formatter={(v, n) => [v > 0 ? fmt(v) : "—", n]} contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="ly"     name={`${yearB} Actual`}   fill="#CBD5E1" radius={[3,3,0,0]} />
                <Bar dataKey="target" name="Monthly Target"      fill={`${TEAL}44`} radius={[3,3,0,0]} />
                <Bar dataKey="actual" name={`${yearA} Actual`}   fill={BRAND_COLORS[brandFilter] || TEAL} radius={[3,3,0,0]}>
                  {data.map((d, i) => <Cell key={i} fill={d.actual > d.target ? "#16A34A" : d.actual > 0 ? (BRAND_COLORS[brandFilter] || TEAL) : CARD_BG} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Month status pills */}
            <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
              {data.filter(d=>d.actual>0).map(d => {
                const st = monthStatus(d);
                if (!st) return null;
                return (
                  <div key={d.m} style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 6, padding: "4px 9px", fontSize: 10, color: st.text, fontWeight: 600 }}>
                    {d.m}: {st.label} ({((d.actual/d.target)*100).toFixed(0)}%)
                  </div>
                );
              })}
            </div>
            {/* LY seasonality callout */}
            <div style={{ marginTop: 12, padding: "9px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderLeft: "3px solid #1D4ED8", borderRadius: 6, fontSize: 11, color: "#1E40AF", lineHeight: 1.6 }}>
              <strong>📅 Seasonality note ({yearB}):</strong> Jan was your strongest month ($148K), falling to a trough in Dec ($28K). Q1 historically accounts for {((data.slice(0,3).reduce((s,d)=>s+d.ly,0)/data.reduce((s,d)=>s+d.ly,0))*100).toFixed(0)}% of annual revenue. April–May were mid-strength — plan inventory accordingly.
            </div>
          </div>
        </div>
      )}

      {/* ── TABLE VIEW ────────────────────────────────────────────────────── */}
      {viewMode === "table" && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ padding: "13px 18px 11px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>{brandFilter} — Monthly Detail</div>
            <div style={{ display: "flex", gap: 6 }}><Btn>↓ Download</Btn><Btn>↺ Refresh</Btn></div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <TH>Month</TH>
                <TH right>{yearA} Revenue</TH>
                <TH right>{yearA} Cases</TH>
                <TH right>Monthly Target</TH>
                <TH right>vs Target</TH>
                <TH right>{yearB} Revenue</TH>
                <TH right>YoY Rev %</TH>
                <TH right>YoY Cases %</TH>
                <TH>Status</TH>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => {
                const st   = monthStatus(d);
                const yoy  = d.ly > 0 ? ((d.actual - d.ly) / d.ly) * 100 : null;
                const yoyCs = d.lyCases > 0 && d.cases > 0 ? ((d.cases - d.lyCases) / d.lyCases) * 100 : null;
                const vsT  = d.actual > 0 ? ((d.actual - d.target) / d.target) * 100 : null;
                const isFuture = !d.actual;
                return (
                  <tr key={i} style={{ background: isFuture ? "#FAFBFC" : i%2===0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}`, opacity: isFuture ? 0.55 : 1 }}>
                    <td style={{ padding: "8px 10px", fontWeight: 600, color: isFuture ? GRAY_MID : DARK }}>{d.m}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: isFuture ? 400 : 600, color: isFuture ? GRAY_MID : DARK }}>
                      {d.actual > 0 ? fmt(d.actual) : <span style={{color:GRAY_MID,fontSize:10}}>pending</span>}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "right", color: isFuture ? GRAY_MID : DARK }}>{d.cases > 0 ? d.cases : "—"}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", color: GRAY }}>{fmt(d.target)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>
                      {vsT !== null ? <span style={{ color: vsT >= 0 ? GREEN : RED, fontWeight: 700 }}>{vsT >= 0 ? "▲" : "▼"} {Math.abs(vsT).toFixed(1)}%</span> : <span style={{color:GRAY_MID}}>—</span>}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "right", color: GRAY }}>{d.ly > 0 ? fmt(d.ly) : "—"}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>
                      {yoy !== null && d.actual > 0 ? <span style={{ color: yoy >= 0 ? GREEN : RED, fontWeight: 700 }}>{yoy >= 0 ? "▲" : "▼"} {Math.abs(yoy).toFixed(1)}%</span> : <span style={{color:GRAY_MID}}>—</span>}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>
                      {yoyCs !== null && d.cases > 0 ? <span style={{ color: yoyCs >= 0 ? GREEN : RED, fontWeight: 700 }}>{yoyCs >= 0 ? "▲" : "▼"} {Math.abs(yoyCs).toFixed(1)}%</span> : <span style={{color:GRAY_MID}}>—</span>}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {st ? <span style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}`, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700 }}>{st.label}</span>
                           : isFuture ? <span style={{ color: GRAY_MID, fontSize: 10 }}>{fmt(d.ly)} LY</span> : null}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                <td style={{ padding: "8px 10px", fontWeight: 700 }}>YTD Total</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{fmt(ytdActual)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{ytdCases}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: GRAY }}>{fmt(ytdTarget)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  <span style={{ color: vsTargetPct >= 0 ? GREEN : RED, fontWeight: 700 }}>
                    {vsTargetPct?.toFixed(1)}%
                  </span>
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: GRAY }}>{fmt(ytdLY)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  <span style={{ color: yoyPct >= 0 ? GREEN : RED, fontWeight: 700 }}>{yoyPct?.toFixed(1)}%</span>
                </td>
                <td colSpan={2} style={{ padding: "8px 10px", fontSize: 11, color: GRAY }}>Annual pace: {fmt(annPace)} ({pacePct.toFixed(0)}% of goal)</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── COMPARE VIEW ─────────────────────────────────────────────────── */}
      {viewMode === "compare" && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ padding: "13px 18px 11px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>Side-by-Side: {yearA} vs {yearB}</div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: TEAL }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: TEAL }} />{yearA} Actual
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: GRAY }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "#CBD5E1" }} />{yearB} Actual
              </div>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <TH>Month</TH>
                  <TH right>{yearA} Revenue</TH>
                  <TH right>{yearA} Cases</TH>
                  <TH right>{yearB} Revenue</TH>
                  <TH right>{yearB} Cases</TH>
                  <TH right>Change in $</TH>
                  <TH right>Change in %</TH>
                  <TH right>Change in Cases</TH>
                  <TH right>Change Cases %</TH>
                </tr>
              </thead>
              <tbody>
                {data.map((d, i) => {
                  const chgVal = d.ly > 0 ? d.actual - d.ly : null;
                  const chgPct = d.ly > 0 && d.actual > 0 ? ((d.actual - d.ly) / d.ly) * 100 : null;
                  const chgCs  = d.lyCases > 0 && d.cases > 0 ? d.cases - d.lyCases : null;
                  const chgCsPct = d.lyCases > 0 && d.cases > 0 ? ((d.cases - d.lyCases) / d.lyCases) * 100 : null;
                  const isFuture = !d.actual;
                  const good = v => <span style={{ color: v >= 0 ? GREEN : RED, fontWeight: 700 }}>{v >= 0 ? "▲" : "▼"} {Math.abs(v).toFixed(v > 100 || v < -100 ? 0 : 1)}{typeof v === "number" && !Number.isInteger(v) ? "" : ""}</span>;
                  return (
                    <tr key={i} style={{ background: isFuture ? "#FAFBFC" : i%2===0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}`, opacity: isFuture ? 0.5 : 1 }}>
                      <td style={{ padding: "8px 10px", fontWeight: 600, color: isFuture ? GRAY_MID : DARK }}>{d.m}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: isFuture ? GRAY_MID : TEAL }}>
                        {d.actual > 0 ? fmt(d.actual) : <span style={{fontSize:10,color:GRAY_MID}}>pending</span>}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: isFuture?GRAY_MID:DARK }}>{d.cases > 0 ? d.cases : "—"}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: GRAY }}>{d.ly > 0 ? fmt(d.ly) : "—"}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: GRAY }}>{d.lyCases > 0 ? d.lyCases : "—"}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>
                        {chgVal !== null && d.actual > 0 ? <span style={{ color: chgVal >= 0 ? GREEN : RED, fontWeight: 600 }}>{chgVal >= 0 ? "+" : ""}{fmt(chgVal)}</span> : <span style={{color:GRAY_MID}}>—</span>}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>
                        {chgPct !== null ? good(chgPct) : <span style={{color:GRAY_MID}}>—</span>}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>
                        {chgCs !== null ? <span style={{ color: chgCs >= 0 ? GREEN : RED, fontWeight: 600 }}>{chgCs >= 0 ? "+" : ""}{chgCs}</span> : <span style={{color:GRAY_MID}}>—</span>}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>
                        {chgCsPct !== null ? good(chgCsPct) : <span style={{color:GRAY_MID}}>—</span>}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700 }}>YTD Total</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: TEAL }}>{fmt(ytdActual)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{ytdCases}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: GRAY }}>{fmt(ytdLY)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: GRAY }}>{ytdLYCases}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    <span style={{ color: (ytdActual-ytdLY) >= 0 ? GREEN : RED, fontWeight: 700 }}>{(ytdActual-ytdLY) >= 0 ? "+" : ""}{fmt(ytdActual-ytdLY)}</span>
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    <span style={{ color: yoyPct >= 0 ? GREEN : RED, fontWeight: 700 }}>{yoyPct >= 0 ? "▲" : "▼"} {Math.abs(yoyPct).toFixed(1)}%</span>
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    <span style={{ color: (ytdCases-ytdLYCases) >= 0 ? GREEN : RED, fontWeight: 700 }}>{(ytdCases-ytdLYCases) >= 0 ? "+" : ""}{ytdCases-ytdLYCases}</span>
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    <span style={{ color: ((ytdCases-ytdLYCases)/ytdLYCases) >= 0 ? GREEN : RED, fontWeight: 700 }}>
                      {(((ytdCases-ytdLYCases)/ytdLYCases)*100).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Brand breakdown (always visible) ──────────────────────────────── */}
      {brandFilter === "All Brands" && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "12px 18px 10px", borderBottom: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>Brand Contribution by Month (YTD)</div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <TH>Brand</TH>
                <TH right>Jan</TH><TH right>Feb</TH><TH right>Mar</TH>
                <TH right>YTD Total</TH><TH right>YTD Cases</TH><TH right>Mix %</TH>
              </tr>
            </thead>
            <tbody>
              {brandRows.map((b, i) => (
                <tr key={i} style={{ background: i%2===0?WHITE:CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: "7px 10px", fontWeight: 500 }}>{b.brand}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtK(Math.round(b.amount*0.24))}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtK(Math.round(b.amount*0.29))}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtK(Math.round(b.amount*0.47))}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(b.amount)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{b.cases}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, background: BORDER, height: 5, borderRadius: 99, minWidth: 50, overflow: "hidden" }}>
                        <div style={{ width: `${b.pct}%`, height: "100%", background: TEAL, borderRadius: 99 }} />
                      </div>
                      <span style={{ fontWeight: 600, minWidth: 36, textAlign: "right" }}>{b.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE: HELMSMAN INTERNAL PORTFOLIO DASHBOARD
// ═════════════════════════════════════════════════════════════════════════════

// ── Real data from screenshots ────────────────────────────────────────────
const HM_PORTFOLIO = {
  ytdRev: 2244263.72, ytdCases: 10093.17,
  lyYtdRev: 2116257.70, lyYtdCases: 8948.59,
  mtdRev: 284757.87, mtdCases: 1707.01,
  lyMtdRev: 238359.35, lyMtdCases: 1604.34,
  totalClients: 45, totalBrands: 101, avgClientAgeYrs: 2.58,
};

const HM_MONTHLY = [
  { m:"Jan", rev2026: 762426.81, rev2025: 680000 },
  { m:"Feb", rev2026: 1197079.04, rev2025: 890000 },
  { m:"Mar", rev2026: 284757.87, rev2025: 238359 },
  { m:"Apr", rev2026: 0, rev2025: 520000 },
  { m:"May", rev2026: 0, rev2025: 610000 },
  { m:"Jun", rev2026: 0, rev2025: 740000 },
  { m:"Jul", rev2026: 0, rev2025: 680000 },
  { m:"Aug", rev2026: 0, rev2025: 590000 },
  { m:"Sep", rev2026: 0, rev2025: 820000 },
  { m:"Oct", rev2026: 0, rev2025: 1180000 },
  { m:"Nov", rev2026: 0, rev2025: 980000 },
  { m:"Dec", rev2026: 0, rev2025: 2380000 },
];

// ── Account Managers ───────────────────────────────────────────────────────
const ACCOUNT_MANAGERS = [
  { id:"OC", name:"Olive Chae",          title:"Account Manager", avatar:"OC", color:"#0D5C63" },
  { id:"JB", name:"Jeanne De Vera-Beltran", title:"Account Manager", avatar:"JB", color:"#2563EB" },
];

// ── Client roster (real data from screenshots + realistic synthetic health data) ─
const DEFAULT_CASE_FEE  = 2.50;
const YTD_MONTHS        = 3;

// Transportation margin — % markup on freight invoices (placeholder, adjustable in Fees tab)
const DEFAULT_TRANSPORT_MARKUP = 0.15; // 15%
const DEFAULT_FREIGHT_PER_CASE = 3.00; // $3/case base freight estimate

// Account tier classification
function accountTier(c) {
  if (c.strategic) return "Strategic";
  const annualRate = (c.ytdCases / YTD_MONTHS) * 12;
  if (annualRate >= 1000) return "Big";
  if (annualRate >= 500)  return "Medium";
  if (annualRate >= 100)  return "Small";
  return "Micro";
}

const ACCOUNT_TIER_CFG = {
  Strategic: { bg:"#FAF5FF", text:"#7C3AED", border:"#C4B5FD", icon:"⭐", label:"Strategic" },
  Big:       { bg:GREEN_BG,  text:GREEN,      border:"#86EFAC", icon:"🏆", label:"Big (1000+)"  },
  Medium:    { bg:"#EFF6FF", text:"#1D4ED8",  border:"#BFDBFE", icon:"📈", label:"Medium (500–999)" },
  Small:     { bg:AMBER_BG,  text:AMBER,      border:"#FCD34D", icon:"🌱", label:"Small (100–499)"  },
  Micro:     { bg:CARD_BG,   text:GRAY,       border:BORDER,    icon:"🔬", label:"Micro (<100)"     },
  Prospect:  { bg:"#FDF2F8", text:"#BE185D",  border:"#FBCFE8", icon:"🎯", label:"Prospect"         },
};

// AM role time allocation (both % time + client load model)
const AM_ROLES = [
  { role:"Account Management",      pct:30, icon:"🤝", desc:"Strategy, reviews, relationship health" },
  { role:"Purchase Orders",          pct:25, icon:"📋", desc:"PO creation, approval, tracking" },
  { role:"Formula & COLA Filing",    pct:20, icon:"📄", desc:"TTB compliance, label approvals" },
  { role:"Logistics Follow-up",      pct:10, icon:"🚚", desc:"Freight, warehouse, delivery status" },
  { role:"Distributor Add-on",       pct:10, icon:"🏪", desc:"New distributor onboarding & comms" },
  { role:"Depletion Follow-up",      pct:5,  icon:"📊", desc:"Sell-through tracking, depletion reports" },
];
// 25% capacity buffer — we hire quality people before we're full
const AM_CAPACITY_MAX    = 20;  // theoretical max clients per AM
const AM_CAPACITY_TARGET = 15;  // 75% = healthy utilization with 25% buffer

// HM_AM_COSTS kept for internal reference only — not displayed
const HM_AM_COSTS = {
  OC: { overhead: 1.35 },
  JB: { overhead: 1.35 },
};

const HM_PIPELINE = [
  { id:"PR01", name:"Matchbook Distilling",   stage:"Prospect",   category:"other",   estCases:180,  estMonthlyFee:900,  am:"OC", probability:70, daysInStage:14 },
  { id:"PR02", name:"Kikori Whiskey",         stage:"Prospect",   category:"other",   estCases:240,  estMonthlyFee:1000, am:"JB", probability:50, daysInStage:28 },
  { id:"PR03", name:"Forthave Spirits",       stage:"Onboarding", category:"gin",     estCases:90,   estMonthlyFee:750,  am:"JB", probability:95, daysInStage:7  },
  { id:"PR04", name:"Madre Mezcal",           stage:"Prospect",   category:"tequila", estCases:320,  estMonthlyFee:1100, am:"OC", probability:40, daysInStage:45 },
  { id:"PR05", name:"Brennivin",              stage:"Onboarding", category:"aquavit", estCases:110,  estMonthlyFee:800,  am:"OC", probability:90, daysInStage:3  },
  { id:"PR06", name:"Split Rail Whiskey",     stage:"Prospect",   category:"other",   estCases:150,  estMonthlyFee:850,  am:"JB", probability:30, daysInStage:60 },
  { id:"CH01", name:"Saint Luna",             stage:"Churned",    category:"other",   estCases:0,    estMonthlyFee:0,    am:"OC", probability:0,  daysInStage:90 },
  { id:"CH02", name:"BITE Drinks",            stage:"Churned",    category:"rtd",     estCases:0,    estMonthlyFee:0,    am:"JB", probability:0,  daysInStage:180 },
];

const HM_CLIENTS = [
  // country: client HQ / brand origin country | strategic: flagship regardless of case count
  { id:"C01", name:"Rare Character",        ytdRev:1590072, ytdCases:4301, lyCases:3800, lyRev:1240000, cbRate:3.2,  lastOrder:8,  compliance:0, monthlyFee:1500, startDate:"2021-05-27", am:"OC", category:"other",   brands:8, depRatio:0.92, contractType:"annual",  complexity:5, country:"USA",       strategic:true  },
  { id:"C02", name:"Oliana Experience",     ytdRev:140158,  ytdCases:845,  lyCases:680,  lyRev:112000,  cbRate:6.1,  lastOrder:12, compliance:0, monthlyFee:1000, startDate:"2022-01-15", am:"JB", category:"other",   brands:4, depRatio:0.88, contractType:"annual",  complexity:3, country:"Brazil",    strategic:false },
  { id:"C03", name:"Drifter Spirits",       ytdRev:139568,  ytdCases:977,  lyCases:1980, lyRev:302000,  cbRate:9.8,  lastOrder:3,  compliance:1, monthlyFee:1200, startDate:"2020-07-20", am:"OC", category:"cachaca", brands:6, depRatio:0.74, contractType:"annual",  complexity:4, country:"USA",       strategic:true  },
  { id:"C04", name:"Terres Blanches",       ytdRev:85705,   ytdCases:823,  lyCases:710,  lyRev:68000,   cbRate:4.2,  lastOrder:15, compliance:0, monthlyFee:900,  startDate:"2023-03-09", am:"OC", category:"other",   brands:3, depRatio:0.91, contractType:"annual",  complexity:3, country:"France",    strategic:false },
  { id:"C05", name:"Lot 001",               ytdRev:70602,   ytdCases:319,  lyCases:290,  lyRev:58000,   cbRate:5.8,  lastOrder:22, compliance:0, monthlyFee:800,  startDate:"2022-08-01", am:"OC", category:"other",   brands:2, depRatio:0.85, contractType:"monthly", complexity:2, country:"USA",       strategic:false },
  { id:"C06", name:"E A Worldwide Traders", ytdRev:52836,   ytdCases:1258, lyCases:1100, lyRev:44000,   cbRate:7.4,  lastOrder:18, compliance:0, monthlyFee:1100, startDate:"2023-06-12", am:"JB", category:"other",   brands:5, depRatio:0.82, contractType:"annual",  complexity:4, country:"USA",       strategic:false },
  { id:"C07", name:"Run The Jewels",        ytdRev:26171,   ytdCases:455,  lyCases:520,  lyRev:31000,   cbRate:8.1,  lastOrder:28, compliance:1, monthlyFee:800,  startDate:"2022-10-01", am:"JB", category:"rtd",     brands:2, depRatio:0.71, contractType:"monthly", complexity:2, country:"USA",       strategic:false },
  { id:"C08", name:"Cardenwe",              ytdRev:25325,   ytdCases:152,  lyCases:118,  lyRev:19000,   cbRate:3.9,  lastOrder:9,  compliance:0, monthlyFee:800,  startDate:"2023-09-01", am:"JB", category:"other",   brands:2, depRatio:0.94, contractType:"monthly", complexity:2, country:"Mexico",    strategic:false },
  { id:"C09", name:"Snow Tequila",          ytdRev:22926,   ytdCases:136,  lyCases:110,  lyRev:18000,   cbRate:5.2,  lastOrder:31, compliance:0, monthlyFee:850,  startDate:"2024-01-10", am:"JB", category:"tequila", brands:3, depRatio:0.79, contractType:"monthly", complexity:2, country:"Mexico",    strategic:false },
  { id:"C10", name:"WD O'Connell",          ytdRev:12340,   ytdCases:77,   lyCases:65,   lyRev:9800,    cbRate:4.1,  lastOrder:45, compliance:0, monthlyFee:700,  startDate:"2022-12-19", am:"JB", category:"other",   brands:2, depRatio:0.86, contractType:"monthly", complexity:1, country:"Ireland",   strategic:false },
  { id:"C11", name:"Tra Kal",               ytdRev:11951,   ytdCases:96,   lyCases:115,  lyRev:14200,   cbRate:6.8,  lastOrder:52, compliance:1, monthlyFee:700,  startDate:"2021-12-01", am:"JB", category:"other",   brands:2, depRatio:0.68, contractType:"monthly", complexity:2, country:"Argentina", strategic:false },
  { id:"C12", name:"Irish Spirit Brands",   ytdRev:9711,    ytdCases:60,   lyCases:78,   lyRev:12400,   cbRate:7.2,  lastOrder:38, compliance:0, monthlyFee:850,  startDate:"2022-04-01", am:"JB", category:"other",   brands:3, depRatio:0.72, contractType:"annual",  complexity:2, country:"Ireland",   strategic:false },
  { id:"C13", name:"Kinsale",               ytdRev:8960,    ytdCases:56,   lyCases:44,   lyRev:6800,    cbRate:3.5,  lastOrder:21, compliance:0, monthlyFee:700,  startDate:"2023-11-01", am:"JB", category:"other",   brands:1, depRatio:0.88, contractType:"monthly", complexity:1, country:"Ireland",   strategic:false },
  { id:"C14", name:"Staibano",              ytdRev:1250,    ytdCases:12,   lyCases:42,   lyRev:4800,    cbRate:11.2, lastOrder:61, compliance:2, monthlyFee:700,  startDate:"2022-03-01", am:"JB", category:"other",   brands:2, depRatio:0.52, contractType:"monthly", complexity:2, country:"USA",       strategic:false },
  { id:"C15", name:"Hella Cocktail Co.",    ytdRev:5840,    ytdCases:38,   lyCases:62,   lyRev:9200,    cbRate:9.4,  lastOrder:44, compliance:1, monthlyFee:850,  startDate:"2022-07-15", am:"JB", category:"rtd",     brands:3, depRatio:0.65, contractType:"monthly", complexity:3, country:"USA",       strategic:false },
  { id:"C16", name:"Hughes Bros",           ytdRev:6200,    ytdCases:41,   lyCases:36,   lyRev:5100,    cbRate:4.8,  lastOrder:16, compliance:0, monthlyFee:700,  startDate:"2023-04-01", am:"JB", category:"other",   brands:2, depRatio:0.91, contractType:"monthly", complexity:1, country:"Australia", strategic:false },
  { id:"C17", name:"Okavango Gin",          ytdRev:7820,    ytdCases:52,   lyCases:40,   lyRev:5900,    cbRate:5.5,  lastOrder:24, compliance:0, monthlyFee:700,  startDate:"2023-08-01", am:"JB", category:"gin",     brands:2, depRatio:0.83, contractType:"monthly", complexity:1, country:"UK",        strategic:false },
  { id:"C18", name:"The Slushie Guys LLC",  ytdRev:4620,    ytdCases:28,   lyCases:38,   lyRev:6100,    cbRate:8.9,  lastOrder:55, compliance:1, monthlyFee:700,  startDate:"2023-05-01", am:"JB", category:"rtd",     brands:2, depRatio:0.60, contractType:"monthly", complexity:2, country:"USA",       strategic:false },
  { id:"C19", name:"Defy Wine Ltd",         ytdRev:3890,    ytdCases:24,   lyCases:33,   lyRev:5200,    cbRate:6.2,  lastOrder:48, compliance:0, monthlyFee:700,  startDate:"2023-02-01", am:"JB", category:"other",   brands:1, depRatio:0.74, contractType:"monthly", complexity:1, country:"UK",        strategic:false },
  { id:"C20", name:"Dos Cachorros",         ytdRev:2640,    ytdCases:18,   lyCases:22,   lyRev:3100,    cbRate:7.8,  lastOrder:67, compliance:1, monthlyFee:700,  startDate:"2024-03-01", am:"JB", category:"tequila", brands:1, depRatio:0.58, contractType:"monthly", complexity:1, country:"Mexico",    strategic:false },
  { id:"C21", name:"Rockwood and Hines",    ytdRev:6372,    ytdCases:44,   lyCases:36,   lyRev:5000,    cbRate:4.1,  lastOrder:19, compliance:0, monthlyFee:700,  startDate:"2024-02-01", am:"JB", category:"other",   brands:1, depRatio:0.89, contractType:"monthly", complexity:1, country:"USA",       strategic:false },
  { id:"C22", name:"Veda",                  ytdRev:4752,    ytdCases:32,   lyCases:26,   lyRev:3800,    cbRate:5.0,  lastOrder:33, compliance:0, monthlyFee:700,  startDate:"2024-06-01", am:"JB", category:"other",   brands:1, depRatio:0.81, contractType:"monthly", complexity:1, country:"Japan",     strategic:false },
  { id:"C23", name:"Vieroma",               ytdRev:735,     ytdCases:5,    lyCases:14,   lyRev:2100,    cbRate:12.1, lastOrder:88, compliance:2, monthlyFee:700,  startDate:"2023-01-01", am:"JB", category:"other",   brands:1, depRatio:0.41, contractType:"monthly", complexity:1, country:"Brazil",    strategic:false },
];

// ── Contract registry (notice period + exit fees per client) ──────────────
const HM_CONTRACTS = {
  C01: { type:"annual",  exitFee:7500, noticeMos:5, endDate:"2026-12-31", autoRenew:true  },
  C02: { type:"annual",  exitFee:7500, noticeMos:5, endDate:"2026-09-30", autoRenew:true  },
  C03: { type:"annual",  exitFee:7500, noticeMos:5, endDate:"2026-07-20", autoRenew:false },
  C04: { type:"annual",  exitFee:5000, noticeMos:5, endDate:"2027-03-09", autoRenew:true  },
  C05: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C06: { type:"annual",  exitFee:5000, noticeMos:5, endDate:"2026-06-12", autoRenew:true  },
  C07: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C08: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C09: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C10: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C11: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C12: { type:"annual",  exitFee:5000, noticeMos:5, endDate:"2026-04-01", autoRenew:false },
  C13: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C14: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C15: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C16: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C17: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C18: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C19: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C20: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C21: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C22: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
  C23: { type:"monthly", exitFee:5000, noticeMos:5, endDate:null,         autoRenew:false },
};

// ── Closed / Won log — new clients signed per year ────────────────────────
const HM_CLOSED_WON = [
  { year:2020, signed:["Drifter Spirits"],                                                     count:1, avgRetainer:1200 },
  { year:2021, signed:["Rare Character","Saint Luna","Tra Kal"],                               count:3, avgRetainer:967  },
  { year:2022, signed:["Oliana Experience","BITE Drinks","Run The Jewels",
                        "Irish Spirit Brands","Hella Cocktail Co.","Lot 001","WD O'Connell"],  count:7, avgRetainer:793  },
  { year:2023, signed:["E A Worldwide Traders","Cardenwe","Terres Blanches","Kinsale",
                        "Staibano","Vieroma","Okavango Gin","Defy Wine Ltd",
                        "The Slushie Guys LLC"],                                                count:9, avgRetainer:756  },
  { year:2024, signed:["Snow Tequila","Dos Cachorros","Rockwood and Hines","Veda"],             count:4, avgRetainer:725  },
  { year:2025, signed:[],                                                                       count:0, avgRetainer:0    },
  { year:2026, signed:[],                                                                       count:0, avgRetainer:0    },
];

// ── Churn log — lost clients with reasons ────────────────────────────────
const HM_CHURN_LOG = [
  { id:"CH01", name:"Saint Luna",  startDate:"2021-04-09", churnDate:"2024-09-01", cohortYr:2021,
    reason:"Budget",           reasonDetail:"Founder decided to manage distribution in-house to cut costs.",
    lastRetainer:900, exitFeePaid:false, exitFeeAmt:0, am:"OC", reactivation:"Medium" },
  { id:"CH02", name:"BITE Drinks", startDate:"2022-08-19", churnDate:"2024-03-01", cohortYr:2022,
    reason:"Strategic Pivot",  reasonDetail:"Brand pivoted to DTC model, paused wholesale distribution.",
    lastRetainer:700, exitFeePaid:false, exitFeeAmt:0, am:"JB", reactivation:"High"   },
];

// ── Brand forecast (2026 full year, Jan–Mar actual, Apr–Dec projected) ───
const HM_BRAND_FORECAST = [
  // Client C01 — Rare Character (flagship, 8 brands aggregated)
  { clientId:"C01", clientName:"Rare Character",    brand:"Rare Character Portfolio",
    actualCases:4301, actualRev:1590072, forecastCases:19000, forecastRev:7100000,
    confidence:"High", am:"OC", ytdMonths:3 },
  // Client C02 — Oliana
  { clientId:"C02", clientName:"Oliana Experience", brand:"Oliana Portfolio",
    actualCases:845,  actualRev:140158,  forecastCases:3800,  forecastRev:640000,
    confidence:"Medium", am:"JB", ytdMonths:3 },
  // Client C03 — Drifter Spirits (broken by brand)
  { clientId:"C03", clientName:"Drifter Spirits",   brand:"Avuá Cachaça",
    actualCases:586,  actualRev:83574,   forecastCases:2100,  forecastRev:298000,
    confidence:"Medium", am:"OC", ytdMonths:3 },
  { clientId:"C03", clientName:"Drifter Spirits",   brand:"Paladar Tequila",
    actualCases:205,  actualRev:29310,   forecastCases:1100,  forecastRev:157000,
    confidence:"Low",    am:"OC", ytdMonths:3 },
  { clientId:"C03", clientName:"Drifter Spirits",   brand:"Svöl Aquavit",
    actualCases:147,  actualRev:21067,   forecastCases:550,   forecastRev:78000,
    confidence:"Medium", am:"OC", ytdMonths:3 },
  { clientId:"C03", clientName:"Drifter Spirits",   brand:"Choco Smooth / Other",
    actualCases:39,   actualRev:5617,    forecastCases:200,   forecastRev:28600,
    confidence:"Low",    am:"OC", ytdMonths:3 },
  // Client C04 — Terres Blanches
  { clientId:"C04", clientName:"Terres Blanches",   brand:"Terres Blanches Portfolio",
    actualCases:823,  actualRev:85705,   forecastCases:3500,  forecastRev:365000,
    confidence:"High",   am:"OC", ytdMonths:3 },
  // Client C05 — Lot 001
  { clientId:"C05", clientName:"Lot 001",            brand:"Lot 001 Portfolio",
    actualCases:319,  actualRev:70602,   forecastCases:1400,  forecastRev:310000,
    confidence:"Medium", am:"OC", ytdMonths:3 },
  // Client C06 — EA Worldwide
  { clientId:"C06", clientName:"E A Worldwide Traders", brand:"EA Worldwide Portfolio",
    actualCases:1258, actualRev:52836,   forecastCases:5200,  forecastRev:218000,
    confidence:"High",   am:"JB", ytdMonths:3 },
  // Remaining JB clients aggregated
  { clientId:"JB_OTHER", clientName:"Other JB Clients (14)", brand:"Combined Portfolio",
    actualCases:918,  actualRev:149487,  forecastCases:4000,  forecastRev:650000,
    confidence:"Medium", am:"JB", ytdMonths:3 },
];

// ── Brand Registry — 2-level hierarchy: Client → Brands ─────────────────
// stage: ACTIVE | LAUNCH | WATCH | INACTIVE
// category mirrors client categories for filtering
const HM_BRANDS = [
  // C01 — Rare Character (8 brands)
  { id:"B001", clientId:"C01", name:"Rare Character Small Batch",  category:"bourbon",  am:"OC", ytdCases:1420, ytdRev:524260, lyCases:1180, stage:"ACTIVE",   skus:4 },
  { id:"B002", clientId:"C01", name:"Rare Character Cask Strength",category:"bourbon",  am:"OC", ytdCases:980,  ytdRev:361860, lyCases:820,  stage:"ACTIVE",   skus:3 },
  { id:"B003", clientId:"C01", name:"Rare Character Single Barrel", category:"bourbon",  am:"OC", ytdCases:760,  ytdRev:280720, lyCases:640,  stage:"ACTIVE",   skus:6 },
  { id:"B004", clientId:"C01", name:"Rare Character Rye",           category:"other",    am:"OC", ytdCases:620,  ytdRev:228980, lyCases:520,  stage:"ACTIVE",   skus:2 },
  { id:"B005", clientId:"C01", name:"Rare Character Rum",           category:"other",    am:"OC", ytdCases:280,  ytdRev:103360, lyCases:310,  stage:"WATCH",    skus:2 },
  { id:"B006", clientId:"C01", name:"Rare Character Wheat",         category:"bourbon",  am:"OC", ytdCases:160,  ytdRev:59040,  lyCases:190,  stage:"WATCH",    skus:1 },
  { id:"B007", clientId:"C01", name:"Rare Character Gin",           category:"gin",      am:"OC", ytdCases:55,   ytdRev:20295,  lyCases:80,   stage:"WATCH",    skus:2 },
  { id:"B008", clientId:"C01", name:"Rare Character Limited",       category:"bourbon",  am:"OC", ytdCases:26,   ytdRev:11557,  lyCases:60,   stage:"LAUNCH",   skus:1 },

  // C02 — Oliana Experience (4 brands)
  { id:"B009", clientId:"C02", name:"Oliana Rosé",                  category:"other",    am:"JB", ytdCases:380,  ytdRev:63080,  lyCases:290,  stage:"ACTIVE",   skus:2 },
  { id:"B010", clientId:"C02", name:"Oliana Red Blend",             category:"other",    am:"JB", ytdCases:260,  ytdRev:43160,  lyCases:200,  stage:"ACTIVE",   skus:2 },
  { id:"B011", clientId:"C02", name:"Oliana White",                 category:"other",    am:"JB", ytdCases:155,  ytdRev:25730,  lyCases:140,  stage:"ACTIVE",   skus:1 },
  { id:"B012", clientId:"C02", name:"Oliana Sparkling",             category:"other",    am:"JB", ytdCases:50,   ytdRev:8290,   lyCases:50,   stage:"WATCH",    skus:1 },

  // C03 — Drifter Spirits (6 brands)
  { id:"B013", clientId:"C03", name:"Avuá Cachaça",                 category:"cachaca",  am:"OC", ytdCases:586,  ytdRev:83574,  lyCases:1186, stage:"ACTIVE",   skus:5 },
  { id:"B014", clientId:"C03", name:"Paladar Tequila",              category:"tequila",  am:"OC", ytdCases:205,  ytdRev:29310,  lyCases:415,  stage:"ACTIVE",   skus:4 },
  { id:"B015", clientId:"C03", name:"Svöl Aquavit",                 category:"aquavit",  am:"OC", ytdCases:147,  ytdRev:21067,  lyCases:297,  stage:"ACTIVE",   skus:3 },
  { id:"B016", clientId:"C03", name:"Choco Smooth",                 category:"other",    am:"OC", ytdCases:25,   ytdRev:3575,   lyCases:55,   stage:"WATCH",    skus:2 },
  { id:"B017", clientId:"C03", name:"Drifter Cane & Corn",         category:"other",    am:"OC", ytdCases:9,    ytdRev:1287,   lyCases:18,   stage:"WATCH",    skus:1 },
  { id:"B018", clientId:"C03", name:"Gajardo",                      category:"other",    am:"OC", ytdCases:5,    ytdRev:755,    lyCases:9,    stage:"INACTIVE", skus:1 },

  // C04 — Terres Blanches (3 brands)
  { id:"B019", clientId:"C04", name:"Terres Blanches Chablis",      category:"other",    am:"OC", ytdCases:410,  ytdRev:42640,  lyCases:350,  stage:"ACTIVE",   skus:3 },
  { id:"B020", clientId:"C04", name:"Terres Blanches Sancerre",     category:"other",    am:"OC", ytdCases:280,  ytdRev:29120,  lyCases:240,  stage:"ACTIVE",   skus:2 },
  { id:"B021", clientId:"C04", name:"Terres Blanches Muscadet",     category:"other",    am:"OC", ytdCases:133,  ytdRev:13832,  lyCases:120,  stage:"ACTIVE",   skus:1 },

  // C05 — Lot 001 (2 brands)
  { id:"B022", clientId:"C05", name:"Lot 001 Bourbon",              category:"bourbon",  am:"OC", ytdCases:210,  ytdRev:46620,  lyCases:195,  stage:"ACTIVE",   skus:3 },
  { id:"B023", clientId:"C05", name:"Lot 001 Rye",                  category:"other",    am:"OC", ytdCases:109,  ytdRev:24178,  lyCases:95,   stage:"ACTIVE",   skus:2 },

  // C06 — E A Worldwide (5 brands)
  { id:"B024", clientId:"C06", name:"EA Worldwide Mezcal",          category:"tequila",  am:"JB", ytdCases:520,  ytdRev:21840,  lyCases:440,  stage:"ACTIVE",   skus:4 },
  { id:"B025", clientId:"C06", name:"EA Worldwide Rum",             category:"other",    am:"JB", ytdCases:340,  ytdRev:14280,  lyCases:310,  stage:"ACTIVE",   skus:3 },
  { id:"B026", clientId:"C06", name:"EA Worldwide Vodka",           category:"other",    am:"JB", ytdCases:220,  ytdRev:9240,   lyCases:200,  stage:"ACTIVE",   skus:2 },
  { id:"B027", clientId:"C06", name:"EA Worldwide Gin",             category:"gin",      am:"JB", ytdCases:118,  ytdRev:4956,   lyCases:100,  stage:"ACTIVE",   skus:2 },
  { id:"B028", clientId:"C06", name:"EA Worldwide Limited",         category:"other",    am:"JB", ytdCases:60,   ytdRev:2520,   lyCases:50,   stage:"LAUNCH",   skus:1 },

  // C07 — Run The Jewels (2 brands)
  { id:"B029", clientId:"C07", name:"RTJ American Whiskey",         category:"other",    am:"JB", ytdCases:310,  ytdRev:17980,  lyCases:370,  stage:"WATCH",    skus:2 },
  { id:"B030", clientId:"C07", name:"RTJ Cocktail RTD",             category:"rtd",      am:"JB", ytdCases:145,  ytdRev:8410,   lyCases:150,  stage:"WATCH",    skus:3 },

  // C08 — Cardenwe (2 brands)
  { id:"B031", clientId:"C08", name:"Cardenwe Añejo",               category:"tequila",  am:"JB", ytdCases:95,   ytdRev:15865,  lyCases:72,   stage:"ACTIVE",   skus:2 },
  { id:"B032", clientId:"C08", name:"Cardenwe Blanco",              category:"tequila",  am:"JB", ytdCases:57,   ytdRev:9519,   lyCases:46,   stage:"ACTIVE",   skus:1 },

  // C09 — Snow Tequila (3 brands)
  { id:"B033", clientId:"C09", name:"Snow Blanco",                  category:"tequila",  am:"JB", ytdCases:72,   ytdRev:12168,  lyCases:58,   stage:"ACTIVE",   skus:2 },
  { id:"B034", clientId:"C09", name:"Snow Reposado",                category:"tequila",  am:"JB", ytdCases:44,   ytdRev:7436,   lyCases:36,   stage:"ACTIVE",   skus:1 },
  { id:"B035", clientId:"C09", name:"Snow Cristalino",              category:"tequila",  am:"JB", ytdCases:20,   ytdRev:3380,   lyCases:16,   stage:"LAUNCH",   skus:1 },

  // C10–C23: smaller clients, 1–2 brands each (representative sample)
  { id:"B036", clientId:"C10", name:"WD O'Connell All Grain",       category:"other",    am:"JB", ytdCases:50,   ytdRev:8000,   lyCases:42,   stage:"ACTIVE",   skus:3 },
  { id:"B037", clientId:"C10", name:"WD O'Connell Pot Still",       category:"other",    am:"JB", ytdCases:27,   ytdRev:4320,   lyCases:23,   stage:"ACTIVE",   skus:1 },
  { id:"B038", clientId:"C11", name:"Tra Kal Malbec",               category:"other",    am:"JB", ytdCases:60,   ytdRev:7500,   lyCases:72,   stage:"WATCH",    skus:2 },
  { id:"B039", clientId:"C11", name:"Tra Kal Cabernet",             category:"other",    am:"JB", ytdCases:36,   ytdRev:4500,   lyCases:43,   stage:"WATCH",    skus:1 },
  { id:"B040", clientId:"C12", name:"Irish Spirit Brands Whiskey",  category:"other",    am:"JB", ytdCases:38,   ytdRev:6270,   lyCases:50,   stage:"WATCH",    skus:2 },
  { id:"B041", clientId:"C12", name:"Irish Spirit Brands Gin",      category:"gin",      am:"JB", ytdCases:15,   ytdRev:2475,   lyCases:20,   stage:"WATCH",    skus:1 },
  { id:"B042", clientId:"C12", name:"Irish Spirit Brands Cream",    category:"other",    am:"JB", ytdCases:7,    ytdRev:980,    lyCases:8,    stage:"INACTIVE", skus:1 },
  { id:"B043", clientId:"C13", name:"Kinsale Irish Whiskey",        category:"other",    am:"JB", ytdCases:56,   ytdRev:8960,   lyCases:44,   stage:"ACTIVE",   skus:2 },
  { id:"B044", clientId:"C14", name:"Staibano Limoncello",          category:"other",    am:"JB", ytdCases:8,    ytdRev:840,    lyCases:28,   stage:"WATCH",    skus:2 },
  { id:"B045", clientId:"C14", name:"Staibano Amaro",               category:"other",    am:"JB", ytdCases:4,    ytdRev:420,    lyCases:14,   stage:"INACTIVE", skus:1 },
  { id:"B046", clientId:"C15", name:"Hella Bitters & Soda",         category:"rtd",      am:"JB", ytdCases:22,   ytdRev:3520,   lyCases:38,   stage:"WATCH",    skus:4 },
  { id:"B047", clientId:"C15", name:"Hella Cocktail Mixers",        category:"rtd",      am:"JB", ytdCases:10,   ytdRev:1600,   lyCases:16,   stage:"WATCH",    skus:3 },
  { id:"B048", clientId:"C15", name:"Hella Shrubs",                 category:"rtd",      am:"JB", ytdCases:6,    ytdRev:960,    lyCases:8,    stage:"INACTIVE", skus:2 },
  { id:"B049", clientId:"C16", name:"Hughes Bros Whiskey",          category:"other",    am:"JB", ytdCases:27,   ytdRev:4050,   lyCases:24,   stage:"ACTIVE",   skus:2 },
  { id:"B050", clientId:"C16", name:"Hughes Bros Gin",              category:"gin",      am:"JB", ytdCases:14,   ytdRev:2100,   lyCases:12,   stage:"ACTIVE",   skus:1 },
  { id:"B051", clientId:"C17", name:"Okavango Original Dry Gin",    category:"gin",      am:"JB", ytdCases:36,   ytdRev:5400,   lyCases:28,   stage:"ACTIVE",   skus:2 },
  { id:"B052", clientId:"C17", name:"Okavango Pink Gin",            category:"gin",      am:"JB", ytdCases:16,   ytdRev:2400,   lyCases:12,   stage:"LAUNCH",   skus:1 },
  { id:"B053", clientId:"C18", name:"Slushie Co. Classic",          category:"rtd",      am:"JB", ytdCases:18,   ytdRev:3060,   lyCases:26,   stage:"WATCH",    skus:3 },
  { id:"B054", clientId:"C18", name:"Slushie Co. Spiked",           category:"rtd",      am:"JB", ytdCases:10,   ytdRev:1700,   lyCases:12,   stage:"WATCH",    skus:2 },
  { id:"B055", clientId:"C19", name:"Defy Red",                     category:"other",    am:"JB", ytdCases:14,   ytdRev:2240,   lyCases:20,   stage:"WATCH",    skus:2 },
  { id:"B056", clientId:"C19", name:"Defy White",                   category:"other",    am:"JB", ytdCases:10,   ytdRev:1600,   lyCases:13,   stage:"WATCH",    skus:1 },
  { id:"B057", clientId:"C20", name:"Dos Cachorros Reposado",       category:"tequila",  am:"JB", ytdCases:12,   ytdRev:1800,   lyCases:15,   stage:"WATCH",    skus:2 },
  { id:"B058", clientId:"C20", name:"Dos Cachorros Blanco",         category:"tequila",  am:"JB", ytdCases:6,    ytdRev:900,    lyCases:7,    stage:"LAUNCH",   skus:1 },
  { id:"B059", clientId:"C21", name:"Rockwood & Hines Bourbon",     category:"bourbon",  am:"JB", ytdCases:44,   ytdRev:6372,   lyCases:36,   stage:"ACTIVE",   skus:3 },
  { id:"B060", clientId:"C22", name:"Veda Gin",                     category:"gin",      am:"JB", ytdCases:32,   ytdRev:4752,   lyCases:26,   stage:"ACTIVE",   skus:2 },
  { id:"B061", clientId:"C23", name:"Vieroma Amaro",                category:"other",    am:"JB", ytdCases:5,    ytdRev:735,    lyCases:14,   stage:"WATCH",    skus:1 },
];

// Derived: brands per client lookup
const BRANDS_BY_CLIENT = HM_BRANDS.reduce((acc, b) => {
  if (!acc[b.clientId]) acc[b.clientId] = [];
  acc[b.clientId].push(b);
  return acc;
}, {});

const BRAND_STAGE_CFG = {
  ACTIVE:   { bg:GREEN_BG,  text:GREEN,  border:"#86EFAC", label:"Active"   },
  LAUNCH:   { bg:"#EFF6FF", text:"#1D4ED8", border:"#BFDBFE", label:"Launch" },
  WATCH:    { bg:AMBER_BG,  text:AMBER,  border:"#FCD34D", label:"Watch"   },
  INACTIVE: { bg:CARD_BG,   text:GRAY,   border:BORDER,    label:"Inactive" },
};


// ── Health Scoring ─────────────────────────────────────────────────────────
function clientHealthScore(c) {
  const ytdMos = 3; // Jan–Mar
  const yoy = c.lyRev > 0 ? ((c.ytdRev - (c.lyRev * ytdMos / 12)) / (c.lyRev * ytdMos / 12)) * 100 : 0;
  let score = 0;
  // Revenue trend (30 pts)
  score += yoy > 10 ? 30 : yoy > 0 ? 22 : yoy > -10 ? 12 : 5;
  // Order recency (20 pts)
  score += c.lastOrder < 30 ? 20 : c.lastOrder < 60 ? 14 : c.lastOrder < 90 ? 6 : 0;
  // Chargeback rate (20 pts)
  score += c.cbRate < 5 ? 20 : c.cbRate < 8 ? 14 : c.cbRate < 12 ? 8 : 2;
  // Compliance (15 pts)
  score += c.compliance === 0 ? 15 : c.compliance === 1 ? 8 : 2;
  // Depletion ratio (15 pts)
  score += c.depRatio > 0.85 ? 15 : c.depRatio > 0.70 ? 10 : 4;
  return Math.min(100, score);
}

const HEALTH_CFG = {
  HEALTHY:  { min:80, bg:GREEN_BG,  text:GREEN,  border:"#86EFAC", label:"Healthy",  dot:"#22C55E" },
  STABLE:   { min:60, bg:"#E8F4F5", text:TEAL,   border:"#B2D8DC", label:"Stable",   dot:TEAL2    },
  AT_RISK:  { min:40, bg:AMBER_BG,  text:AMBER,  border:"#FCD34D", label:"At Risk",  dot:AMBER    },
  CRITICAL: { min:0,  bg:RED_BG,    text:RED,    border:"#FCA5A5", label:"Critical", dot:RED      },
};

function getHealth(score) {
  if (score >= 80) return "HEALTHY";
  if (score >= 60) return "STABLE";
  if (score >= 40) return "AT_RISK";
  return "CRITICAL";
}

// Enrich client data with computed fields
const HM_CLIENTS_ENRICHED = HM_CLIENTS.map(c => {
  const score = clientHealthScore(c);
  const health = getHealth(score);
  const lyYtdRev = (c.lyRev / 12) * 3;
  const yoyPct = lyYtdRev > 0 ? ((c.ytdRev - lyYtdRev) / lyYtdRev) * 100 : null;
  const pctPortfolio = (c.ytdRev / HM_PORTFOLIO.ytdRev) * 100;
  const am = ACCOUNT_MANAGERS.find(a => a.id === c.am);
  return { ...c, score, health, yoyPct, pctPortfolio, am };
});

function AMAvatar({ am, size=22 }) {
  if (!am) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
      <div style={{ width:size, height:size, borderRadius:"50%", background:am.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.4, fontWeight:700, color:WHITE, flexShrink:0 }}>
        {am.avatar}
      </div>
      <span style={{ fontSize:10.5, color:DARK, fontWeight:500 }}>{am.name}</span>
    </div>
  );
}

function HelsmanDashboard() {
  const [tab,        setTab]      = useState("pnl");
  const [amFilt,     setAmFilt]   = useState("All");
  const [sortCol,    setSortCol]  = useState("totalYTD");
  const [sortDir,    setSortDir]  = useState("desc");
  const [selectedId, setSelected] = useState(null);
  const [growthYear, setGrowthYear] = useState(null); // drill-down year on Growth tab
  const [fcFilter,   setFcFilter] = useState("All"); // AM filter on Forecast tab
  const [brandFilt,  setBrandFilt] = useState("All"); // filter on Brands tab
  const [brandClient,setBrandClient]=useState(null);  // client drill-down on Brands tab
  const [brandSort,  setBrandSort] = useState("ytdCases");
  const [brandDir,   setBrandDir]  = useState("desc");
  const [freightMarkup,  setFreightMarkup]  = useState(DEFAULT_TRANSPORT_MARKUP);
  const [freightPerCase, setFreightPerCase] = useState(DEFAULT_FREIGHT_PER_CASE);

  // ── Fee state ─────────────────────────────────────────────────────────
  const [monthlyFees, setMonthlyFees] = useState(
    () => Object.fromEntries(HM_CLIENTS.map(c => [c.id, c.monthlyFee]))
  );
  const [caseFeeRate, setCaseFeeRate] = useState(DEFAULT_CASE_FEE);
  const [feeEditId,   setFeeEditId]   = useState(null);
  const [feeDraft,    setFeeDraft]    = useState("");

  // ── Revenue helpers ───────────────────────────────────────────────────
  const retYTD   = c => (monthlyFees[c.id] || 0) * YTD_MONTHS;
  const caseYTD  = c => c.ytdCases * caseFeeRate;
  const totalYTD = c => retYTD(c) + caseYTD(c);
  const annRate  = c => (monthlyFees[c.id] || 0) * 12 + (c.ytdCases / YTD_MONTHS * 12) * caseFeeRate;

  const portRet     = HM_CLIENTS_ENRICHED.reduce((s,c) => s + retYTD(c),   0);
  const portCase    = HM_CLIENTS_ENRICHED.reduce((s,c) => s + caseYTD(c),  0);
  const portTotal   = portRet + portCase;
  const portAnn     = HM_CLIENTS_ENRICHED.reduce((s,c) => s + annRate(c),  0);
  const portCases   = HM_PORTFOLIO.ytdCases;
  const portCasesLY = HM_PORTFOLIO.lyYtdCases;
  const portFreight  = portCases * freightPerCase * freightMarkup;
  const portGrandTotal = portTotal + portFreight;
  const getTier = c => accountTier(c);
  const caseGrowth  = ((portCases - portCasesLY) / portCasesLY) * 100;
  const revPerCase  = portCases > 0 ? portTotal / portCases : 0;

  // ── Case velocity YoY ─────────────────────────────────────────────────
  const caseVelYoy = c => {
    const lyPace = (c.lyCases / 12) * YTD_MONTHS;
    if (!lyPace) return null;
    return ((c.ytdCases - lyPace) / lyPace) * 100;
  };

  // ── Contract exposure ─────────────────────────────────────────────────
  const contractExposure = c => {
    const ct = HM_CONTRACTS[c.id];
    if (!ct) return 0;
    if (ct.type === "annual" && ct.endDate && new Date(ct.endDate) > new Date()) return ct.exitFee;
    return (monthlyFees[c.id] || 0) * ct.noticeMos;
  };

  // Retainer-at-risk: monthly contracts that are At Risk/Critical
  const atRiskClients = HM_CLIENTS_ENRICHED.filter(
    c => HM_CONTRACTS[c.id]?.type === "monthly" && (c.health === "AT_RISK" || c.health === "CRITICAL")
  );
  const retainerAtRisk      = atRiskClients.reduce((s,c) => s + (monthlyFees[c.id]||0) * 12, 0);
  const monthlyRetainerAtRisk = atRiskClients.length;

  // Annual contracts expiring within 90 days
  const contractsExpiringSoon = Object.entries(HM_CONTRACTS).filter(([id, ct]) => {
    if (ct.type !== "annual" || !ct.endDate) return false;
    const daysLeft = (new Date(ct.endDate) - new Date()) / 86400000;
    return daysLeft > 0 && daysLeft <= 90;
  });

  // ── AM capacity ───────────────────────────────────────────────────────
  const amCapacity = ACCOUNT_MANAGERS.map(am => {
    const clients   = HM_CLIENTS_ENRICHED.filter(c => c.am.id === am.id);
    const amRevYTD  = clients.reduce((s,c) => s + totalYTD(c), 0);
    const amRevAnn  = clients.reduce((s,c) => s + annRate(c),  0);
    const cost      = HM_AM_COSTS[am.id];
    const totalCost = cost ? cost.annual * cost.overhead : 80000;
    const amCases   = clients.reduce((s,c) => s + c.ytdCases, 0);
    const complexity= clients.reduce((s,c) => s + c.complexity, 0) / (clients.length || 1);
    const marginAnn = amRevAnn - totalCost;
    const marginPct = amRevAnn > 0 ? (marginAnn / amRevAnn) * 100 : 0;
    return { ...am, clients, clientCount: clients.length, amRevYTD, amRevAnn, totalCost, amCases, complexity, marginAnn, marginPct };
  });

  // ── Cohort retention ──────────────────────────────────────────────────
  const cohortYears = [2020, 2021, 2022, 2023, 2024];
  const cohorts = cohortYears.map(yr => {
    const activeCt  = HM_CLIENTS.filter(c => parseInt(c.startDate.slice(0,4)) === yr).length;
    const churnedCt = HM_CHURN_LOG.filter(c => c.cohortYr === yr).length;
    const started   = activeCt + churnedCt;
    const ret12     = started > 0 ? Math.round((activeCt / started) * 100) : 100;
    return { yr, started, active: activeCt, churned: churnedCt, ret12 };
  });

  // ── Growth / closed-vs-lost data ──────────────────────────────────────
  const growthData = HM_CLOSED_WON.map(row => ({
    ...row,
    lost: HM_CHURN_LOG.filter(c => new Date(c.churnDate).getFullYear() === row.year).length,
    lostRevYr: HM_CHURN_LOG.filter(c => new Date(c.churnDate).getFullYear() === row.year)
               .reduce((s,c) => s + c.lastRetainer * 12, 0),
    netNew: row.count - HM_CHURN_LOG.filter(c => new Date(c.churnDate).getFullYear() === row.year).length,
  }));

  // ── Brand forecast totals ─────────────────────────────────────────────
  const fcFiltered = fcFilter === "All" ? HM_BRAND_FORECAST
    : HM_BRAND_FORECAST.filter(b => b.am === fcFilter);
  const fcTotalForecast = fcFiltered.reduce((s,b) => s + b.forecastRev, 0);
  const fcTotalActual   = fcFiltered.reduce((s,b) => s + b.actualRev,   0);
  const fcRemaining     = fcTotalForecast - fcTotalActual;
  const fcPct           = fcTotalForecast > 0 ? (fcTotalActual / fcTotalForecast) * 100 : 0;

  // ── Chart data ────────────────────────────────────────────────────────
  const HM_REV_MO = HM_MONTHLY.map(m => ({
    ...m,
    retainer:  m.rev2026 > 0 ? portRet / YTD_MONTHS : 0,
    caseFee:   m.rev2026 > 0 ? (m.rev2026 / HM_PORTFOLIO.ytdRev) * portCases * caseFeeRate : 0,
    transport: m.rev2026 > 0 ? (m.rev2026 / HM_PORTFOLIO.ytdRev) * portCases * freightPerCase * freightMarkup : 0,
  }));
  
  // Account tier breakdown
  const tierCounts = ["Strategic","Big","Medium","Small","Micro"].map(t => ({
    tier: t,
    cfg: ACCOUNT_TIER_CFG[t],
    clients: HM_CLIENTS_ENRICHED.filter(c => getTier(c) === t),
    cases: HM_CLIENTS_ENRICHED.filter(c => getTier(c) === t).reduce((s,c)=>s+c.ytdCases,0),
    rev: HM_CLIENTS_ENRICHED.filter(c => getTier(c) === t).reduce((s,c)=>s+totalYTD(c),0),
  }));

  // ── Helpers ───────────────────────────────────────────────────────────
  function startFeeEdit(c) { setFeeEditId(c.id); setFeeDraft(String(monthlyFees[c.id] || "")); }
  function saveFeeEdit(id) {
    const val = parseInt(feeDraft.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(val) && val >= 0) setMonthlyFees(p => ({ ...p, [id]: val }));
    setFeeEditId(null);
  }
  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  const SortTH = ({ col, children, right }) => (
    <th onClick={() => handleSort(col)}
      style={{ background:TEAL, color:WHITE, padding:"8px 12px", textAlign:right?"right":"left",
        fontSize:11, fontWeight:600, whiteSpace:"nowrap", cursor:"pointer", userSelect:"none" }}>
      {children}{sortCol===col?(sortDir==="desc"?" ↓":" ↑"):""}
    </th>
  );

  const enrichedSorted = HM_CLIENTS_ENRICHED
    .filter(c => amFilt === "All" || c.am.id === amFilt)
    .map(c => ({ ...c, _ret:retYTD(c), _case:caseYTD(c), _total:totalYTD(c), _ann:annRate(c), _velYoy:caseVelYoy(c) }))
    .sort((a,b) => {
      const km = { totalYTD:"_total", caseYTD:"_case", retYTD:"_ret", annual:"_ann", ytdCases:"ytdCases", name:"name", velYoy:"_velYoy" };
      const k  = km[sortCol] || sortCol;
      const va = a[k], vb = b[k];
      if (typeof va === "string") return sortDir==="desc"?vb.localeCompare(va):va.localeCompare(vb);
      return sortDir==="desc"?(vb||0)-(va||0):(va||0)-(vb||0);
    });

  const selClient = selectedId ? enrichedSorted.find(c => c.id === selectedId) : null;

  const AMFilter = ({ value, onChange }) => (
    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
      <span style={{ fontSize:10, color:GRAY, fontWeight:600 }}>AM:</span>
      {["All",...ACCOUNT_MANAGERS.map(a=>a.id)].map(id => {
        const am = ACCOUNT_MANAGERS.find(a=>a.id===id);
        return (
          <button key={id} onClick={()=>onChange(id)}
            style={{ padding:"4px 11px", border:`1px solid ${value===id?(am?.color||TEAL):BORDER}`, borderRadius:5,
              background:value===id?`${am?.color||TEAL}18`:WHITE, color:value===id?(am?.color||TEAL):GRAY,
              fontSize:11, fontWeight:value===id?700:400, cursor:"pointer" }}>
            {id==="All"?"All":am?.name.split(" ")[0]}
          </button>
        );
      })}
    </div>
  );

  const CHURN_REASON_COLORS = {
    "Budget":           { bg:"#FEF3C7", color:AMBER  },
    "Strategic Pivot":  { bg:"#EFF6FF", color:"#1D4ED8" },
    "Service Issue":    { bg:RED_BG,    color:RED    },
    "Acquisition":      { bg:GREEN_BG,  color:GREEN  },
    "Moved In-House":   { bg:CARD_BG,   color:GRAY   },
    "Unknown":          { bg:CARD_BG,   color:GRAY   },
  };

  return (
    <div>
      {/* ── Alerts ────────────────────────────────────────────────── */}
      {contractsExpiringSoon.length > 0 && (
        <Alert type="warning">
          <strong>📋 {contractsExpiringSoon.length} Annual Contract{contractsExpiringSoon.length>1?"s":""} Expiring Within 90 Days:</strong>{" "}
          {contractsExpiringSoon.map(([id]) => HM_CLIENTS.find(c=>c.id===id)?.name).join(", ")} — renewal action required.
        </Alert>
      )}
      {monthlyRetainerAtRisk > 0 && (
        <Alert type="warning">
          <strong>⚓ Retainer at Risk:</strong> {monthlyRetainerAtRisk} month-to-month client{monthlyRetainerAtRisk>1?"s":""} rated At Risk or Critical — <strong>{fmt(retainerAtRisk)}/yr</strong> in retainer cancellable on 5-month notice.
        </Alert>
      )}

      {/* ── KPI strip ─────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:14 }}>
        {[
          { l:"Helmsman Rev — YTD",    v:fmt(portGrandTotal), sub:`${fmt(portRet)} ret · ${fmt(portCase)} cases · ${fmt(portFreight)} freight`, color:TEAL,  hi:true  },
          { l:"Annual Run Rate",        v:fmt(portAnn + portFreight/YTD_MONTHS*12), sub:"retainer + case fees + freight",  color:DARK              },
          { l:"Cases YTD",              v:portCases.toLocaleString(undefined,{maximumFractionDigits:0}),
                                        sub:`${caseGrowth>=0?"+":""}${caseGrowth.toFixed(1)}% vs LY`,                     color:caseGrowth>0?GREEN:RED, warn:caseGrowth<-5 },
          { l:"Transport Margin YTD",   v:fmt(portFreight), sub:`${(freightMarkup*100).toFixed(0)}% markup · $${freightPerCase}/case est`, color:"#7C3AED"  },
          { l:"Retainer at Risk",       v:fmt(retainerAtRisk)+"/yr",
                                        sub:`${monthlyRetainerAtRisk} monthly · 5-mo notice`,                              color:monthlyRetainerAtRisk>0?AMBER:GREEN, warn:monthlyRetainerAtRisk>2 },
          { l:"Active Clients",         v:HM_CLIENTS.length, sub:`${HM_PIPELINE.filter(p=>p.stage!=="Churned").length} in pipeline`, color:DARK },
        ].map((k,i) => (
          <div key={i} style={{ background:k.hi?`${TEAL}10`:k.warn?AMBER_BG:WHITE, border:`1px solid ${k.hi?TEAL:k.warn?"#FCD34D":BORDER}`, borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:9.5, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:4 }}>{k.l}</div>
            <div style={{ fontSize:17, fontWeight:800, color:k.color }}>{k.v}</div>
            <div style={{ fontSize:10, color:GRAY, marginTop:2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────── */}
      <div style={{ display:"flex", border:`1px solid ${BORDER}`, borderRadius:"10px 10px 0 0", overflow:"hidden", marginBottom:16, background:WHITE }}>
        {[
          ["pnl",       "📈 P&L"],
          ["cases",     "📦 Case Velocity"],
          ["growth",    "📊 Growth"],
          ["forecast",  "🔮 Forecast"],
          ["pipeline",  "🚀 Pipeline"],
          ["retention", "🔄 Retention"],
          ["capacity",  "⚙️ Capacity"],
          ["brands",    "🏷️ Brands"],
          ["fees",      "💳 Contracts"],
        ].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ flex:1, padding:"9px 6px", border:"none", borderBottom:`3px solid ${tab===v?TEAL:"transparent"}`,
              background:tab===v?`${TEAL}08`:WHITE, color:tab===v?TEAL:GRAY, fontSize:11, fontWeight:tab===v?700:400, cursor:"pointer" }}>
            {l}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB 1 — P&L OVERVIEW
      ══════════════════════════════════════════════════════════════ */}
      {tab==="pnl" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 310px", gap:16, marginBottom:16 }}>
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:DARK }}>Helmsman Revenue — Monthly</div>
                  <div style={{ fontSize:11, color:GRAY }}>Retainer (floor) + Case fees (variable)</div>
                </div>
                <div style={{ display:"flex", gap:12 }}>
                  {[[TEAL,"Retainer"],[TEAL2,"Case Fees"],["#7C3AED","Transport"]].map(([c,l])=>(
                    <div key={l} style={{ display:"flex",alignItems:"center",gap:4,fontSize:10.5,color:GRAY }}>
                      <div style={{ width:8,height:8,borderRadius:2,background:c }}/>{l}
                    </div>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={HM_REV_MO} barCategoryGap="22%" margin={{top:4,right:4,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                  <XAxis dataKey="m" tick={{fontSize:10,fill:GRAY}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize:9,fill:GRAY}} tickFormatter={v=>v>=1000?`$${(v/1000).toFixed(0)}K`:"$0"} axisLine={false} tickLine={false} width={42} />
                  <Tooltip formatter={(v,n)=>[v>0?`$${v.toLocaleString("en-US",{maximumFractionDigits:0})}`:"—",n]} contentStyle={{fontSize:11}} />
                  <Bar dataKey="retainer"  name="Retainer"    stackId="a" fill={TEAL}      radius={[0,0,0,0]} />
                  <Bar dataKey="caseFee"   name="Case Fees"   stackId="a" fill={TEAL2}     radius={[0,0,0,0]} />
                  <Bar dataKey="transport" name="Transport"   stackId="a" fill={"#7C3AED"} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px" }}>
              <div style={{ fontSize:13, fontWeight:700, color:DARK, marginBottom:12 }}>Unit Economics</div>
              <div style={{ background:`${TEAL}0C`, border:`1px solid ${TEAL}33`, borderRadius:8, padding:"11px 13px", marginBottom:10 }}>
                <div style={{ fontSize:9.5, color:TEAL, fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>⚓ Retainer Floor / Month</div>
                <div style={{ fontSize:20, fontWeight:800, color:TEAL }}>{fmt(portRet/YTD_MONTHS)}<span style={{ fontSize:11, fontWeight:400, color:GRAY }}>/mo</span></div>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:8 }}>
                  <div style={{ flex:1, height:6, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                    <div style={{ width:`${(portRet/portTotal*100).toFixed(0)}%`, height:"100%", background:TEAL, borderRadius:99 }}/>
                  </div>
                  <span style={{ fontSize:10, fontWeight:700, color:TEAL }}>{(portRet/portTotal*100).toFixed(0)}% of rev</span>
                </div>
              </div>
              <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderLeft:`3px solid #1D4ED8`, borderRadius:8, padding:"11px 13px", marginBottom:10 }}>
                <div style={{ fontSize:9.5, color:"#1E40AF", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>📦 Helmsman Rev / Case</div>
                <div style={{ fontSize:20, fontWeight:800, color:"#1D4ED8" }}>${revPerCase.toFixed(2)}<span style={{ fontSize:11, fontWeight:400, color:GRAY }}>/case</span></div>
                <div style={{ fontSize:10, color:"#1E40AF", marginTop:6 }}>At 500 clients · 1,000 cs/yr avg → {fmt(500*1000*revPerCase)}/yr</div>
              </div>
              <div style={{ background:CARD_BG, border:`1px solid ${BORDER}`, borderRadius:8, padding:"11px 13px" }}>
                <div style={{ fontSize:9.5, color:GRAY, fontWeight:700, textTransform:"uppercase", marginBottom:6 }}>Case Fee Sensitivity</div>
                {[500,1000,2000,5000].map(n => (
                  <div key={n} style={{ display:"flex", justifyContent:"space-between", padding:"3px 0", borderBottom:`1px solid ${BORDER}`, fontSize:11 }}>
                    <span style={{ color:GRAY }}>+{n.toLocaleString()} cases/yr</span>
                    <span style={{ fontWeight:700, color:DARK }}>+{fmt(n*caseFeeRate)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px" }}>
            <div style={{ fontSize:13, fontWeight:700, color:DARK, marginBottom:12 }}>Revenue & Margin by Account Manager</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              {amCapacity.map(am => (
                <div key={am.id} style={{ border:`1px solid ${BORDER}`, borderRadius:9, overflow:"hidden" }}>
                  <div style={{ background:am.color, padding:"11px 14px", display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:34,height:34,borderRadius:"50%",background:"rgba(255,255,255,0.22)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:WHITE }}>{am.avatar}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:WHITE }}>{am.name}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)" }}>{am.clientCount} clients · {am.amCases.toLocaleString(undefined,{maximumFractionDigits:0})} cases YTD</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:15, fontWeight:800, color:WHITE }}>{fmt(am.amRevAnn)}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)" }}>annual rate</div>
                    </div>
                  </div>
                  <div style={{ padding:"12px 14px", display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
                    {[
                      { l:"Rev YTD",      v:fmt(am.amRevYTD)  },
                      { l:"Annual Rate",  v:fmt(am.amRevAnn)  },
                      { l:"Cases Managed",v:am.amCases.toLocaleString(undefined,{maximumFractionDigits:0}) },
                      { l:"Rev / Client", v:fmt(am.amRevAnn/am.clientCount) },
                    ].map((k,i)=>(
                      <div key={i} style={{ background:CARD_BG, borderRadius:6, padding:"7px 9px" }}>
                        <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", marginBottom:2 }}>{k.l}</div>
                        <div style={{ fontSize:12, fontWeight:700, color:k.color||DARK }}>{k.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Account Tier Breakdown */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px", marginTop:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:DARK }}>Account Tier Mix</div>
              <div style={{ fontSize:10, color:GRAY }}>Strategic = manual flag · others auto-classified by annualized case rate</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
              {tierCounts.map(t => (
                <div key={t.tier} style={{ background:t.cfg.bg, border:`1px solid ${t.cfg.border}`, borderRadius:8, padding:"11px 13px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                    <span style={{ fontSize:14 }}>{t.cfg.icon}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:t.cfg.text, textTransform:"uppercase" }}>{t.cfg.label.split(" ")[0]}</span>
                  </div>
                  <div style={{ fontSize:20, fontWeight:800, color:t.cfg.text }}>{t.clients.length}</div>
                  <div style={{ fontSize:10, color:GRAY, marginTop:3 }}>{t.cases.toLocaleString(undefined,{maximumFractionDigits:0})} cases · {fmt(t.rev)}</div>
                </div>
              ))}
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr>
                  {["Tier","Definition","Clients","Cases YTD","Helmsman Rev YTD","% of Portfolio","Avg Cases/Client"].map(h=>(
                    <th key={h} style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:["Cases YTD","Helmsman Rev YTD","% of Portfolio","Avg Cases/Client"].includes(h)?"right":"left", fontSize:10.5, fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tierCounts.map((t,i) => {
                  const pct = portTotal>0?(t.rev/portTotal*100):0;
                  const avgCases = t.clients.length>0?(t.cases/t.clients.length):0;
                  return (
                    <tr key={t.tier} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                      <td style={{ padding:"8px 10px" }}>
                        <span style={{ background:t.cfg.bg, color:t.cfg.text, border:`1px solid ${t.cfg.border}`, borderRadius:4, padding:"2px 8px", fontSize:10.5, fontWeight:700 }}>{t.cfg.icon} {t.tier}</span>
                      </td>
                      <td style={{ padding:"8px 10px", color:GRAY, fontSize:11 }}>{t.cfg.label.includes("(")?t.cfg.label.split("(")[1].replace(")",""):"Flagship — manual flag"}</td>
                      <td style={{ padding:"8px 10px", fontWeight:700 }}>{t.clients.length}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right" }}>{t.cases.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, color:TEAL }}>{fmt(t.rev)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                          <div style={{ width:60, height:7, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                            <div style={{ width:`${Math.min(100,pct)}%`, height:"100%", background:t.cfg.text, borderRadius:99 }} />
                          </div>
                          <span style={{ fontWeight:700, color:t.cfg.text }}>{pct.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:GRAY }}>{avgCases.toFixed(0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Transportation Margin Settings */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px", marginTop:14 }}>
            <div style={{ fontSize:13, fontWeight:700, color:DARK, marginBottom:10 }}>🚚 Transportation Margin Settings</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
              <div style={{ background:CARD_BG, border:`1px solid ${BORDER}`, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:GRAY, fontWeight:700, textTransform:"uppercase", marginBottom:8 }}>Markup % on Freight</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <input type="number" min="0" max="100" step="1" value={Math.round(freightMarkup*100)}
                    onChange={e=>setFreightMarkup((parseFloat(e.target.value)||0)/100)}
                    style={{ width:60, padding:"6px 8px", border:`1.5px solid #7C3AED`, borderRadius:6, fontSize:15, fontWeight:700, color:DARK, textAlign:"center" }} />
                  <span style={{ fontSize:13, color:GRAY }}>%</span>
                </div>
              </div>
              <div style={{ background:CARD_BG, border:`1px solid ${BORDER}`, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:GRAY, fontWeight:700, textTransform:"uppercase", marginBottom:8 }}>Est. Freight / Case ($)</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:13, color:GRAY }}>$</span>
                  <input type="number" min="0" step="0.50" value={freightPerCase}
                    onChange={e=>setFreightPerCase(parseFloat(e.target.value)||0)}
                    style={{ width:70, padding:"6px 8px", border:`1.5px solid #7C3AED`, borderRadius:6, fontSize:15, fontWeight:700, color:DARK, textAlign:"center" }} />
                </div>
              </div>
              <div style={{ background:"#FAF5FF", border:"1px solid #C4B5FD", borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:"#7C3AED", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>Transport Rev YTD</div>
                <div style={{ fontSize:18, fontWeight:800, color:"#7C3AED" }}>{fmt(portFreight)}</div>
                <div style={{ fontSize:10, color:GRAY, marginTop:3 }}>{portCases.toLocaleString(undefined,{maximumFractionDigits:0})} cases × ${freightPerCase} × {(freightMarkup*100).toFixed(0)}%</div>
              </div>
              <div style={{ background:"#FAF5FF", border:"1px solid #C4B5FD", borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:"#7C3AED", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>Transport Annual Rate</div>
                <div style={{ fontSize:18, fontWeight:800, color:"#7C3AED" }}>{fmt(portFreight/YTD_MONTHS*12)}</div>
                <div style={{ fontSize:10, color:GRAY, marginTop:3 }}>Placeholder — connect to Monday B2</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 2 — CASE VELOCITY
      ══════════════════════════════════════════════════════════════ */}
      {tab==="cases" && (
        <div>
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"10px 14px", marginBottom:12, display:"flex", gap:12, alignItems:"center" }}>
            <AMFilter value={amFilt} onChange={setAmFilt} />
            <span style={{ marginLeft:"auto", fontSize:11, color:GRAY }}>
              {enrichedSorted.length} clients · <strong style={{ color:DARK }}>{enrichedSorted.reduce((s,c)=>s+c.ytdCases,0).toLocaleString(undefined,{maximumFractionDigits:0})} cases YTD</strong>
              <span style={{ color:caseGrowth>=0?GREEN:RED, fontWeight:700, marginLeft:6 }}>{caseGrowth>=0?"▲":"▼"}{Math.abs(caseGrowth).toFixed(1)}% vs LY</span>
            </span>
          </div>

          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr>
                  <SortTH col="name">Client</SortTH>
                  <th style={{ background:TEAL, color:WHITE, padding:"8px 12px", fontSize:11, fontWeight:600 }}>AM</th>
                  <SortTH col="ytdCases" right>Cases YTD</SortTH>
                  <SortTH col="velYoy" right>YoY Velocity</SortTH>
                  <th style={{ background:TEAL, color:WHITE, padding:"8px 12px", textAlign:"right", fontSize:11, fontWeight:600 }}>LY Pace</th>
                  <SortTH col="caseYTD" right>Case Fees YTD</SortTH>
                  <SortTH col="totalYTD" right>Helmsman Rev YTD</SortTH>
                  <SortTH col="annual" right>Annual Rate</SortTH>
                </tr>
              </thead>
              <tbody>
                {enrichedSorted.map((c,i) => {
                  const lyPace = (c.lyCases / 12) * YTD_MONTHS;
                  const vel    = c._velYoy;
                  const maxC   = Math.max(...enrichedSorted.map(x=>x.ytdCases));
                  const isSel  = c.id === selectedId;
                  return (
                    <tr key={c.id} onClick={() => setSelected(isSel?null:c.id)}
                      style={{ background:isSel?`${TEAL}0D`:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}`, cursor:"pointer" }}>
                      <td style={{ padding:"8px 12px", fontWeight:600, color:DARK }}>
                          {isSel&&<span style={{color:TEAL,marginRight:4,fontSize:10}}>▶</span>}
                          {c.name}
                          {(()=>{ const tc=ACCOUNT_TIER_CFG[getTier(c)]; return tc?<span style={{marginLeft:5,background:tc.bg,color:tc.text,border:`1px solid ${tc.border}`,borderRadius:3,padding:"1px 5px",fontSize:9,fontWeight:700}}>{tc.icon}</span>:null; })()}
                          {(BRANDS_BY_CLIENT[c.id]||[]).length > 0 && (
                            <button onClick={e=>{e.stopPropagation();setTab("brands");setBrandClient(c.id);}}
                              title="View brands"
                              style={{ marginLeft:6, background:`${TEAL}15`, border:`1px solid ${TEAL}44`, borderRadius:4, padding:"1px 6px", fontSize:9.5, color:TEAL, fontWeight:700, cursor:"pointer" }}>
                              {(BRANDS_BY_CLIENT[c.id]||[]).length} brands
                            </button>
                          )}
                        </td>
                      <td style={{ padding:"8px 12px" }}><AMAvatar am={c.am} size={18} /></td>
                      <td style={{ padding:"8px 12px", textAlign:"right" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                          <div style={{ width:60, height:7, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                            <div style={{ width:`${(c.ytdCases/maxC)*100}%`, height:"100%", background:TEAL, borderRadius:99 }} />
                          </div>
                          <span style={{ fontWeight:700 }}>{c.ytdCases.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                        </div>
                      </td>
                      <td style={{ padding:"8px 12px", textAlign:"right" }}>
                        {vel!==null ? (
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:5 }}>
                            <div style={{ width:50, height:7, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                              <div style={{ width:`${Math.min(100,Math.abs(vel)/50*100)}%`, height:"100%", background:vel>=0?GREEN:RED, borderRadius:99 }} />
                            </div>
                            <span style={{ fontWeight:700, color:vel>5?GREEN:vel<-5?RED:AMBER }}>{vel>=0?"▲":"▼"}{Math.abs(vel).toFixed(1)}%</span>
                          </div>
                        ) : <span style={{ color:GRAY_MID, fontSize:10 }}>New client</span>}
                      </td>
                      <td style={{ padding:"8px 12px", textAlign:"right", color:GRAY }}>{lyPace.toFixed(0)} cs</td>
                      <td style={{ padding:"8px 12px", textAlign:"right", color:TEAL, fontWeight:600 }}>{fmt(c._case)}</td>
                      <td style={{ padding:"8px 12px", textAlign:"right", fontWeight:700, color:DARK }}>{fmt(c._total)}</td>
                      <td style={{ padding:"8px 12px", textAlign:"right", color:GREEN, fontWeight:600 }}>{fmt(c._ann)}</td>
                    </tr>
                  );
                })}
                <tr style={{ background:"#EAF3F4", borderTop:`2px solid #B2D8DC` }}>
                  <td colSpan={2} style={{ padding:"9px 12px", fontWeight:800 }}>Total</td>
                  <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:800 }}>{enrichedSorted.reduce((s,c)=>s+c.ytdCases,0).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td style={{ padding:"9px 12px", textAlign:"right" }}><span style={{ fontWeight:700, color:caseGrowth>=0?GREEN:RED }}>{caseGrowth>=0?"▲":"▼"}{Math.abs(caseGrowth).toFixed(1)}%</span></td>
                  <td style={{ padding:"9px 12px", textAlign:"right", color:GRAY }}>{((portCasesLY/12)*YTD_MONTHS).toFixed(0)} cs</td>
                  <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:800, color:TEAL }}>{fmt(enrichedSorted.reduce((s,c)=>s+c._case,0))}</td>
                  <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:800, fontSize:14 }}>{fmt(enrichedSorted.reduce((s,c)=>s+c._total,0))}</td>
                  <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:800, color:GREEN }}>{fmt(enrichedSorted.reduce((s,c)=>s+c._ann,0))}</td>
                </tr>
              </tbody>
            </table>
            {selClient && (
              <div style={{ borderTop:`2px solid ${TEAL}33`, padding:"14px 18px", background:`${TEAL}06` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:DARK }}>{selClient.name} — Case Velocity</div>
                  <button onClick={()=>setSelected(null)} style={{ padding:"4px 10px", border:`1px solid ${BORDER}`, borderRadius:5, background:WHITE, color:GRAY, fontSize:11, cursor:"pointer" }}>✕</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
                  {[
                    { l:"Cases YTD",       v:selClient.ytdCases.toLocaleString(undefined,{maximumFractionDigits:0}) },
                    { l:"LY Same Period",  v:((selClient.lyCases/12)*YTD_MONTHS).toFixed(0)+" cs" },
                    { l:"YoY Velocity",    v:selClient._velYoy!=null?`${selClient._velYoy>=0?"▲":"▼"}${Math.abs(selClient._velYoy).toFixed(1)}%`:"New", color:selClient._velYoy>0?GREEN:RED },
                    { l:"Annual Case Rate",v:(selClient.ytdCases/YTD_MONTHS*12).toFixed(0)+" cs/yr" },
                    { l:"Case Fee YTD",    v:fmt(selClient._case), color:TEAL },
                  ].map((k,j)=>(
                    <div key={j} style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:7, padding:"9px 12px" }}>
                      <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", marginBottom:3 }}>{k.l}</div>
                      <div style={{ fontSize:14, fontWeight:700, color:k.color||DARK }}>{k.v}</div>
                    </div>
                  ))}
                </div>
                {selClient._velYoy!==null && selClient._velYoy<-10 && (
                  <div style={{ marginTop:10, background:RED_BG, border:"1px solid #FCA5A5", borderLeft:`3px solid ${RED}`, borderRadius:7, padding:"9px 13px", fontSize:11, color:RED, lineHeight:1.6 }}>
                    <strong>⚠ Case velocity declining {Math.abs(selClient._velYoy).toFixed(1)}% YoY.</strong> This directly reduces Helmsman case fee income. Check for distributor changes, reduced SKU count, or lost accounts.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 3 — GROWTH (Clients Closed vs Lost per Year + Reasons)
      ══════════════════════════════════════════════════════════════ */}
      {tab==="growth" && (
        <div>
          {/* Summary KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:16 }}>
            {[
              { l:"Total Signed (all time)", v:growthData.reduce((s,r)=>s+r.count,0),    color:GREEN  },
              { l:"Total Churned (all time)", v:HM_CHURN_LOG.length,                     color:RED    },
              { l:"Net Active",              v:HM_CLIENTS.length,                         color:TEAL   },
              { l:"Best Year (closings)",    v:growthData.reduce((a,r)=>r.count>a.count?r:a,{count:0}).year||"—", color:DARK },
              { l:"Avg New / Year",          v:(growthData.reduce((s,r)=>s+r.count,0)/growthData.filter(r=>r.count>0).length).toFixed(1), color:DARK },
            ].map((k,i)=>(
              <div key={i} style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:GRAY, fontWeight:600, textTransform:"uppercase", marginBottom:4 }}>{k.l}</div>
                <div style={{ fontSize:22, fontWeight:800, color:k.color }}>{k.v}</div>
              </div>
            ))}
          </div>

          {/* Closed vs Lost bar chart */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px", marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:DARK }}>Clients Closed vs Lost — by Year</div>
                <div style={{ fontSize:11, color:GRAY }}>Click a year to see detail. Net new = Closed − Lost.</div>
              </div>
              <div style={{ display:"flex", gap:12 }}>
                {[[GREEN,"Closed"],[RED,"Churned"],[TEAL,"Net New"]].map(([c,l])=>(
                  <div key={l} style={{ display:"flex",alignItems:"center",gap:4,fontSize:10.5,color:GRAY }}>
                    <div style={{ width:8,height:8,borderRadius:2,background:c }}/>{l}
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={growthData} barCategoryGap="28%" barGap={3}
                margin={{top:4,right:4,bottom:0,left:0}}
                onClick={d => d?.activePayload && setGrowthYear(y => y===d.activePayload[0].payload.year?null:d.activePayload[0].payload.year)}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                <XAxis dataKey="year" tick={{fontSize:10,fill:GRAY}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize:9,fill:GRAY}} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
                <Tooltip contentStyle={{fontSize:11}} />
                <Bar dataKey="count"  name="Clients Signed" fill={GREEN} radius={[3,3,0,0]} />
                <Bar dataKey="lost"   name="Clients Lost"   fill={RED}   radius={[3,3,0,0]} />
                <Bar dataKey="netNew" name="Net New"        fill={TEAL}  radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* Year detail drill-down */}
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:13, fontWeight:700, color:DARK }}>
                  {growthYear ? `${growthYear} — Clients Signed` : "All Years — Signed Clients"}
                </span>
                {growthYear && <button onClick={()=>setGrowthYear(null)} style={{ padding:"3px 8px", border:`1px solid ${BORDER}`, borderRadius:4, background:WHITE, color:GRAY, fontSize:10, cursor:"pointer" }}>Show all</button>}
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    {["Year","Clients Signed","Avg Retainer","Churned","Retention"].map(h=>(
                      <th key={h} style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:h==="Year"?"left":"right", fontSize:10.5, fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(growthYear ? growthData.filter(r=>r.year===growthYear) : growthData).map((row,i)=>{
                    const ret = row.count > 0 ? Math.round(((row.count - row.lost)/row.count)*100) : 100;
                    return (
                      <tr key={row.year} onClick={()=>setGrowthYear(y=>y===row.year?null:row.year)}
                        style={{ background:growthYear===row.year?`${TEAL}0D`:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}`, cursor:"pointer" }}>
                        <td style={{ padding:"8px 10px", fontWeight:700, color:TEAL }}>{row.year}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700 }}>{row.count}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right", color:GRAY }}>{row.avgRetainer>0?`$${row.avgRetainer.toLocaleString()}/mo`:"—"}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right", color:row.lost>0?RED:GRAY }}>{row.lost>0?row.lost:"—"}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right" }}>
                          {row.count > 0 ? (
                            <span style={{ fontWeight:700, color:ret>=90?GREEN:ret>=70?AMBER:RED }}>{ret}%</span>
                          ) : <span style={{ color:GRAY_MID }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {growthYear && growthData.find(r=>r.year===growthYear)?.signed.length > 0 && (
                <div style={{ padding:"12px 16px", borderTop:`1px solid ${BORDER}`, background:CARD_BG }}>
                  <div style={{ fontSize:10, fontWeight:700, color:GRAY, marginBottom:6 }}>CLIENTS SIGNED IN {growthYear}</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {growthData.find(r=>r.year===growthYear).signed.map(name=>(
                      <span key={name} style={{ background:GREEN_BG, border:"1px solid #86EFAC", borderRadius:4, padding:"3px 8px", fontSize:10, color:GREEN, fontWeight:600 }}>{name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Churn detail + reasons */}
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:13, fontWeight:700, color:DARK }}>Churn Detail & Reasons</span>
                <span style={{ background:RED_BG, color:RED, borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{HM_CHURN_LOG.length} lost</span>
              </div>
              {HM_CHURN_LOG.map((c,i) => {
                const rc = CHURN_REASON_COLORS[c.reason] || CHURN_REASON_COLORS.Unknown;
                const tenureMos = Math.round((new Date(c.churnDate) - new Date(c.startDate)) / (1000*60*60*24*30.44));
                return (
                  <div key={c.id} style={{ padding:"14px 16px", borderBottom:`1px solid ${BORDER}`, background:i%2===0?WHITE:CARD_BG }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:DARK }}>{c.name}</div>
                        <div style={{ fontSize:10, color:GRAY, marginTop:1 }}>
                          {c.startDate.slice(0,7)} → {c.churnDate.slice(0,7)} · {tenureMos} months tenure · AM: {ACCOUNT_MANAGERS.find(a=>a.id===c.am)?.name.split(" ")[0]}
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:RED }}>−{fmt(c.lastRetainer*12)}/yr</div>
                        {c.exitFeePaid && <div style={{ fontSize:10, color:GREEN }}>Exit fee: {fmt(c.exitFeeAmt)}</div>}
                        {!c.exitFeePaid && <div style={{ fontSize:10, color:GRAY }}>Exit fee: waived</div>}
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ background:rc.bg, color:rc.color, border:`1px solid ${rc.color}44`, borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{c.reason}</span>
                      <span style={{ fontSize:11, color:GRAY, flex:1 }}>{c.reasonDetail}</span>
                    </div>
                    <div style={{ marginTop:6, display:"flex", gap:6 }}>
                      <span style={{ fontSize:10, color:GRAY }}>Reactivation:</span>
                      <span style={{ fontSize:10, fontWeight:700, color:c.reactivation==="High"?GREEN:c.reactivation==="Medium"?AMBER:RED }}>{c.reactivation}</span>
                    </div>
                  </div>
                );
              })}
              {HM_CHURN_LOG.length === 0 && (
                <div style={{ padding:"24px", textAlign:"center", color:GRAY, fontSize:12 }}>No churned clients on record.</div>
              )}
              <div style={{ padding:"10px 16px", background:"#FEF2F2", borderTop:`1px solid #FCA5A5`, fontSize:11, color:RED, lineHeight:1.6 }}>
                <strong>Total annual revenue lost:</strong> {fmt(HM_CHURN_LOG.reduce((s,c)=>s+c.lastRetainer*12,0))}/yr · Seed this board in Monday (B5) with all historical churn before go-live.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 4 — BRAND FORECAST
      ══════════════════════════════════════════════════════════════ */}
      {tab==="forecast" && (
        <div>
          {/* Forecast KPI strip */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
            {[
              { l:"2026 Full-Year Forecast",  v:fmt(fcFiltered.reduce((s,b)=>s+b.forecastRev,0)),  color:TEAL,  hi:true  },
              { l:"Revenue Actual YTD",        v:fmt(fcTotalActual),    sub:`${YTD_MONTHS} months`,   color:GREEN          },
              { l:"Forecast Remaining",        v:fmt(fcRemaining),      sub:"Apr–Dec",                color:DARK           },
              { l:"YTD Attainment",            v:`${fcPct.toFixed(1)}%`, sub:"of full-year forecast", color:fcPct>25?GREEN:AMBER },
            ].map((k,i)=>(
              <div key={i} style={{ background:k.hi?`${TEAL}10`:WHITE, border:`1px solid ${k.hi?TEAL:BORDER}`, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:4 }}>{k.l}</div>
                <div style={{ fontSize:18, fontWeight:800, color:k.color }}>{k.v}</div>
                {k.sub && <div style={{ fontSize:10, color:GRAY, marginTop:2 }}>{k.sub}</div>}
              </div>
            ))}
          </div>

          {/* Filter + table */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:700, color:DARK }}>Brand Revenue Forecast — 2026</div>
              <AMFilter value={fcFilter} onChange={setFcFilter} />
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr>
                  {[["Brand",""],["Client",""],["AM","60px"],["Confidence","100px"],
                    ["Actual Cases YTD","right"],["Forecast Cases (Full Yr)","right"],
                    ["Actual Rev YTD","right"],["Full-Year Forecast","right"],
                    ["Remaining","right"],["Attainment","right"]
                  ].map(([h,align],idx)=>(
                    <th key={idx} style={{ background:TEAL, color:WHITE, padding:"8px 10px",
                      textAlign:align==="right"?"right":"left", fontSize:10.5, fontWeight:600, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fcFiltered.map((b,i) => {
                  const remaining  = b.forecastRev - b.actualRev;
                  const pct        = b.forecastRev > 0 ? (b.actualRev / b.forecastRev) * 100 : 0;
                  const confColors = { High:{ bg:GREEN_BG, c:GREEN }, Medium:{ bg:AMBER_BG, c:AMBER }, Low:{ bg:RED_BG, c:RED } };
                  const cc         = confColors[b.confidence] || confColors.Medium;
                  const am         = ACCOUNT_MANAGERS.find(a=>a.id===b.am);
                  const maxFc      = Math.max(...fcFiltered.map(x=>x.forecastRev));
                  return (
                    <tr key={`${b.clientId}-${b.brand}`} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                      <td style={{ padding:"9px 10px", fontWeight:700, color:DARK }}>{b.brand}</td>
                      <td style={{ padding:"9px 10px", color:GRAY, fontSize:11 }}>{b.clientName}</td>
                      <td style={{ padding:"9px 10px" }}><AMAvatar am={am} size={17}/></td>
                      <td style={{ padding:"9px 10px" }}>
                        <span style={{ background:cc.bg, color:cc.c, border:`1px solid ${cc.c}44`, borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{b.confidence}</span>
                      </td>
                      <td style={{ padding:"9px 10px", textAlign:"right" }}>{b.actualCases.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                      <td style={{ padding:"9px 10px", textAlign:"right" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, justifyContent:"flex-end" }}>
                          <div style={{ width:50, height:6, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                            <div style={{ width:`${(b.forecastCases/(Math.max(...fcFiltered.map(x=>x.forecastCases))))*100}%`, height:"100%", background:TEAL, borderRadius:99 }} />
                          </div>
                          <span style={{ fontWeight:700 }}>{b.forecastCases.toLocaleString()}</span>
                        </div>
                      </td>
                      <td style={{ padding:"9px 10px", textAlign:"right", color:TEAL, fontWeight:600 }}>{fmt(b.actualRev)}</td>
                      <td style={{ padding:"9px 10px", textAlign:"right" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, justifyContent:"flex-end" }}>
                          <div style={{ width:50, height:6, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                            <div style={{ width:`${(b.forecastRev/maxFc)*100}%`, height:"100%", background:TEAL2, borderRadius:99 }} />
                          </div>
                          <span style={{ fontWeight:700 }}>{fmt(b.forecastRev)}</span>
                        </div>
                      </td>
                      <td style={{ padding:"9px 10px", textAlign:"right", color:GREEN, fontWeight:600 }}>{fmt(remaining)}</td>
                      <td style={{ padding:"9px 10px", textAlign:"right" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, justifyContent:"flex-end" }}>
                          <div style={{ width:36, height:7, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                            <div style={{ width:`${Math.min(100,pct)}%`, height:"100%", background:pct>=25?GREEN:AMBER, borderRadius:99 }} />
                          </div>
                          <span style={{ fontWeight:700, color:pct>=25?GREEN:AMBER, fontSize:11 }}>{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background:"#EAF3F4", borderTop:`2px solid #B2D8DC` }}>
                  <td colSpan={4} style={{ padding:"9px 10px", fontWeight:800 }}>Total</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800 }}>{fcFiltered.reduce((s,b)=>s+b.actualCases,0).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800 }}>{fcFiltered.reduce((s,b)=>s+b.forecastCases,0).toLocaleString()}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, color:TEAL }}>{fmt(fcTotalActual)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, fontSize:14 }}>{fmt(fcTotalForecast)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, color:GREEN }}>{fmt(fcRemaining)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800 }}><span style={{ color:fcPct>=25?GREEN:AMBER }}>{fcPct.toFixed(0)}%</span></td>
                </tr>
              </tbody>
            </table>
            <div style={{ padding:"11px 16px", background:CARD_BG, borderTop:`1px solid ${BORDER}`, fontSize:11, color:GRAY }}>
              📌 Forecast data sourced from <strong style={{ color:DARK }}>Monday Board 3 — Brand Forecast</strong> (AM-managed, updated monthly by the 1st). Actuals auto-filled from B2 after each month closes.
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 5 — PIPELINE
      ══════════════════════════════════════════════════════════════ */}
      {tab==="pipeline" && (
        <div>
          <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderLeft:`3px solid #1D4ED8`, borderRadius:8, padding:"11px 16px", marginBottom:14, fontSize:11, color:"#1E40AF" }}>
            <strong>🚀 Pipeline is managed by the Sales team (Monday Board 6).</strong> The Helm reads 5 fields only: Name, Stage, Est. Cases, Est. Retainer, and Probability. Everything else lives in the Sales board.
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:16 }}>
            {[
              { stage:"Prospect",   color:AMBER,  items: HM_PIPELINE.filter(p=>p.stage==="Prospect")   },
              { stage:"Onboarding", color:TEAL2,  items: HM_PIPELINE.filter(p=>p.stage==="Onboarding") },
              { stage:"Active",     color:GREEN,  items: HM_CLIENTS_ENRICHED                            },
              { stage:"At Risk",    color:AMBER,  items: HM_CLIENTS_ENRICHED.filter(c=>c.health==="AT_RISK"||c.health==="CRITICAL") },
              { stage:"Churned",    color:RED,    items: HM_PIPELINE.filter(p=>p.stage==="Churned")    },
            ].map((s,i) => {
              const estRev = s.stage === "Active" ? portAnn
                : s.stage === "At Risk" ? s.items.reduce((sum,c) => sum+annRate(c), 0)
                : s.items.reduce((sum,p) => sum+(p.estMonthlyFee||0)*12+(p.estCases||0)*caseFeeRate, 0);
              return (
                <div key={i} style={{ background:WHITE, border:`2px solid ${s.color}33`, borderRadius:10, padding:"13px 14px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:s.color }} />
                    <span style={{ fontSize:10, fontWeight:700, color:s.color, textTransform:"uppercase" }}>{s.stage}</span>
                  </div>
                  <div style={{ fontSize:22, fontWeight:800, color:DARK }}>{s.items.length}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:s.color, marginTop:6 }}>{fmt(estRev)}<span style={{ fontWeight:400, color:GRAY }}>/yr est</span></div>
                </div>
              );
            })}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"11px 16px", borderBottom:`1px solid ${BORDER}`, fontSize:13, fontWeight:700, color:DARK }}>Prospects & Onboarding</div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    {["Name","Stage","AM","Est Cases/yr","Est Retainer","Probability","Wtd Rev/yr"].map(h=>(
                      <th key={h} style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:"left", fontSize:10.5, fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {HM_PIPELINE.filter(p=>p.stage!=="Churned").map((p,i)=>{
                    const am     = ACCOUNT_MANAGERS.find(a=>a.id===p.am);
                    const estAnn = p.estMonthlyFee*12 + p.estCases*caseFeeRate;
                    const isOB   = p.stage === "Onboarding";
                    return (
                      <tr key={p.id} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                        <td style={{ padding:"7px 10px", fontWeight:600, color:DARK }}>{p.name}</td>
                        <td style={{ padding:"7px 10px" }}>
                          <span style={{ background:isOB?`${TEAL2}20`:AMBER_BG, color:isOB?TEAL2:AMBER, border:`1px solid ${isOB?TEAL2:AMBER}44`, borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{p.stage}</span>
                        </td>
                        <td style={{ padding:"7px 10px" }}><AMAvatar am={am} size={17}/></td>
                        <td style={{ padding:"7px 10px", textAlign:"right" }}>{p.estCases.toLocaleString()}</td>
                        <td style={{ padding:"7px 10px", textAlign:"right" }}>${p.estMonthlyFee.toLocaleString()}/mo</td>
                        <td style={{ padding:"7px 10px", textAlign:"right" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5, justifyContent:"flex-end" }}>
                            <div style={{ width:40, height:6, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                              <div style={{ width:`${p.probability}%`, height:"100%", background:p.probability>75?GREEN:p.probability>40?AMBER:RED, borderRadius:99 }} />
                            </div>
                            <span style={{ fontWeight:700, color:p.probability>75?GREEN:p.probability>40?AMBER:RED }}>{p.probability}%</span>
                          </div>
                        </td>
                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:700, color:GREEN }}>{fmt(estAnn*p.probability/100)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding:"10px 16px", borderTop:`1px solid ${BORDER}`, background:CARD_BG, fontSize:11, color:GRAY }}>
                Weighted pipeline: <strong style={{ color:GREEN }}>{fmt(HM_PIPELINE.filter(p=>p.stage!=="Churned").reduce((s,p)=>s+(p.estMonthlyFee*12+p.estCases*caseFeeRate)*p.probability/100,0))}</strong>/yr
              </div>
            </div>

            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"11px 16px", borderBottom:`1px solid ${BORDER}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:13, fontWeight:700, color:DARK }}>Churned Clients</span>
                <span style={{ background:RED_BG, color:RED, borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{HM_CHURN_LOG.length} lost</span>
              </div>
              {HM_CHURN_LOG.map((c,i) => {
                const am = ACCOUNT_MANAGERS.find(a=>a.id===c.am);
                const rc = CHURN_REASON_COLORS[c.reason]||CHURN_REASON_COLORS.Unknown;
                return (
                  <div key={c.id} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}`, background:i%2===0?WHITE:CARD_BG }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:700, color:DARK }}>{c.name}</div>
                        <div style={{ fontSize:10, color:GRAY, marginTop:2 }}>{am?.name} · churned {c.churnDate.slice(0,7)}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <span style={{ background:rc.bg, color:rc.color, border:`1px solid ${rc.color}44`, borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{c.reason}</span>
                        <div style={{ fontSize:10, color:RED, marginTop:3 }}>{fmt(c.lastRetainer*12)}/yr lost</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 6 — RETENTION COHORTS
      ══════════════════════════════════════════════════════════════ */}
      {tab==="retention" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16 }}>
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"13px 18px", borderBottom:`1px solid ${BORDER}` }}>
                <div style={{ fontSize:14, fontWeight:700, color:DARK }}>Client Cohort Retention</div>
                <div style={{ fontSize:11, color:GRAY, marginTop:2 }}>Of clients who started each year, how many are still active today?</div>
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    {["Start Year","Started","Active","Churned","Retention","Status"].map(h=>(
                      <th key={h} style={{ background:TEAL, color:WHITE, padding:"8px 14px", textAlign:h==="Start Year"||h==="Status"?"left":"center", fontSize:11, fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((co,i) => {
                    const isGood = co.ret12 >= 80;
                    const isWarn = co.ret12 >= 60 && co.ret12 < 80;
                    return (
                      <tr key={co.yr} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                        <td style={{ padding:"10px 14px", fontWeight:700, color:DARK }}>{co.yr}</td>
                        <td style={{ padding:"10px 14px", textAlign:"center" }}>{co.started}</td>
                        <td style={{ padding:"10px 14px", textAlign:"center", fontWeight:700, color:GREEN }}>{co.active}</td>
                        <td style={{ padding:"10px 14px", textAlign:"center", color:co.churned>0?RED:GRAY }}>{co.churned>0?co.churned:"—"}</td>
                        <td style={{ padding:"10px 14px", textAlign:"center" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"center" }}>
                            <div style={{ width:80, height:10, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                              <div style={{ width:`${co.ret12}%`, height:"100%", background:isGood?GREEN:isWarn?AMBER:RED, borderRadius:99 }} />
                            </div>
                            <span style={{ fontWeight:700, color:isGood?GREEN:isWarn?AMBER:RED }}>{co.ret12}%</span>
                          </div>
                        </td>
                        <td style={{ padding:"10px 14px" }}>
                          <span style={{ background:isGood?GREEN_BG:isWarn?AMBER_BG:RED_BG, color:isGood?GREEN:isWarn?AMBER:RED, border:`1px solid ${isGood?"#86EFAC":isWarn?"#FCD34D":"#FCA5A5"}`, borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:700 }}>
                            {isGood?"Healthy":isWarn?"Watch":"At Risk"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding:"12px 18px", borderTop:`1px solid ${BORDER}`, background:CARD_BG, display:"flex", justifyContent:"space-between", fontSize:11 }}>
                <span style={{ color:GRAY }}>Overall retention: <strong style={{ color:DARK }}>{Math.round((HM_CLIENTS.length/(HM_CLIENTS.length+HM_CHURN_LOG.length))*100)}%</strong></span>
                <span style={{ color:GRAY }}>Industry benchmark (services): <strong style={{ color:DARK }}>{"> 85% / yr"}</strong></span>
              </div>
            </div>

          {/* Category + Country breakdown */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
            {/* By Category */}
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}`, fontSize:13, fontWeight:700, color:DARK }}>Clients by Category</div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead><tr>
                  {["Category","Clients","Brands","Cases YTD","Helmsman Rev YTD","Avg Health"].map(h=>(
                    <th key={h} style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:["Clients","Brands","Cases YTD","Helmsman Rev YTD","Avg Health"].includes(h)?"right":"left", fontSize:10.5, fontWeight:600 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(()=>{
                    const CAT_LABELS = { cachaca:"Cachaça", tequila:"Tequila", gin:"Gin", rtd:"RTD", aquavit:"Aquavit", bourbon:"Bourbon", other:"Other" };
                    const cats = [...new Set(HM_CLIENTS.map(c=>c.category))];
                    return cats.map((cat,i) => {
                      const catClients = HM_CLIENTS_ENRICHED.filter(c=>c.category===cat);
                      const catBrands  = HM_BRANDS.filter(b=>b.category===cat).length;
                      const catCases   = catClients.reduce((s,c)=>s+c.ytdCases,0);
                      const catRev     = catClients.reduce((s,c)=>s+totalYTD(c),0);
                      const avgHealth  = catClients.reduce((s,c)=>s+c.score,0)/(catClients.length||1);
                      const healthColor= avgHealth>=80?GREEN:avgHealth>=60?TEAL:avgHealth>=40?AMBER:RED;
                      return (
                        <tr key={cat} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                          <td style={{ padding:"8px 10px", fontWeight:700, color:DARK }}>{CAT_LABELS[cat]||cat}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right" }}>{catClients.length}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:GRAY }}>{catBrands}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right" }}>{catCases.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, color:TEAL }}>{fmt(catRev)}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right" }}>
                            <span style={{ fontWeight:700, color:healthColor }}>{avgHealth.toFixed(0)}</span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {/* By Country */}
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}`, fontSize:13, fontWeight:700, color:DARK }}>Clients by Country</div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead><tr>
                  {["Country","Clients","Cases YTD","Helmsman Rev YTD","Categories","Tier Mix"].map(h=>(
                    <th key={h} style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:["Clients","Cases YTD","Helmsman Rev YTD"].includes(h)?"right":"left", fontSize:10.5, fontWeight:600 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(()=>{
                    const FLAG = { USA:"🇺🇸", France:"🇫🇷", Ireland:"🇮🇪", Brazil:"🇧🇷", Argentina:"🇦🇷", Mexico:"🇲🇽", Australia:"🇦🇺", Japan:"🇯🇵", UK:"🇬🇧" };
                    const CAT_LABELS = { cachaca:"Cachaça", tequila:"Tequila", gin:"Gin", rtd:"RTD", aquavit:"Aquavit", bourbon:"Bourbon", other:"Other" };
                    const countries = [...new Set(HM_CLIENTS.map(c=>c.country))].sort();
                    return countries.map((ctry,i) => {
                      const cc     = HM_CLIENTS_ENRICHED.filter(c=>c.country===ctry);
                      const cases  = cc.reduce((s,c)=>s+c.ytdCases,0);
                      const rev    = cc.reduce((s,c)=>s+totalYTD(c),0);
                      const cats   = [...new Set(cc.map(c=>c.category))].map(c=>CAT_LABELS[c]||c).join(", ");
                      const tiers  = [...new Set(cc.map(c=>getTier(c)))];
                      return (
                        <tr key={ctry} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                          <td style={{ padding:"8px 10px", fontWeight:700, color:DARK }}>{FLAG[ctry]||"🌐"} {ctry}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right" }}>{cc.length}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right" }}>{cases.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, color:TEAL }}>{fmt(rev)}</td>
                          <td style={{ padding:"8px 10px", fontSize:10, color:GRAY }}>{cats}</td>
                          <td style={{ padding:"8px 10px" }}>
                            <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                              {tiers.map(t=>{ const tc=ACCOUNT_TIER_CFG[t]; return tc?<span key={t} style={{ background:tc.bg, color:tc.text, border:`1px solid ${tc.border}`, borderRadius:3, padding:"1px 5px", fontSize:9, fontWeight:700 }}>{tc.icon}</span>:null; })}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>

            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:12, fontWeight:700, color:DARK, marginBottom:10 }}>Why Retention Matters</div>
                {[
                  { metric:"Cost to acquire 1 client", v:"$2,000–5,000 est.", note:"vs $0 to retain" },
                  { metric:"Break-even on new client",  v:"3–4 months",      note:"at $1,000/mo retainer" },
                  { metric:"Revenue at 90% retention",  v:fmt(portAnn*0.9),  note:"vs "+fmt(portAnn)+" today" },
                  { metric:"Revenue at 95% retention",  v:fmt(portAnn*0.95), note:"gap: "+fmt(portAnn*0.05) },
                ].map((k,i)=>(
                  <div key={i} style={{ padding:"7px 0", borderBottom:i<3?`1px solid ${BORDER}`:"none" }}>
                    <div style={{ fontSize:10, color:GRAY }}>{k.metric}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:DARK }}>{k.v}</span>
                      <span style={{ fontSize:10, color:GRAY_MID }}>{k.note}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderLeft:`3px solid #1D4ED8`, borderRadius:10, padding:"13px 15px", fontSize:11, color:"#1E40AF", lineHeight:1.7 }}>
                <strong>CEO Action:</strong> Track Gross Revenue Retention (GRR) monthly — what % of last month's retainer is still on the books this month, ignoring new clients. Target GRR {">"} 90%.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 7 — CAPACITY & MARGIN
      ══════════════════════════════════════════════════════════════ */}
      {tab==="capacity" && (
        <div>
          {/* 25% capacity buffer banner */}
          <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderLeft:`3px solid #1D4ED8`, borderRadius:8, padding:"11px 16px", marginBottom:14, fontSize:11, color:"#1E40AF" }}>
            <strong>⭐ Helmsman Capacity Philosophy:</strong> We hire quality people before we are full. Target utilization is <strong>75% of max capacity</strong> — maintaining a <strong>25% buffer</strong> for service quality and growth readiness. Max: {AM_CAPACITY_MAX} clients/AM · Target: {AM_CAPACITY_TARGET} clients/AM.
          </div>

          {/* AM cards — NO salary/cost shown */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
            {amCapacity.map(am => {
              const utilization = am.clientCount / AM_CAPACITY_MAX * 100;
              const targetPct   = AM_CAPACITY_TARGET / AM_CAPACITY_MAX * 100;
              const tierBreak   = ["Strategic","Big","Medium","Small","Micro"].map(t=>({
                tier:t, cfg:ACCOUNT_TIER_CFG[t],
                count: am.clients.filter(c=>getTier(c)===t).length
              })).filter(t=>t.count>0);
              return (
                <div key={am.id} style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ background:am.color, padding:"13px 18px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:40,height:40,borderRadius:"50%",background:"rgba(255,255,255,0.22)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,color:WHITE }}>{am.avatar}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:800, color:WHITE }}>{am.name}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)" }}>{am.title}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:16, fontWeight:800, color:WHITE }}>{am.clientCount} / {AM_CAPACITY_TARGET} clients</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)" }}>target utilization</div>
                    </div>
                  </div>

                  {/* Capacity utilization bar */}
                  <div style={{ padding:"12px 18px", borderBottom:`1px solid ${BORDER}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:GRAY, marginBottom:5 }}>
                      <span>Capacity Utilization</span>
                      <span style={{ fontWeight:700, color:utilization>90?"#DC2626":utilization>75?AMBER:GREEN }}>
                        {utilization.toFixed(0)}% of max · {AM_CAPACITY_MAX - am.clientCount} slots remaining
                      </span>
                    </div>
                    <div style={{ height:12, background:BORDER, borderRadius:99, overflow:"hidden", position:"relative" }}>
                      <div style={{ position:"absolute", left:`${targetPct}%`, top:0, bottom:0, width:2, background:"rgba(0,0,0,0.25)", zIndex:1 }} />
                      <div style={{ width:`${Math.min(100,utilization)}%`, height:"100%",
                        background:utilization>90?"#DC2626":utilization>75?AMBER:GREEN, borderRadius:99 }} />
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:GRAY_MID, marginTop:3 }}>
                      <span>0</span>
                      <span style={{ color:AMBER }}>▲ 75% target</span>
                      <span>100% max ({AM_CAPACITY_MAX})</span>
                    </div>
                  </div>

                  {/* Stats — revenue only, no cost */}
                  <div style={{ padding:"12px 18px", display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, borderBottom:`1px solid ${BORDER}` }}>
                    {[
                      { l:"Rev YTD",      v:fmt(am.amRevYTD),   color:TEAL },
                      { l:"Annual Rate",  v:fmt(am.amRevAnn),   color:DARK },
                      { l:"Rev/Client",   v:fmt(am.amRevAnn/am.clientCount), color:DARK },
                      { l:"Cases Managed",v:am.amCases.toLocaleString(undefined,{maximumFractionDigits:0}), color:DARK },
                      { l:"Avg Complexity",v:`${am.complexity.toFixed(1)} / 5`, color:DARK },
                      { l:"Brands Managed",v:am.clients.reduce((s,c)=>s+(BRANDS_BY_CLIENT[c.id]||[]).length,0), color:DARK },
                    ].map((k,i)=>(
                      <div key={i} style={{ background:CARD_BG, borderRadius:6, padding:"8px 10px" }}>
                        <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", marginBottom:2 }}>{k.l}</div>
                        <div style={{ fontSize:12, fontWeight:700, color:k.color }}>{k.v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tier mix */}
                  <div style={{ padding:"10px 18px", borderBottom:`1px solid ${BORDER}` }}>
                    <div style={{ fontSize:10, fontWeight:700, color:GRAY, marginBottom:6 }}>CLIENT TIER MIX</div>
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                      {tierBreak.map(t=>(
                        <span key={t.tier} style={{ background:t.cfg.bg, color:t.cfg.text, border:`1px solid ${t.cfg.border}`, borderRadius:4, padding:"3px 8px", fontSize:10, fontWeight:700 }}>
                          {t.cfg.icon} {t.tier} × {t.count}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Client list with health + tier badges */}
                  <div style={{ padding:"10px 18px 14px" }}>
                    <div style={{ fontSize:10, fontWeight:700, color:GRAY, marginBottom:6 }}>CLIENT LOAD</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {am.clients.map(c => {
                        const tc = ACCOUNT_TIER_CFG[getTier(c)];
                        return (
                          <div key={c.id} style={{ padding:"3px 8px", background:HEALTH_CFG[c.health]?.bg||CARD_BG, border:`1px solid ${HEALTH_CFG[c.health]?.border||BORDER}`, borderRadius:4, fontSize:10, color:HEALTH_CFG[c.health]?.text||DARK, display:"flex", alignItems:"center", gap:4 }}>
                            {c.name.split(" ")[0]}
                            {tc && <span style={{ fontSize:9 }}>{tc.icon}</span>}
                            <span style={{ fontWeight:700, opacity:0.7 }}>{c.complexity}★</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Allocation by Role */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px", marginBottom:14 }}>
            <div style={{ fontSize:14, fontWeight:700, color:DARK, marginBottom:4 }}>AM Time Allocation by Role</div>
            <div style={{ fontSize:11, color:GRAY, marginBottom:14 }}>How a full-time AM at Helmsman splits their working hours across responsibilities</div>
            {AM_ROLES.map((r,i) => {
              const ROLE_COLORS = ["#0D5C63","#14B8C4","#7C3AED","#D97706","#1D4ED8","#059669"];
              const col = ROLE_COLORS[i];
              return (
                <div key={r.role} style={{ display:"grid", gridTemplateColumns:"200px 1fr 44px", gap:12, alignItems:"center", marginBottom:10 }}>
                  <div>
                    <span style={{ fontSize:14, marginRight:6 }}>{r.icon}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:DARK }}>{r.role}</span>
                  </div>
                  <div>
                    <div style={{ height:14, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                      <div style={{ width:`${r.pct}%`, height:"100%", background:col, borderRadius:99,
                        transition:"width 0.3s" }} />
                    </div>
                    <div style={{ fontSize:10, color:GRAY, marginTop:3 }}>{r.desc}</div>
                  </div>
                  <div style={{ fontWeight:800, fontSize:13, color:col, textAlign:"right" }}>{r.pct}%</div>
                </div>
              );
            })}
            <div style={{ marginTop:10, background:CARD_BG, border:`1px solid ${BORDER}`, borderRadius:7, padding:"9px 12px", fontSize:11, color:GRAY, lineHeight:1.6 }}>
              At <strong style={{ color:DARK }}>{AM_CAPACITY_TARGET} clients/AM (target)</strong> with the above mix: each client receives ~{((AM_ROLES.find(r=>r.role==="Account Management")?.pct||30)/100*40*52/AM_CAPACITY_TARGET).toFixed(1)} hrs/yr of direct account management attention.
            </div>
          </div>

          {/* Scale Projection — with tier mix, NO salary amounts */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px" }}>
            <div style={{ fontSize:14, fontWeight:700, color:DARK, marginBottom:4 }}>Scale Projection — Helmsman at 500 Clients</div>
            <div style={{ fontSize:11, color:GRAY, marginBottom:14 }}>Revenue estimate assumes current avg retainer & case rate. Assumes 75% target utilization (25% buffer). Classify accounts by tier to understand mix quality.</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
              {[
                { n:25,  tierMix:{ Strategic:2, Big:6,  Medium:8,  Small:7,  Micro:2  } },
                { n:100, tierMix:{ Strategic:5, Big:20, Medium:30, Small:30, Micro:15 } },
                { n:250, tierMix:{ Strategic:8, Big:40, Medium:70, Small:90, Micro:42 } },
                { n:500, tierMix:{ Strategic:10,Big:80, Medium:140,Small:180,Micro:90 } },
              ].map(({ n, tierMix }) => {
                const amsNeeded  = Math.ceil(n / AM_CAPACITY_TARGET);
                const avgRevClient = portAnn / HM_CLIENTS.length;
                const revEst     = n * avgRevClient;
                const freightEst = n * (portCases/HM_CLIENTS.length) * freightPerCase * freightMarkup;
                const totalRevEst= revEst + freightEst;
                return (
                  <div key={n} style={{ background:CARD_BG, border:`1px solid ${BORDER}`, borderRadius:8, padding:"13px 14px" }}>
                    <div style={{ fontSize:18, fontWeight:800, color:TEAL, marginBottom:2 }}>{n} clients</div>
                    <div style={{ fontSize:10, color:GRAY, marginBottom:10 }}>{amsNeeded} AM{amsNeeded>1?"s":""} · {AM_CAPACITY_TARGET} clients/AM target</div>
                    {[
                      { l:"Est. Revenue",      v:fmt(revEst),      color:DARK },
                      { l:"+ Transport Margin",v:fmt(freightEst),  color:"#7C3AED" },
                      { l:"Total Est. Revenue",v:fmt(totalRevEst), color:GREEN, bold:true },
                    ].map((k,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderBottom:`1px solid ${BORDER}`, fontSize:11 }}>
                        <span style={{ color:k.bold?DARK:GRAY, fontWeight:k.bold?700:400 }}>{k.l}</span>
                        <span style={{ fontWeight:k.bold?800:700, color:k.color }}>{k.v}</span>
                      </div>
                    ))}
                    <div style={{ marginTop:10, fontSize:10, fontWeight:700, color:GRAY, marginBottom:5 }}>TIER MIX</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {Object.entries(tierMix).map(([tier,count]) => {
                        const tc = ACCOUNT_TIER_CFG[tier];
                        return count > 0 ? (
                          <span key={tier} style={{ background:tc?.bg||CARD_BG, color:tc?.text||DARK, border:`1px solid ${tc?.border||BORDER}`, borderRadius:3, padding:"2px 6px", fontSize:9.5, fontWeight:700 }}>
                            {tc?.icon} {tier[0]}:{count}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 8 — BRANDS (2-level hierarchy: Client → Brands)
      ══════════════════════════════════════════════════════════════ */}
      {tab==="brands" && (() => {
        // Sort & filter brands
        const bFiltered = HM_BRANDS
          .filter(b => brandFilt==="All" || b.am===brandFilt)
          .filter(b => !brandClient || b.clientId===brandClient);
        const bSorted = [...bFiltered].sort((a,z) => {
          const va = a[brandSort], vb = z[brandSort];
          if (typeof va==="string") return brandDir==="desc"?vb.localeCompare(va):va.localeCompare(vb);
          return brandDir==="desc"?(vb||0)-(va||0):(va||0)-(vb||0);
        });
        const totalBrands   = HM_BRANDS.length;
        const activeBrands  = HM_BRANDS.filter(b=>b.stage==="ACTIVE").length;
        const launchBrands  = HM_BRANDS.filter(b=>b.stage==="LAUNCH").length;
        const watchBrands   = HM_BRANDS.filter(b=>b.stage==="WATCH").length;
        const inactiveBrands= HM_BRANDS.filter(b=>b.stage==="INACTIVE").length;
        const totalBrandCases = bFiltered.reduce((s,b)=>s+b.ytdCases,0);
        const totalBrandRev   = bFiltered.reduce((s,b)=>s+b.ytdRev,0);
        const maxCases = Math.max(...bFiltered.map(b=>b.ytdCases));

        const BSortTH = ({ col, children, right }) => (
          <th onClick={()=>{ if(brandSort===col) setBrandDir(d=>d==="desc"?"asc":"desc"); else{ setBrandSort(col); setBrandDir("desc"); }}}
            style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:right?"right":"left",
              fontSize:10.5, fontWeight:600, whiteSpace:"nowrap", cursor:"pointer", userSelect:"none" }}>
            {children}{brandSort===col?(brandDir==="desc"?" ↓":" ↑"):""}
          </th>
        );

        return (
          <div>
            {/* KPI strip */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10, marginBottom:14 }}>
              {[
                { l:"Total Brands",    v:totalBrands,   color:TEAL,  hi:true  },
                { l:"Active",          v:activeBrands,  color:GREEN          },
                { l:"Launch",          v:launchBrands,  color:"#1D4ED8"      },
                { l:"Watch",           v:watchBrands,   color:AMBER          },
                { l:"Inactive",        v:inactiveBrands,color:GRAY           },
                { l:"Avg Brands/Client",v:(totalBrands/HM_CLIENTS.length).toFixed(1), color:DARK },
              ].map((k,i)=>(
                <div key={i} style={{ background:k.hi?`${TEAL}10`:WHITE, border:`1px solid ${k.hi?TEAL:BORDER}`, borderRadius:8, padding:"11px 13px" }}>
                  <div style={{ fontSize:9.5, color:GRAY, fontWeight:600, textTransform:"uppercase", marginBottom:4 }}>{k.l}</div>
                  <div style={{ fontSize:20, fontWeight:800, color:k.color }}>{k.v}</div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"10px 14px", marginBottom:12, display:"flex", gap:16, alignItems:"center", flexWrap:"wrap" }}>
              <AMFilter value={brandFilt} onChange={v=>{ setBrandFilt(v); setBrandClient(null); }} />
              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                <span style={{ fontSize:10, color:GRAY, fontWeight:600 }}>CLIENT:</span>
                <select value={brandClient||""} onChange={e=>setBrandClient(e.target.value||null)}
                  style={{ padding:"4px 8px", border:`1px solid ${BORDER}`, borderRadius:5, fontSize:11, color:DARK, background:WHITE }}>
                  <option value="">All clients</option>
                  {HM_CLIENTS.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                <span style={{ fontSize:10, color:GRAY, fontWeight:600 }}>STAGE:</span>
                {["ALL","ACTIVE","LAUNCH","WATCH","INACTIVE"].map(s=>{
                  const cfg = s==="ALL" ? null : BRAND_STAGE_CFG[s];
                  return (
                    <button key={s} style={{ padding:"3px 10px", border:`1px solid ${BORDER}`, borderRadius:4,
                      background:WHITE, color:cfg?.text||GRAY, fontSize:10.5, cursor:"pointer" }}>
                      {s==="ALL"?"All":cfg?.label}
                    </button>
                  );
                })}
              </div>
              <span style={{ marginLeft:"auto", fontSize:11, color:GRAY }}>
                <strong style={{ color:DARK }}>{bFiltered.length} brands</strong> · {totalBrandCases.toLocaleString(undefined,{maximumFractionDigits:0})} cases · {fmt(totalBrandRev)} rev YTD
              </span>
            </div>

            {/* Brand table */}
            <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    <BSortTH col="name">Brand</BSortTH>
                    <BSortTH col="clientId">Client</BSortTH>
                    <th style={{ background:TEAL, color:WHITE, padding:"7px 10px", fontSize:10.5, fontWeight:600 }}>AM</th>
                    <th style={{ background:TEAL, color:WHITE, padding:"7px 10px", fontSize:10.5, fontWeight:600 }}>Stage</th>
                    <th style={{ background:TEAL, color:WHITE, padding:"7px 10px", fontSize:10.5, fontWeight:600 }}>Cat.</th>
                    <BSortTH col="skus" right>SKUs</BSortTH>
                    <BSortTH col="ytdCases" right>Cases YTD</BSortTH>
                    <BSortTH col="lyCases" right>Cases LY</BSortTH>
                    <th style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:"right", fontSize:10.5, fontWeight:600 }}>YoY</th>
                    <BSortTH col="ytdRev" right>Revenue YTD</BSortTH>
                    <th style={{ background:TEAL, color:WHITE, padding:"7px 10px", textAlign:"right", fontSize:10.5, fontWeight:600 }}>% of Client</th>
                  </tr>
                </thead>
                <tbody>
                  {bSorted.map((b,i) => {
                    const client   = HM_CLIENTS.find(c=>c.id===b.clientId);
                    const am       = ACCOUNT_MANAGERS.find(a=>a.id===b.am);
                    const cfg      = BRAND_STAGE_CFG[b.stage];
                    const lyPace   = (b.lyCases / 12) * YTD_MONTHS;
                    const yoy      = lyPace > 0 ? ((b.ytdCases - lyPace) / lyPace) * 100 : null;
                    const clientTotalCases = (BRANDS_BY_CLIENT[b.clientId]||[]).reduce((s,x)=>s+x.ytdCases,0);
                    const pctOfClient = clientTotalCases > 0 ? (b.ytdCases / clientTotalCases * 100) : 0;
                    const CAT_LABELS = { cachaca:"Cachaça", tequila:"Tequila", gin:"Gin", rtd:"RTD", aquavit:"Aquavit", bourbon:"Bourbon", other:"Other" };
                    return (
                      <tr key={b.id} style={{ background:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                        <td style={{ padding:"8px 10px", fontWeight:700, color:DARK }}>{b.name}</td>
                        <td style={{ padding:"8px 10px" }}>
                          <button onClick={()=>setBrandClient(c=>c===b.clientId?null:b.clientId)}
                            style={{ background:"none", border:"none", color:TEAL, fontSize:11, fontWeight:600, cursor:"pointer", padding:0, textAlign:"left" }}>
                            {client?.name||b.clientId}
                          </button>
                        </td>
                        <td style={{ padding:"8px 10px" }}><AMAvatar am={am} size={17}/></td>
                        <td style={{ padding:"8px 10px" }}>
                          <span style={{ background:cfg.bg, color:cfg.text, border:`1px solid ${cfg.border}`, borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{cfg.label}</span>
                        </td>
                        <td style={{ padding:"8px 10px", color:GRAY, fontSize:11 }}>{CAT_LABELS[b.category]||b.category}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right", color:GRAY }}>{b.skus}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5, justifyContent:"flex-end" }}>
                            <div style={{ width:50, height:6, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                              <div style={{ width:`${maxCases>0?(b.ytdCases/maxCases)*100:0}%`, height:"100%", background:TEAL, borderRadius:99 }} />
                            </div>
                            <span style={{ fontWeight:700 }}>{b.ytdCases.toLocaleString()}</span>
                          </div>
                        </td>
                        <td style={{ padding:"8px 10px", textAlign:"right", color:GRAY }}>{lyPace.toFixed(0)}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right" }}>
                          {yoy!==null ? (
                            <span style={{ fontWeight:700, color:yoy>5?GREEN:yoy<-5?RED:AMBER }}>{yoy>=0?"▲":"▼"}{Math.abs(yoy).toFixed(0)}%</span>
                          ) : <span style={{ color:GRAY_MID, fontSize:10 }}>New</span>}
                        </td>
                        <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:600, color:TEAL }}>{fmt(b.ytdRev)}</td>
                        <td style={{ padding:"8px 10px", textAlign:"right" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5, justifyContent:"flex-end" }}>
                            <div style={{ width:36, height:6, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                              <div style={{ width:`${Math.min(100,pctOfClient)}%`, height:"100%", background:TEAL2, borderRadius:99 }} />
                            </div>
                            <span style={{ fontSize:11, color:GRAY }}>{pctOfClient.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background:"#EAF3F4", borderTop:`2px solid #B2D8DC` }}>
                    <td colSpan={6} style={{ padding:"8px 10px", fontWeight:800 }}>Total ({bFiltered.length} brands)</td>
                    <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:800 }}>{totalBrandCases.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                    <td colSpan={2} style={{ padding:"8px 10px" }}></td>
                    <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:800, color:TEAL }}>{fmt(totalBrandRev)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Client brand breakdown cards — shown when a client is selected */}
            {brandClient && (() => {
              const client = HM_CLIENTS.find(c=>c.id===brandClient);
              const cBrands = BRANDS_BY_CLIENT[brandClient]||[];
              const cTotal  = cBrands.reduce((s,b)=>s+b.ytdCases,0);
              return (
                <div style={{ marginTop:14, background:WHITE, border:`2px solid ${TEAL}33`, borderRadius:10, padding:"16px 20px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:800, color:DARK }}>{client?.name} — Brand Breakdown</div>
                      <div style={{ fontSize:11, color:GRAY, marginTop:2 }}>{cBrands.length} brands · {cTotal.toLocaleString(undefined,{maximumFractionDigits:0})} cases YTD</div>
                    </div>
                    <button onClick={()=>setBrandClient(null)} style={{ padding:"4px 10px", border:`1px solid ${BORDER}`, borderRadius:5, background:WHITE, color:GRAY, fontSize:11, cursor:"pointer" }}>✕ Clear</button>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {[...cBrands].sort((a,b)=>b.ytdCases-a.ytdCases).map(b => {
                      const pct = cTotal>0?(b.ytdCases/cTotal*100):0;
                      const cfg = BRAND_STAGE_CFG[b.stage];
                      return (
                        <div key={b.id} style={{ display:"grid", gridTemplateColumns:"200px 1fr 80px 80px 80px", gap:12, alignItems:"center", padding:"8px 12px", background:CARD_BG, borderRadius:7, border:`1px solid ${BORDER}` }}>
                          <div style={{ fontWeight:700, color:DARK, fontSize:12 }}>{b.name}</div>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <div style={{ flex:1, height:8, background:BORDER, borderRadius:99, overflow:"hidden" }}>
                              <div style={{ width:`${pct}%`, height:"100%", background:TEAL, borderRadius:99 }} />
                            </div>
                            <span style={{ fontSize:11, color:GRAY, width:32, textAlign:"right" }}>{pct.toFixed(0)}%</span>
                          </div>
                          <div style={{ textAlign:"right", fontWeight:700, fontSize:12 }}>{b.ytdCases.toLocaleString()}<span style={{ fontSize:10, fontWeight:400, color:GRAY }}> cs</span></div>
                          <div style={{ textAlign:"right", fontSize:11, color:TEAL, fontWeight:600 }}>{fmt(b.ytdRev)}</div>
                          <div><span style={{ background:cfg.bg, color:cfg.text, border:`1px solid ${cfg.border}`, borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{cfg.label}</span></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════
          TAB 9 — CONTRACTS & FEES
          Shows: contract type, notice period, exit fee, retainer edits
      ══════════════════════════════════════════════════════════════ */}
      {tab==="fees" && (
        <div>
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px", marginBottom:14 }}>
            <div style={{ fontSize:14, fontWeight:700, color:DARK, marginBottom:12 }}>Global Fee Settings</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
              <div style={{ background:CARD_BG, border:`1px solid ${BORDER}`, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:9.5, color:GRAY, fontWeight:700, textTransform:"uppercase", marginBottom:8 }}>Case Fee Rate ($/case)</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:14, color:GRAY }}>$</span>
                  <input type="number" min="0" step="0.25" value={caseFeeRate}
                    onChange={e => setCaseFeeRate(parseFloat(e.target.value)||0)}
                    style={{ width:80, padding:"6px 8px", border:`1.5px solid ${TEAL}`, borderRadius:6, fontSize:15, fontWeight:700, color:DARK, textAlign:"center" }} />
                  <span style={{ fontSize:11, color:GRAY }}>/case</span>
                </div>
              </div>
              {[
                { l:"Retainer YTD",        v:fmt(portRet),   sub:`${YTD_MONTHS} months`, color:TEAL  },
                { l:"Case Fees YTD",       v:fmt(portCase),  sub:`${portCases.toLocaleString(undefined,{maximumFractionDigits:0})} cases`, color:TEAL },
                { l:"Total Helmsman YTD",  v:fmt(portTotal), sub:`${fmt(portAnn)} annualized`, color:GREEN, hi:true },
              ].map((k,i)=>(
                <div key={i} style={{ background:k.hi?GREEN_BG:CARD_BG, border:`1px solid ${k.hi?"#86EFAC":BORDER}`, borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:9.5, color:GRAY, fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>{k.l}</div>
                  <div style={{ fontSize:18, fontWeight:800, color:k.color }}>{k.v}</div>
                  <div style={{ fontSize:10, color:GRAY, marginTop:3 }}>{k.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contract exposure summary */}
          <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderLeft:`3px solid #1D4ED8`, borderRadius:8, padding:"12px 16px", marginBottom:14, fontSize:11, color:"#1E40AF" }}>
            <strong>Standard Contract Terms:</strong> 5-month cancellation notice period for all clients. Annual contracts still in term: early exit fee of $5,000 (standard) or $7,500 (premium). Monthly contracts: client can cancel with 5 months notice — no additional penalty.
          </div>

          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 18px", borderBottom:`1px solid ${BORDER}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:14, fontWeight:700, color:DARK }}>Contracts & Retainers — All Clients</div>
              <div style={{ fontSize:11, color:GRAY }}>Click ✏ to edit retainer · Updates all tabs live</div>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr>
                  {[["Client",""],["AM","55px"],["Contract","90px"],["End Date","90px"],["Exit Fee","80px"],["Notice","70px"],
                    ["Monthly Retainer","130px"],["Retainer YTD","100px"],["Case Fees YTD","105px"],
                    ["Total YTD","110px"],["Annual Rate","110px"],["Cancel Exposure","120px"]].map(([h,w],idx)=>(
                    <th key={idx} style={{ background:TEAL, color:WHITE, padding:"7px 10px",
                      textAlign:["Monthly Retainer","Retainer YTD","Case Fees YTD","Total YTD","Annual Rate","Cancel Exposure","Exit Fee"].includes(h)?"right":"left",
                      fontSize:10.5, fontWeight:600, whiteSpace:"nowrap", width:w||"auto" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {HM_CLIENTS_ENRICHED.sort((a,b)=>totalYTD(b)-totalYTD(a)).map((c,i) => {
                  const ret  = retYTD(c);
                  const cf   = caseYTD(c);
                  const tot  = ret + cf;
                  const ann  = annRate(c);
                  const ct   = HM_CONTRACTS[c.id];
                  const exp  = contractExposure(c);
                  const isA  = ct?.type === "annual";
                  const isEdit = feeEditId === c.id;
                  const daysToEnd = ct?.endDate ? Math.round((new Date(ct.endDate)-new Date())/86400000) : null;
                  const expireSoon = daysToEnd !== null && daysToEnd > 0 && daysToEnd <= 90;
                  return (
                    <tr key={c.id} style={{ background:expireSoon?`${AMBER}08`:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}` }}>
                      <td style={{ padding:"7px 10px", fontWeight:600, color:DARK }}>{c.name}</td>
                      <td style={{ padding:"7px 10px" }}><AMAvatar am={c.am} size={18} /></td>
                      <td style={{ padding:"7px 10px" }}>
                        <span style={{ background:isA?GREEN_BG:CARD_BG, color:isA?GREEN:GRAY, border:`1px solid ${isA?"#86EFAC":BORDER}`, borderRadius:4, padding:"2px 7px", fontSize:9.5, fontWeight:700 }}>
                          {isA?"Annual":"Monthly"}
                        </span>
                      </td>
                      <td style={{ padding:"7px 10px", fontSize:10 }}>
                        {ct?.endDate ? (
                          <span style={{ color:expireSoon?AMBER:GRAY, fontWeight:expireSoon?700:400 }}>
                            {ct.endDate.slice(0,10)}{expireSoon?` (${daysToEnd}d)`:""}
                          </span>
                        ) : <span style={{ color:GRAY_MID }}>—</span>}
                      </td>
                      <td style={{ padding:"7px 10px", textAlign:"right" }}>
                        {isA && ct?.endDate && new Date(ct.endDate)>new Date() ? (
                          <span style={{ fontWeight:700, color:AMBER }}>${(ct.exitFee||0).toLocaleString()}</span>
                        ) : <span style={{ color:GRAY_MID }}>—</span>}
                      </td>
                      <td style={{ padding:"7px 10px", textAlign:"right", fontSize:11, color:GRAY }}>{ct?.noticeMos||5} mo</td>
                      <td style={{ padding:"7px 10px", textAlign:"right" }}>
                        {isEdit ? (
                          <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:4 }}>
                            <span style={{ color:GRAY, fontSize:11 }}>$</span>
                            <input autoFocus type="number" value={feeDraft}
                              onChange={e=>setFeeDraft(e.target.value)}
                              onKeyDown={e=>{ if(e.key==="Enter") saveFeeEdit(c.id); if(e.key==="Escape") setFeeEditId(null); }}
                              style={{ width:72, padding:"3px 5px", border:`1.5px solid ${TEAL}`, borderRadius:5, fontSize:12, textAlign:"right", fontWeight:700 }} />
                            <button onClick={()=>saveFeeEdit(c.id)} style={{ padding:"2px 6px", background:TEAL, border:"none", borderRadius:4, color:WHITE, fontSize:11, fontWeight:700, cursor:"pointer" }}>✓</button>
                            <button onClick={()=>setFeeEditId(null)} style={{ padding:"2px 5px", background:WHITE, border:`1px solid ${BORDER}`, borderRadius:4, color:GRAY, fontSize:11, cursor:"pointer" }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:6 }}>
                            <span style={{ fontWeight:700 }}>${(monthlyFees[c.id]||0).toLocaleString()}<span style={{ fontWeight:400, color:GRAY }}>/mo</span></span>
                            <button onClick={()=>startFeeEdit(c)} style={{ padding:"2px 6px", border:`1px solid ${BORDER}`, borderRadius:4, background:WHITE, color:GRAY, fontSize:10, cursor:"pointer" }}>✏</button>
                          </div>
                        )}
                      </td>
                      <td style={{ padding:"7px 10px", textAlign:"right", color:TEAL, fontWeight:600 }}>{fmt(ret)}</td>
                      <td style={{ padding:"7px 10px", textAlign:"right", color:TEAL, fontWeight:600 }}>{fmt(cf)}</td>
                      <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:800, fontSize:13, color:DARK }}>{fmt(tot)}</td>
                      <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:700, color:GREEN }}>{fmt(ann)}</td>
                      <td style={{ padding:"7px 10px", textAlign:"right" }}>
                        <span style={{ fontWeight:700, color:isA&&ct?.endDate&&new Date(ct.endDate)>new Date()?AMBER:DARK }}>
                          {fmt(exp)}
                        </span>
                        <div style={{ fontSize:9.5, color:GRAY }}>{isA&&ct?.endDate&&new Date(ct.endDate)>new Date()?"exit fee":"5-mo notice"}</div>
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background:"#EAF3F4", borderTop:`2px solid #B2D8DC` }}>
                  <td colSpan={6} style={{ padding:"9px 10px", fontWeight:800 }}>Total</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800 }}>${Math.round(HM_CLIENTS_ENRICHED.reduce((s,c)=>s+(monthlyFees[c.id]||0),0)).toLocaleString()}/mo avg</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, color:TEAL }}>{fmt(portRet)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, color:TEAL }}>{fmt(portCase)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, fontSize:14 }}>{fmt(portTotal)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800, color:GREEN }}>{fmt(portAnn)}</td>
                  <td style={{ padding:"9px 10px", textAlign:"right", fontWeight:800 }}>{fmt(HM_CLIENTS_ENRICHED.reduce((s,c)=>s+contractExposure(c),0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
function ProductManagement({ navigate }) {
  const [search,     setSearch]     = useState("");
  const [brandF,     setBrandF]     = useState("All");
  const [categoryF,  setCategoryF]  = useState("All");
  const [statusF,    setStatusF]    = useState("All");
  const [stageF,     setStageF]     = useState("All");
  const [ttbF,       setTtbF]       = useState("All");  // All | Missing | Present
  const [selectedId, setSelectedId] = useState(null);
  const [page,       setPage]       = useState(1);
  const [editId,     setEditId]     = useState(null);
  const [editDraft,  setEditDraft]  = useState(null);
  const rowsPerPage = 8;

  const brands = ["All", ...Array.from(new Set(PRODUCT_CATALOG.map(p => p.brand)))];

  const filtered = PRODUCT_CATALOG.filter(p => {
    const q = search.toLowerCase();
    if (q && !p.name.toLowerCase().includes(q) && !p.code.toLowerCase().includes(q) && !p.brand.toLowerCase().includes(q)) return false;
    if (brandF    !== "All" && p.brand    !== brandF)    return false;
    if (categoryF !== "All" && p.category !== categoryF) return false;
    if (statusF   !== "All" && p.status   !== statusF)   return false;
    if (stageF    !== "All" && p.stage    !== stageF)    return false;
    if (ttbF === "Missing" && p.ttbId)  return false;
    if (ttbF === "Present" && !p.ttbId) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const visible    = filtered.slice((page-1)*rowsPerPage, page*rowsPerPage);
  const selected   = PRODUCT_CATALOG.find(p => p.id === selectedId);

  // Summary stats
  const missingTTB  = PRODUCT_CATALOG.filter(p => !p.ttbId).length;
  const reviewCount = PRODUCT_CATALOG.filter(p => p.status === "Review").length;
  const launchCount = PRODUCT_CATALOG.filter(p => p.stage === "LAUNCH").length;
  const noCatCount  = PRODUCT_CATALOG.filter(p => p.category === "other" && p.line !== "Gajardo").length;

  function startEdit(p) { setEditId(p.id); setEditDraft({ ...p }); setSelectedId(p.id); }
  function saveEdit() { setEditId(null); setEditDraft(null); } // In real app: persist to DB

  return (
    <div>
      {/* ── Alerts ──────────────────────────────────────────────────────── */}
      {missingTTB > 0 && (
        <Alert type="critical">
          <strong>{missingTTB} product{missingTTB>1?"s":""} missing TTB ID</strong> — cannot be legally sold without a COLA registration.{" "}
          <span onClick={() => setTtbF("Missing")} style={{ color: RED, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Filter to missing →</span>
        </Alert>
      )}
      {reviewCount > 0 && (
        <Alert type="warning">
          <strong>{reviewCount} product{reviewCount>1?"s":""} flagged for Review</strong> — low velocity, low DOH, or EXIT-tier candidates. Confirm SKU lifecycle status.
        </Alert>
      )}

      {/* ── Summary strip ──────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 14 }}>
        {[
          { label: "Total Products",     value: PRODUCT_CATALOG.length, sub: `${filtered.length} matching filters`, warn: false },
          { label: "Missing TTB ID",     value: missingTTB,  sub: "Compliance risk",       warn: missingTTB > 0  },
          { label: "Under Review",       value: reviewCount, sub: "EXIT candidates",        warn: reviewCount > 0 },
          { label: "Launch Stage",       value: launchCount, sub: "Using ramp targets",     warn: false           },
          { label: "Unclassified Cat.",  value: noCatCount,  sub: "Using default growth",   warn: noCatCount > 0  },
        ].map((c,i) => (
          <div key={i} style={{ background: c.warn?RED_BG:WHITE, border:`1px solid ${c.warn?"#FCA5A5":BORDER}`, borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:10, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:4 }}>{c.label}</div>
            <div style={{ fontSize:20, fontWeight:800, color:c.warn?RED:DARK }}>{c.value}</div>
            <div style={{ fontSize:10, color:GRAY, marginTop:2 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selectedId ? "1fr 340px" : "1fr", gap: 16, alignItems: "start" }}>
        <div>
          {/* ── Search + Filters ─────────────────────────────────────────── */}
          <div style={{ background: WHITE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"12px 16px", marginBottom:12 }}>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
              {/* Search */}
              <div style={{ position:"relative", flex:1, minWidth:200 }}>
                <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:GRAY_MID, fontSize:13 }}>🔍</span>
                <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
                  placeholder="Search products, codes, brands…"
                  style={{ width:"100%", padding:"7px 10px 7px 30px", border:`1px solid ${BORDER}`, borderRadius:7, fontSize:12, color:DARK, outline:"none" }} />
              </div>
              {/* Filters */}
              {[
                { label:"Brand",    val:brandF,    set:setBrandF,    opts:brands },
                { label:"Category", val:categoryF, set:setCategoryF, opts:["All",...Object.keys(CATEGORY_LABELS)] },
                { label:"Status",   val:statusF,   set:setStatusF,   opts:["All","Active","Review","Inactive"] },
                { label:"Stage",    val:stageF,    set:setStageF,    opts:["All","ESTABLISHED","LAUNCH","WATCH","EXIT"] },
                { label:"TTB ID",   val:ttbF,      set:setTtbF,      opts:["All","Present","Missing"] },
              ].map(({label,val,set,opts}) => (
                <div key={label} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <span style={{ fontSize:10, color:GRAY, fontWeight:600, whiteSpace:"nowrap" }}>{label}:</span>
                  <select value={val} onChange={e=>{set(e.target.value);setPage(1);}}
                    style={{ border:`1px solid ${val!=="All"?TEAL:BORDER}`, borderRadius:6, padding:"5px 8px", fontSize:11, color:val!=="All"?TEAL:DARK, background:val!=="All"?`${TEAL}0D`:WHITE, fontWeight:val!=="All"?700:400, cursor:"pointer" }}>
                    {opts.map(o => <option key={o} value={o}>{o==="All"?`All ${label}s`:CATEGORY_LABELS[o]||o}</option>)}
                  </select>
                </div>
              ))}
              {(search||brandF!=="All"||categoryF!=="All"||statusF!=="All"||stageF!=="All"||ttbF!=="All") && (
                <button onClick={()=>{setSearch("");setBrandF("All");setCategoryF("All");setStatusF("All");setStageF("All");setTtbF("All");setPage(1);}}
                  style={{ padding:"5px 10px", border:`1px solid ${BORDER}`, borderRadius:6, background:WHITE, color:GRAY, fontSize:11, cursor:"pointer" }}>✕ Clear</button>
              )}
              <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                <Btn>↑ Export</Btn>
                <button style={{ padding:"6px 14px", background:TEAL, border:"none", borderRadius:7, color:WHITE, fontSize:12, fontWeight:700, cursor:"pointer" }}>+ New Product</button>
              </div>
            </div>
          </div>

          {/* ── Table ────────────────────────────────────────────────────── */}
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr>
                  <th style={{ background:TEAL, padding:"8px 10px", textAlign:"left", color:WHITE, fontWeight:700, fontSize:11 }}>
                    <input type="checkbox" style={{ accentColor:WHITE }} />
                  </th>
                  {[["Product Code","130px"],["Product Name",""],["Brand","110px"],["Category","100px"],["Stage","90px"],["TTB ID","110px"],["DoH","60px"],["Status","80px"],["","70px"]].map(([h,w])=>(
                    <th key={h} style={{ background:TEAL, padding:"8px 10px", textAlign:"left", color:WHITE, fontWeight:700, fontSize:11, whiteSpace:"nowrap", width:w||"auto" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((p, i) => {
                  const sc  = STAGE_CFG[p.stage] || STAGE_CFG.ESTABLISHED;
                  const isSel = p.id === selectedId;
                  return (
                    <tr key={p.id} onClick={() => setSelectedId(isSel ? null : p.id)}
                      style={{ background: isSel?`${TEAL}0D`:i%2===0?WHITE:CARD_BG, borderBottom:`1px solid ${BORDER}`, cursor:"pointer",
                        outline: isSel?`2px solid ${TEAL}33`:"none", outlineOffset:-1 }}>
                      <td style={{ padding:"8px 10px" }} onClick={e=>e.stopPropagation()}>
                        <input type="checkbox" />
                      </td>
                      <td style={{ padding:"8px 10px", fontFamily:"monospace", fontSize:10.5, color:GRAY, whiteSpace:"nowrap" }}>{p.code}</td>
                      <td style={{ padding:"8px 10px", fontWeight:500, maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={p.name}>
                        {isSel && <span style={{ color:TEAL, marginRight:4, fontSize:10 }}>▶</span>}
                        {p.name}
                        {p.organic && <span style={{ marginLeft:5, fontSize:9, background:"#DCFCE7", color:GREEN, border:"1px solid #86EFAC", borderRadius:3, padding:"1px 5px", fontWeight:700 }}>ORGANIC</span>}
                      </td>
                      <td style={{ padding:"8px 10px", color:GRAY, fontSize:11, whiteSpace:"nowrap" }}>{p.brand.split(" ")[0]}</td>
                      <td style={{ padding:"8px 10px" }}>
                        <span style={{ background:`${TEAL}15`, color:TEAL, borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:600, whiteSpace:"nowrap" }}>
                          {CATEGORY_LABELS[p.category] || p.category}
                        </span>
                      </td>
                      <td style={{ padding:"8px 10px" }}>
                        <span style={{ background:sc.bg, color:sc.text, border:`1px solid ${sc.border}`, borderRadius:4, padding:"2px 6px", fontSize:9.5, fontWeight:700, whiteSpace:"nowrap" }}>
                          {sc.icon} {p.stage}
                        </span>
                      </td>
                      <td style={{ padding:"8px 10px" }}>
                        {p.ttbId
                          ? <span style={{ fontFamily:"monospace", fontSize:10, color:DARK }}>{p.ttbId}</span>
                          : <span style={{ background:RED_BG, color:RED, borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:700 }}>⚠ Missing</span>}
                      </td>
                      <td style={{ padding:"8px 10px", textAlign:"center" }}>
                        <DohPill val={p.doh} />
                      </td>
                      <td style={{ padding:"8px 10px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                          <div style={{ width:7, height:7, borderRadius:"50%", background:STATUS_DOT[p.status]||GRAY_MID }} />
                          <span style={{ fontSize:11, color:DARK }}>{p.status}</span>
                        </div>
                      </td>
                      <td style={{ padding:"8px 10px" }} onClick={e=>e.stopPropagation()}>
                        <button onClick={() => startEdit(p)}
                          style={{ padding:"4px 10px", border:`1px solid ${BORDER}`, borderRadius:5, background:WHITE, color:GRAY, fontSize:11, cursor:"pointer" }}>✏ Edit</button>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={10} style={{ padding:"32px", textAlign:"center", color:GRAY_MID, fontSize:12 }}>No products match your filters</td></tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={{ padding:"10px 16px", borderTop:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:CARD_BG }}>
              <div style={{ fontSize:11, color:GRAY }}>
                Showing {Math.min((page-1)*rowsPerPage+1, filtered.length)}–{Math.min(page*rowsPerPage, filtered.length)} of <strong>{filtered.length}</strong> results
                {filtered.length !== PRODUCT_CATALOG.length && <span style={{ color:GRAY_MID }}> (filtered from {PRODUCT_CATALOG.length} total)</span>}
              </div>
              <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                  style={{ padding:"4px 10px", border:`1px solid ${BORDER}`, borderRadius:5, background:WHITE, color:page===1?GRAY_MID:DARK, fontSize:11, cursor:page===1?"default":"pointer" }}>← Prev</button>
                {Array.from({length:Math.min(totalPages,5)},(_,i)=>i+1).map(n=>(
                  <button key={n} onClick={()=>setPage(n)}
                    style={{ width:28, height:28, border:`1px solid ${n===page?TEAL:BORDER}`, borderRadius:5, background:n===page?TEAL:WHITE, color:n===page?WHITE:DARK, fontSize:11, fontWeight:n===page?700:400, cursor:"pointer" }}>{n}</button>
                ))}
                {totalPages > 5 && <span style={{ color:GRAY_MID, fontSize:11 }}>… {totalPages}</span>}
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                  style={{ padding:"4px 10px", border:`1px solid ${BORDER}`, borderRadius:5, background:WHITE, color:page===totalPages?GRAY_MID:DARK, fontSize:11, cursor:page===totalPages?"default":"pointer" }}>Next →</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Detail / Edit Panel ──────────────────────────────────────── */}
        {selectedId && selected && (
          <div style={{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:10, overflow:"hidden", position:"sticky", top:0 }}>
            {/* Panel header */}
            <div style={{ background:TEAL, padding:"12px 14px", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.6)", fontWeight:700, letterSpacing:"0.1em", marginBottom:1 }}>{editId?selected.brand:"PRODUCT DETAIL"}</div>
                <div style={{ fontSize:12, fontWeight:800, color:WHITE, lineHeight:1.3, maxWidth:230 }}>{selected.name}</div>
              </div>
              <div style={{ display:"flex", gap:5 }}>
                {editId
                  ? <button onClick={saveEdit} style={{ padding:"4px 10px", background:WHITE, border:"none", borderRadius:5, color:TEAL, fontSize:11, fontWeight:800, cursor:"pointer" }}>💾 Save</button>
                  : <button onClick={()=>startEdit(selected)} style={{ padding:"4px 10px", background:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:5, color:WHITE, fontSize:11, cursor:"pointer" }}>✏ Edit</button>
                }
                <button onClick={()=>{setSelectedId(null);setEditId(null);setEditDraft(null);}}
                  style={{ background:"rgba(255,255,255,0.18)", border:"none", borderRadius:4, color:WHITE, cursor:"pointer", fontSize:14, width:22, height:22, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
              </div>
            </div>

            <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:12 }}>
              {/* Product code + type */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  { l:"Product Code",  v:selected.code, mono:true  },
                  { l:"Product Type",  v:selected.type  },
                  { l:"Brand",         v:selected.brand },
                  { l:"Product Line",  v:selected.line  },
                ].map(({l,v,mono})=>(
                  <div key={l} style={{ background:CARD_BG, borderRadius:6, padding:"8px 10px" }}>
                    <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:11, fontWeight:600, color:DARK, fontFamily:mono?"monospace":"inherit" }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Category — editable */}
              <div style={{ background:CARD_BG, borderRadius:7, padding:"10px 12px" }}>
                <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>
                  Category <span style={{ color:TEAL, fontSize:9 }}>→ drives growth multiplier</span>
                </div>
                {editId ? (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {Object.entries(CATEGORY_LABELS).map(([k,v])=>(
                      <button key={k} onClick={()=>setEditDraft(d=>({...d,category:k}))}
                        style={{ padding:"4px 10px", border:`1px solid ${editDraft.category===k?TEAL:BORDER}`, borderRadius:5, background:editDraft.category===k?TEAL:WHITE, color:editDraft.category===k?WHITE:DARK, fontSize:11, fontWeight:editDraft.category===k?700:400, cursor:"pointer" }}>
                        {v}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ background:`${TEAL}15`, color:TEAL, borderRadius:4, padding:"3px 10px", fontSize:12, fontWeight:700 }}>
                      {CATEGORY_LABELS[selected.category] || selected.category}
                    </span>
                    <span style={{ fontSize:10, color:GRAY }}>
                      → +{DEFAULT_CATEGORIES.find(c=>c.id===selected.category)?.growth||7}% growth target applied
                    </span>
                  </div>
                )}
              </div>

              {/* Stage — editable */}
              <div style={{ background:CARD_BG, borderRadius:7, padding:"10px 12px" }}>
                <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>
                  Brand Stage <span style={{ color:TEAL, fontSize:9 }}>→ drives target method</span>
                </div>
                {editId ? (
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                    {["ESTABLISHED","LAUNCH","WATCH","EXIT"].map(s=>{
                      const sc = STAGE_CFG[s];
                      return (
                        <button key={s} onClick={()=>setEditDraft(d=>({...d,stage:s}))}
                          style={{ padding:"4px 10px", border:`1px solid ${editDraft.stage===s?sc.text:BORDER}`, borderRadius:5, background:editDraft.stage===s?sc.bg:WHITE, color:editDraft.stage===s?sc.text:DARK, fontSize:11, fontWeight:editDraft.stage===s?700:400, cursor:"pointer" }}>
                          {sc.icon} {s}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    {(() => { const sc = STAGE_CFG[selected.stage]; return (
                      <span style={{ background:sc.bg, color:sc.text, border:`1px solid ${sc.border}`, borderRadius:4, padding:"3px 10px", fontSize:12, fontWeight:700 }}>
                        {sc.icon} {selected.stage}
                      </span>
                    );})()}
                    <span style={{ fontSize:10, color:GRAY }}>
                      {selected.stage==="LAUNCH"?"Using launch ramp targets":selected.stage==="ESTABLISHED"?"Using LY × category multiplier":selected.stage==="EXIT"?"No target generated":"Manual review required"}
                    </span>
                  </div>
                )}
              </div>

              {/* TTB ID — editable */}
              <div style={{ background:selected.ttbId?CARD_BG:RED_BG, border:`1px solid ${selected.ttbId?BORDER:"#FCA5A5"}`, borderRadius:7, padding:"10px 12px" }}>
                <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>
                  TTB / COLA ID {!selected.ttbId && <span style={{ color:RED }}>⚠ Required</span>}
                </div>
                {editId ? (
                  <input value={editDraft.ttbId} onChange={e=>setEditDraft(d=>({...d,ttbId:e.target.value}))}
                    placeholder="e.g. TTB-2024-0088"
                    style={{ width:"100%", padding:"6px 10px", border:`1px solid ${TEAL}`, borderRadius:6, fontSize:12, fontFamily:"monospace", color:DARK }} />
                ) : (
                  selected.ttbId
                    ? <div style={{ fontFamily:"monospace", fontSize:12, color:DARK, fontWeight:600 }}>{selected.ttbId}</div>
                    : <div style={{ fontSize:11, color:RED, fontWeight:600 }}>Not registered — cannot sell legally. Add COLA ID to activate.</div>
                )}
              </div>

              {/* Velocity + DoH */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <div style={{ background:CARD_BG, borderRadius:6, padding:"8px 10px" }}>
                  <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:2 }}>Velocity / mo</div>
                  <div style={{ fontSize:16, fontWeight:700, color:DARK }}>{selected.vel.toFixed(1)} cs</div>
                </div>
                <div style={{ background:selected.doh<45?RED_BG:selected.doh>90?GREEN_BG:AMBER_BG, borderRadius:6, padding:"8px 10px" }}>
                  <div style={{ fontSize:9, color:GRAY, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:2 }}>Days on Hand</div>
                  <div style={{ fontSize:16, fontWeight:700, color:selected.doh<45?RED:selected.doh>90?GREEN:AMBER }}>{selected.doh}d</div>
                </div>
              </div>

              {/* Growth multiplier preview */}
              <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderLeft:`3px solid #1D4ED8`, borderRadius:7, padding:"10px 12px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#1E40AF", marginBottom:5 }}>📈 Auto-Target Method</div>
                <div style={{ fontSize:11, color:"#1E40AF", lineHeight:1.7 }}>
                  {selected.stage === "ESTABLISHED" && <>LY Revenue × <strong>+{DEFAULT_CATEGORIES.find(c=>c.id===selected.category)?.growth||7}%</strong> ({CATEGORY_LABELS[selected.category]} benchmark), distributed using <strong>brand seasonality curve</strong></>}
                  {selected.stage === "LAUNCH"      && <>No LY revenue — applying <strong>{CATEGORY_LABELS[selected.category]} launch ramp</strong>. Year 1 target: <strong>{fmt((DEFAULT_CATEGORIES.find(c=>c.id===selected.category)?.launchRamp||[]).reduce((s,v)=>s+v,0))}</strong></>}
                  {selected.stage === "WATCH"       && <>Monitoring — target generated but flagged. Confirm continuation or move to EXIT.</>}
                  {selected.stage === "EXIT"        && <>No target generated. SKU flagged for phase-out. Consider deregistration to reduce compliance costs.</>}
                </div>
              </div>

              {editId && (
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={saveEdit} style={{ flex:1, padding:"9px", background:TEAL, border:"none", borderRadius:7, color:WHITE, fontSize:12, fontWeight:700, cursor:"pointer" }}>💾 Save Changes</button>
                  <button onClick={()=>{setEditId(null);setEditDraft(null);}} style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:7, background:WHITE, color:GRAY, fontSize:12, cursor:"pointer" }}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE: CATEGORY ADMIN — Target Engine Configuration
// ═════════════════════════════════════════════════════════════════════════════

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Default category configs — the source of truth for auto-generated targets
const DEFAULT_CATEGORIES = [
  {
    id: "cachaca", label: "Cachaça", growth: 5,
    seasonal: [10.2, 8.8, 9.5, 8.1, 7.9, 7.2, 7.8, 7.0, 8.4, 8.6, 7.8, 8.7],
    launchRamp: [2800, 3200, 3800, 4500, 5200, 6000, 6800, 7200, 8000, 8800, 9200, 10000],
    note: "Niche specialty — slower market penetration",
    source: "IWSR 2024 + Helmsman portfolio",
  },
  {
    id: "tequila", label: "Tequila", growth: 8,
    seasonal: [7.8, 7.2, 8.4, 9.1, 10.2, 11.0, 10.5, 9.8, 8.6, 7.9, 5.8, 3.7],
    launchRamp: [4000, 5000, 6500, 8000, 10000, 12000, 13500, 14000, 13000, 12000, 9000, 7000],
    note: "Strong Cinco de Mayo + summer peak. Cooling from 2022–23 peak.",
    source: "IWSR 2024 + Nielsen Off-Premise",
  },
  {
    id: "aquavit", label: "Aquavit", growth: 12,
    seasonal: [9.5, 8.6, 8.8, 7.2, 7.0, 6.8, 6.5, 6.2, 8.0, 9.8, 11.2, 10.4],
    launchRamp: [1800, 2200, 2600, 3000, 3400, 3200, 3000, 3200, 4000, 5200, 6800, 7800],
    note: "Emerging category. Holiday Q4 and Nordic-season Q1 are peak windows.",
    source: "Helmsman portfolio aggregate",
  },
  {
    id: "liqueur", label: "Liqueur / Specialty", growth: 3,
    seasonal: [7.0, 6.5, 7.5, 7.8, 8.2, 8.8, 8.6, 8.4, 8.0, 8.2, 9.0, 12.0],
    launchRamp: [1500, 1800, 2200, 2600, 3000, 3400, 3200, 3000, 3200, 3600, 4500, 5500],
    note: "Mature, defensive category. Holiday bump in Dec is significant.",
    source: "IWSR 2024",
  },
  {
    id: "rtd", label: "RTD / Cocktail", growth: 15,
    seasonal: [4.2, 4.5, 6.8, 9.2, 11.5, 13.8, 14.2, 13.0, 10.5, 7.8, 5.2, 5.3],
    launchRamp: [3000, 3500, 5000, 7500, 10000, 13000, 14000, 12000, 9000, 6500, 4500, 4000],
    note: "Highly seasonal — strong spring/summer sell-through. Minimal winter.",
    source: "IWSR 2024 RTD Report",
  },
  {
    id: "gin", label: "Gin", growth: 6,
    seasonal: [7.2, 6.8, 8.0, 9.0, 10.5, 11.8, 11.2, 10.0, 8.8, 8.0, 5.8, 2.9],
    launchRamp: [2000, 2400, 3000, 3800, 5000, 6200, 6000, 5200, 4500, 4000, 3000, 2000],
    note: "Summer-forward. G&T culture drives May–Jul peak.",
    source: "IWSR 2024",
  },
  {
    id: "other", label: "Other / Unclassified", growth: 7,
    seasonal: [8.5, 7.8, 8.8, 8.2, 8.5, 8.2, 8.0, 7.8, 8.2, 8.5, 8.8, 10.7],
    launchRamp: [2500, 2800, 3200, 3600, 4000, 4200, 4000, 3800, 4000, 4400, 4800, 6000],
    note: "Portfolio average. Apply when category is unclear.",
    source: "Helmsman portfolio average",
  },
];

function SeasonalityBar({ values }) {
  const max = Math.max(...values);
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 36 }}>
      {values.map((v, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <div style={{ width: "100%", background: TEAL, borderRadius: "2px 2px 0 0", opacity: 0.6 + (v / max) * 0.4,
            height: `${(v / max) * 28}px`, minHeight: 2 }} />
          <div style={{ fontSize: 7, color: GRAY_MID, lineHeight: 1 }}>{MONTH_LABELS[i].slice(0,1)}</div>
        </div>
      ))}
    </div>
  );
}

function CategoryAdmin() {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selected,   setSelected]   = useState("cachaca");
  const [editMode,   setEditMode]   = useState(false);
  const [draft,      setDraft]      = useState(null);
  const [saved,      setSaved]      = useState(false);
  const [tab,        setTab]        = useState("growth"); // growth | seasonal | launch | brands

  const cat = categories.find(c => c.id === selected);

  function startEdit() { setDraft(JSON.parse(JSON.stringify(cat))); setEditMode(true); setSaved(false); }
  function cancelEdit() { setEditMode(false); setDraft(null); }
  function saveEdit() {
    setCategories(cs => cs.map(c => c.id === selected ? { ...draft } : c));
    setEditMode(false); setDraft(null); setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const cur = editMode ? draft : cat;
  const seasonalTotal = cur ? cur.seasonal.reduce((s,v)=>s+v,0) : 0;

  // Example: what a $500K brand's monthly targets look like
  const EXAMPLE_BASE = 500000;
  const exampleTargets = cur ? cur.seasonal.map(s => Math.round((s / 100) * EXAMPLE_BASE * (1 + cur.growth / 100))) : [];

  // Brand stage examples
  const BRAND_STAGES = [
    { stage: "ESTABLISHED", icon: "🏆", desc: "12+ months of sales history", target: "LY Total × (1 + category growth%)", example: `$500K × ${100 + (cat?.growth||0)}% = $${((500000 * (1 + (cat?.growth||0)/100))/1000).toFixed(0)}K`, color: GREEN, bg: GREEN_BG, border: "#86EFAC" },
    { stage: "RESTART",     icon: "🔄", desc: "Has history but gap year (LY = $0)", target: "Last active year × multiplier + flag", example: "2024 revenue used, gap year noted", color: TEAL, bg: "#E8F4F5", border: "#B2D8DC" },
    { stage: "LAUNCH",      icon: "🚀", desc: "New brand, starting from $0", target: "Category launch ramp curve (monthly $)", example: `Month 1: ${fmtK(cat?.launchRamp?.[0]||0)}, Month 6: ${fmtK(cat?.launchRamp?.[5]||0)}, Month 12: ${fmtK(cat?.launchRamp?.[11]||0)}`, color: AMBER, bg: AMBER_BG, border: "#FCD34D" },
    { stage: "INCOMPLETE",  icon: "⏳", desc: "< 6 months of data", target: "Partial history annualized × category growth", example: "3-month average × 12 × multiplier", color: "#7C3AED", bg: "#F5F3FF", border: "#C4B5FD" },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 20px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 20 }}>⚙️</span>
              <div style={{ fontSize: 18, fontWeight: 800, color: DARK }}>Target Engine — Admin Config</div>
              <span style={{ background: RED_BG, color: RED, border: `1px solid #FCA5A5`, borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>HELMSMAN ONLY</span>
            </div>
            <div style={{ fontSize: 12, color: GRAY, maxWidth: 580, lineHeight: 1.6 }}>
              Configure category growth multipliers, seasonality curves, and launch ramps. These settings auto-generate monthly targets for all brands — clients don't need to set anything.
            </div>
          </div>
          {saved && (
            <div style={{ background: GREEN_BG, border: `1px solid #86EFAC`, borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: GREEN, display: "flex", alignItems: "center", gap: 6 }}>
              ✅ Saved — propagating to all brands
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 16, alignItems: "start" }}>

        {/* Category list */}
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "11px 14px", borderBottom: `1px solid ${BORDER}`, fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Categories
          </div>
          {categories.map(c => (
            <div key={c.id} onClick={() => { setSelected(c.id); setEditMode(false); setDraft(null); }}
              className="nav-row"
              style={{ padding: "11px 14px", borderBottom: `1px solid ${BORDER}`, cursor: "pointer",
                background: selected===c.id ? `${TEAL}10` : WHITE,
                borderLeft: selected===c.id ? `3px solid ${TEAL}` : "3px solid transparent" }}>
              <div style={{ fontSize: 12, fontWeight: selected===c.id?700:500, color: selected===c.id?TEAL:DARK }}>{c.label}</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                <span style={{ fontSize: 10, color: GRAY }}>Growth</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: c.growth >= 10 ? GREEN : c.growth >= 6 ? TEAL : AMBER }}>+{c.growth}%</span>
              </div>
            </div>
          ))}
          <div style={{ padding: "10px 14px" }}>
            <button style={{ width: "100%", padding: "7px", border: `1.5px dashed ${BORDER}`, borderRadius: 6, background: WHITE, color: GRAY, fontSize: 11, cursor: "pointer" }}>
              + Add Category
            </button>
          </div>
        </div>

        {/* Detail panel */}
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
          {/* Category header */}
          <div style={{ background: TEAL, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: WHITE }}>{cur?.label}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>Source: {cur?.source}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {!editMode
                ? <button onClick={startEdit} style={{ padding: "7px 16px", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 6, color: WHITE, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✏️ Edit</button>
                : <>
                    <button onClick={cancelEdit} style={{ padding: "7px 14px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, color: WHITE, fontSize: 12, cursor: "pointer" }}>Cancel</button>
                    <button onClick={saveEdit} style={{ padding: "7px 16px", background: WHITE, border: "none", borderRadius: 6, color: TEAL, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>💾 Save Changes</button>
                  </>
              }
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${BORDER}`, background: CARD_BG }}>
            {[["growth","📈 Growth"],["seasonal","📅 Seasonality"],["launch","🚀 Launch Ramp"],["brands","🏷 Brand Stages"]].map(([v,l]) => (
              <button key={v} onClick={() => setTab(v)}
                style={{ padding: "9px 16px", border: "none", borderBottom: `2px solid ${tab===v?TEAL:"transparent"}`, background: "transparent", color: tab===v?TEAL:GRAY, fontSize: 12, fontWeight: tab===v?700:400, cursor: "pointer" }}>
                {l}
              </button>
            ))}
          </div>

          <div style={{ padding: "20px" }}>

            {/* ── GROWTH TAB ─────────────────────────────────────────────── */}
            {tab === "growth" && (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 14 }}>Growth Multiplier</div>
                    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "20px", textAlign: "center", marginBottom: 16 }}>
                      {editMode ? (
                        <div>
                          <div style={{ fontSize: 11, color: GRAY, marginBottom: 8 }}>Annual growth rate (%)</div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                            <button onClick={() => setDraft(d => ({ ...d, growth: Math.max(0, d.growth - 1) }))}
                              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${BORDER}`, background: WHITE, fontSize: 18, cursor: "pointer", fontWeight: 700 }}>−</button>
                            <input type="number" value={draft.growth} min={0} max={50} onChange={e => setDraft(d => ({ ...d, growth: Number(e.target.value) }))}
                              style={{ width: 72, textAlign: "center", fontSize: 28, fontWeight: 800, color: TEAL, border: `2px solid ${TEAL}`, borderRadius: 8, padding: "6px" }} />
                            <button onClick={() => setDraft(d => ({ ...d, growth: Math.min(50, d.growth + 1) }))}
                              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${BORDER}`, background: WHITE, fontSize: 18, cursor: "pointer", fontWeight: 700 }}>+</button>
                          </div>
                          <div style={{ fontSize: 10, color: GRAY, marginTop: 8 }}>% year-over-year applied to LY baseline</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 48, fontWeight: 900, color: TEAL, lineHeight: 1 }}>+{cur?.growth}%</div>
                          <div style={{ fontSize: 12, color: GRAY, marginTop: 6 }}>Annual growth rate</div>
                        </div>
                      )}
                    </div>

                    {editMode && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 6 }}>Notes / Rationale</div>
                        <textarea value={draft.note} onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
                          style={{ width: "100%", minHeight: 72, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "8px", fontSize: 11, color: DARK, resize: "vertical" }} />
                      </div>
                    )}
                    {!editMode && cur?.note && (
                      <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "10px 12px", fontSize: 11, color: "#1E40AF", lineHeight: 1.6 }}>
                        💡 {cur.note}
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 14 }}>What This Means — Example</div>
                    <div style={{ fontSize: 11, color: GRAY, marginBottom: 10 }}>For a brand with $500K prior year revenue:</div>
                    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: `${TEAL}15` }}>
                            <th style={{ padding: "7px 10px", textAlign: "left", color: DARK, fontWeight: 700 }}>Month</th>
                            <th style={{ padding: "7px 10px", textAlign: "right", color: DARK, fontWeight: 700 }}>Seasonal %</th>
                            <th style={{ padding: "7px 10px", textAlign: "right", color: DARK, fontWeight: 700 }}>Monthly Target</th>
                          </tr>
                        </thead>
                        <tbody>
                          {MONTH_LABELS.map((m, i) => (
                            <tr key={m} style={{ borderBottom: `1px solid ${BORDER}`, background: i%2===0?WHITE:CARD_BG }}>
                              <td style={{ padding: "5px 10px", color: DARK }}>{m}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right", color: GRAY }}>{cur?.seasonal[i].toFixed(1)}%</td>
                              <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, color: TEAL }}>{fmtK(exampleTargets[i] || 0)}</td>
                            </tr>
                          ))}
                          <tr style={{ background: "#EAF3F4", borderTop: `2px solid #B2D8DC` }}>
                            <td colSpan={2} style={{ padding: "6px 10px", fontWeight: 700 }}>Annual Target</td>
                            <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: TEAL }}>{fmtK(exampleTargets.reduce((s,v)=>s+v,0))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── SEASONALITY TAB ──────────────────────────────────────────── */}
            {tab === "seasonal" && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 4 }}>Monthly Seasonality Weights</div>
                <div style={{ fontSize: 11, color: GRAY, marginBottom: 16 }}>
                  Each value = % of annual revenue that falls in that month. Must sum to 100%. Used to distribute the annual target non-linearly across months.
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 8, marginBottom: 16 }}>
                  {MONTH_LABELS.map((m, i) => {
                    const val = cur?.seasonal[i] || 0;
                    const isHigh = val === Math.max(...(cur?.seasonal||[]));
                    const isLow  = val === Math.min(...(cur?.seasonal||[]));
                    return (
                      <div key={m} style={{ background: isHigh?GREEN_BG:isLow?AMBER_BG:CARD_BG, border: `1px solid ${isHigh?"#86EFAC":isLow?"#FCD34D":BORDER}`, borderRadius: 8, padding: "10px 8px", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, marginBottom: 4 }}>{m}</div>
                        {editMode ? (
                          <input type="number" value={draft.seasonal[i]} step={0.1} min={0} max={25}
                            onChange={e => { const ns = [...draft.seasonal]; ns[i] = Number(e.target.value); setDraft(d => ({ ...d, seasonal: ns })); }}
                            style={{ width: "100%", textAlign: "center", fontSize: 14, fontWeight: 700, color: TEAL, border: `1px solid ${TEAL}`, borderRadius: 5, padding: "4px 2px" }} />
                        ) : (
                          <div style={{ fontSize: 16, fontWeight: 800, color: isHigh?GREEN:isLow?AMBER:DARK }}>{val.toFixed(1)}%</div>
                        )}
                        {isHigh && <div style={{ fontSize: 8, color: GREEN, fontWeight: 700, marginTop: 2 }}>PEAK</div>}
                        {isLow  && <div style={{ fontSize: 8, color: AMBER, fontWeight: 700, marginTop: 2 }}>TROUGH</div>}
                      </div>
                    );
                  })}
                </div>

                {/* Sum validation */}
                <div style={{ background: Math.abs(seasonalTotal - 100) < 0.5 ? GREEN_BG : RED_BG, border: `1px solid ${Math.abs(seasonalTotal-100)<0.5?"#86EFAC":"#FCA5A5"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: Math.abs(seasonalTotal-100)<0.5?GREEN:RED }}>
                    {Math.abs(seasonalTotal-100)<0.5 ? "✅ Weights sum to 100%" : `⚠ Weights sum to ${seasonalTotal.toFixed(1)}% — must equal 100%`}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>Total: {seasonalTotal.toFixed(1)}%</span>
                </div>

                {/* Visual bar */}
                <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 10 }}>Seasonality Curve</div>
                  <SeasonalityBar values={cur?.seasonal || []} />
                </div>
              </div>
            )}

            {/* ── LAUNCH RAMP TAB ──────────────────────────────────────────── */}
            {tab === "launch" && (
              <div>
                <div style={{ background: AMBER_BG, border: `1px solid #FCD34D`, borderLeft: `4px solid ${AMBER}`, borderRadius: 8, padding: "12px 16px", marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: AMBER, marginBottom: 4 }}>🚀 For brands starting from $0</div>
                  <div style={{ fontSize: 11, color: "#78350F", lineHeight: 1.7 }}>
                    When a brand has no prior year revenue (new launch or returning from a gap year with no prior data), the growth multiplier can't apply — you can't grow from zero. Instead, the system uses this ramp curve: absolute monthly revenue targets that reflect realistic market penetration for a new brand in this category.
                    <br/><strong>These are set by Helmsman, not the brand.</strong>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 12 }}>Monthly Ramp Targets ($)</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
                      {MONTH_LABELS.map((m, i) => (
                        <div key={m} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "9px 10px" }}>
                          <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, marginBottom: 3 }}>{m} (Mo. {i+1})</div>
                          {editMode ? (
                            <input type="number" value={draft.launchRamp[i]} step={100} min={0}
                              onChange={e => { const nr = [...draft.launchRamp]; nr[i] = Number(e.target.value); setDraft(d => ({ ...d, launchRamp: nr })); }}
                              style={{ width: "100%", fontSize: 13, fontWeight: 700, color: TEAL, border: `1px solid ${TEAL}`, borderRadius: 5, padding: "4px 6px", textAlign: "right" }} />
                          ) : (
                            <div style={{ fontSize: 13, fontWeight: 700, color: TEAL }}>{fmtK(cur?.launchRamp[i] || 0)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, padding: "8px 12px", background: "#EAF3F4", border: `1px solid #B2D8DC`, borderRadius: 6, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                      <span style={{ fontWeight: 700 }}>Year 1 Total</span>
                      <span style={{ fontWeight: 700, color: TEAL }}>{fmt((cur?.launchRamp||[]).reduce((s,v)=>s+v,0))}</span>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 12 }}>Ramp Curve Visualization</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={MONTH_LABELS.map((m,i) => ({ m, target: cur?.launchRamp[i]||0 }))} barCategoryGap="20%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                        <XAxis dataKey="m" tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: GRAY }} tickFormatter={v => v>=1000?`$${(v/1000).toFixed(0)}K`:`$${v}`} axisLine={false} tickLine={false} width={38} />
                        <Tooltip formatter={(v) => [fmt(v), "Monthly Target"]} contentStyle={{ fontSize: 11 }} />
                        <Bar dataKey="target" radius={[3,3,0,0]}>
                          {MONTH_LABELS.map((_, i) => <Cell key={i} fill={i < 3 ? "#FCD34D" : i < 6 ? AMBER : i < 9 ? TEAL2 : TEAL} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 6 }}>
                      {[["#FCD34D","Q1 — Early"],[ AMBER,"Q2 — Building"],[TEAL2,"Q3 — Momentum"],[TEAL,"Q4 — Scaling"]].map(([c,l])=>(
                        <div key={l} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9.5, color: GRAY }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{l}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── BRAND STAGES TAB ─────────────────────────────────────────── */}
            {tab === "brands" && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 4 }}>Brand Stage Logic</div>
                <div style={{ fontSize: 11, color: GRAY, marginBottom: 16, lineHeight: 1.6 }}>
                  The system auto-detects each brand's stage from their sales history. This determines which target method applies. Helmsman can override any brand's stage manually.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {BRAND_STAGES.map(s => (
                    <div key={s.stage} style={{ background: s.bg, border: `1px solid ${s.border}`, borderLeft: `4px solid ${s.color}`, borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 20 }}>{s.icon}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: s.color }}>{s.stage}</div>
                            <div style={{ fontSize: 11, color: GRAY, marginTop: 1 }}>{s.desc}</div>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div style={{ background: "rgba(255,255,255,0.6)", borderRadius: 6, padding: "8px 10px" }}>
                          <div style={{ fontSize: 9, color: GRAY, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Target Method</div>
                          <div style={{ fontSize: 11, color: DARK, fontWeight: 500 }}>{s.target}</div>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.6)", borderRadius: 6, padding: "8px 10px" }}>
                          <div style={{ fontSize: 9, color: GRAY, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Example ({cur?.label})</div>
                          <div style={{ fontSize: 11, color: DARK, fontWeight: 500 }}>{s.example}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 6 }}>Target Label Format</div>
                  <div style={{ fontSize: 10, color: GRAY, lineHeight: 1.8 }}>
                    Every target shown on the dashboard includes a source label so brand managers understand where it came from:<br />
                    <span style={{ background: GREEN_BG, color: GREEN, borderRadius: 4, padding: "1px 7px", fontFamily: "monospace", fontSize: 10 }}>$95K (LY +{cur?.growth}% · {cur?.label} · your seasonality)</span>
                    <span style={{ margin: "0 8px", color: GRAY_MID }}>or</span>
                    <span style={{ background: AMBER_BG, color: AMBER, borderRadius: 4, padding: "1px 7px", fontFamily: "monospace", fontSize: 10 }}>${fmtK(cur?.launchRamp?.[0]||0)} (launch ramp Mo.1 · {cur?.label})</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default function HelmsmanApp() {
  const [page, setPage] = useState("topline");

  const PAGES = {
    helm:        <HelsmanDashboard />,
    topline:     <SalesTopline navigate={setPage} />,
    state:       <StatePerformance navigate={setPage} />,
    brand:       <BrandPerformance navigate={setPage} />,
    monthly:     <MonthlyComparison navigate={setPage} />,
    sku:         <SKUPerformance navigate={setPage} />,
    distributor: <DistributorHealth navigate={setPage} />,
    admin:       <CategoryAdmin />,
    prod:        <ProductManagement navigate={setPage} />,
  };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "'DM Sans','Segoe UI',sans-serif", background: PAGE_BG, overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: #1a3a3e; border-radius: 3px; }
        .nav-row { transition: background 0.15s; cursor: pointer; }
        .nav-row:hover { background: rgba(255,255,255,0.1) !important; }
      `}</style>

      {/* SIDEBAR */}
      <div style={{ width: 196, background: TEAL_DARK, flexShrink: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: WHITE }}>H</div>
            <span style={{ fontWeight: 700, fontSize: 13, color: WHITE, letterSpacing: "-0.01em" }}>HELMSMAN</span>
          </div>
          <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 5, padding: "5px 10px", fontSize: 12, color: "rgba(255,255,255,0.8)", display: "flex", justifyContent: "space-between" }}>
            <span>Drifter Spirits</span><span style={{ opacity: 0.5 }}>▾</span>
          </div>
        </div>

        <div style={{ flex: 1, padding: "10px 0" }}>
          {[{ label: "MAIN", items: [{ id:"helm", label:"The Helm" },{ id:"dash", label:"Dashboard" }] },
            { label: "SALES", items: NAV },
            { label: "LOGISTICS", items: [{ id:"prod", label:"Product" },{ id:"ops", label:"Operations" },{ id:"comp", label:"Compliance" }] },
            { label: "OTHER", items: [{ id:"admin", label:"Growth Multipliers" },{ id:"cfg", label:"Configuration" },{ id:"cbma", label:"CBMA Calculator" }] },
          ].map(group => (
            <div key={group.label}>
              <div style={{ padding: "8px 14px 4px", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em" }}>{group.label}</div>
              {group.items.map(item => (
                <div key={item.id} className="nav-row" onClick={() => setPage(item.id)}
                  style={{ padding: "6px 14px 6px 16px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
                    color: page === item.id ? WHITE : "rgba(255,255,255,0.6)",
                    fontWeight: page === item.id ? 600 : 400,
                    background: page === item.id ? "rgba(255,255,255,0.14)" : "transparent",
                    borderRight: page === item.id ? `3px solid ${TEAL2}` : "3px solid transparent",
                  }}>
                  {item.label}
                  {item.isNew && <span style={{ fontSize: 8, fontWeight: 700, background: TEAL2, color: WHITE, borderRadius: 99, padding: "1px 5px", marginLeft: 2 }}>NEW</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Topbar */}
        <div style={{ background: WHITE, borderBottom: `1px solid ${BORDER}`, padding: "0 24px", height: 48, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: DARK }}>{PAGE_TITLES[page]}</span>
            {page === "distributor" && <span style={{ fontSize: 9, fontWeight: 700, background: TEAL2, color: WHITE, borderRadius: 99, padding: "2px 7px", letterSpacing: "0.06em" }}>NEW MODULE</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: TEAL }}>Drifter Spirits</span>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: WHITE }}>JD</div>
            <span style={{ fontSize: 12, color: GRAY }}>Jeff Diego</span>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {PAGES[page] || <div style={{ color: GRAY, padding: 40 }}>Page coming soon</div>}
        </div>
      </div>
    </div>
  );
}
